import { autoMul } from './systems/autoRules';
import { advanceObjectives } from './questProgress';
import { COMBAT, resolveHit, type HitResult, type HitOutcome } from './combat';
import { isControlStatus } from './data/duelArmor';
import { rankFor, skillPowerMult } from './cultivation';
import { NEIGONG } from './data/neigong';
import {
  BAG_SIZE,
  EQUIP_SLOTS,
  equipmentStats,
  getItem,
  itemOf,
  ITEMS,
  rollWeapon,
  type EquipSlot,
  type ItemDef,
} from './data/items';
import { getMonster, type MonsterDef } from './data/monsters';
import { getSchool, SCHOOLS } from './data/schools';
import { BADGE_TIERS, badgeTitleId } from './data/badges';
import { badgeBlock, claimBadge, uniformShown, wearUniformTier } from './badges';
import { ELEMENT_MATCH_BONUS } from './data/elements';
import { MOUNTS } from './data/mounts';
import { followPetPick, getPet, STARTER_PET } from './data/pets';
import { EMOJI_COOLDOWN, EMOJI_SECONDS, EMOJIS, EMOTES } from './data/emotes';
import {
  FORGE_ELEMENT_INFO,
  FORGE_MAX,
  FORGE_SLOTS,
  FORGE_SLIP_FROM,
  forgeStep,
  INFUSE_MAX,
  infuseStep,
  isForgeElement,
  UNFORGED,
  type ForgeElement,
  type WeaponForge,
} from './data/forge';
import { CRAFT_MATS } from './data/itemCraft';
import { pvpNormalizerOf } from './data/pvpTemplate';
import { sellPrice, SHOP } from './data/shop';
import { JADE } from './data/mall';
import { CLOSED_NOTICE, zoneEnabled } from './data/enabledZones';
import { HUB, warpDistance, ZONES, type ZoneId } from './data/zones';
import type { TrainingState } from './training';
import { GroupDance, type DanceCommand, type DanceEvent } from './groupDance';
import { SceneRunner, type SceneCommand, type SceneEvent, type SceneState } from './scene';
import { Interiors, type InteriorCommand, type InteriorEvent } from './interiors';
import type { LongMoveEvent } from './data/longMoves/types';
import type { TrainingSpotDef } from './data/trainingSpots';
import type { TrainingSpotEvent, TrainingSpotState } from './trainingSpots';
import type { AnimalMind } from './systems/animals';
import type { SeamGateSystem } from './systems/seamGate';
import type { FastTravelCommand } from './systems/fastTravel';
import type { BreakthroughCommand, BreakthroughSystem } from './systems/breakthrough';
import { isPersonal, NEAR_RADIUS, newPlayerSlot, type Addressed, type Audience, type PlayerSlot } from './playerSlot';
import { createSystems, type PlayerInput, type SystemHost, type Systems, type WorldSystem } from './systems';
import { WIND } from './systems/wind';
import { shopPrice, type NpcMemoryCommand, type NpcMemoryEvent } from './systems/npcMemory';
import type { OfflineWorkCommand, OfflineWorkEvent } from './systems/offlineWork';
import type { ItemsCommand, ItemsEvent } from './systems/items';
import type { FishBiteCommand, FishBiteEvent } from './systems/fishBite';
import type { ChopBeatCommand, ChopBeatEvent } from './systems/chopBeat';
import type { AutoGatherCommand, AutoGatherEvent } from './systems/autoGather';
import type { AutoQuestCommand } from './systems/autoQuest';
import type { NavCommand, NavEvent } from './systems/nav';
import type { SafeZoneEvent } from './systems/safeZones';
import type { DuelGroundCommand, DuelGroundEvent } from './systems/duelGround';
import type { PvpDuelCommand, PvpDuelEvent } from './systems/pvpDuel';
import type { ZoneChatCommand, ZoneChatEvent } from './systems/zoneChat';
import { legendaryDealt, legendaryPower } from './data/legendary';
import { FAIR_ARENA } from './data/tournament';
import { DUEL_FLAGS } from './data/duelMatch';
import type { DuelMatchCommand } from './systems/duelMatch';
import type { TourneyCommand, TourneyEvent } from './systems/tournament';
import type { LockOnCommand } from './systems/duelLock';
import type { GuildCommand, GuildEvent } from './systems/guild';
import type { WarehouseCommand, WarehouseEvent } from './systems/warehouse';
import type { GuildWarCommand, GuildWarEvent } from './systems/guildWar';
import type { GuildBannerCommand, GuildBannerEvent } from './systems/guildBanner';
import type { DungeonEvent } from './systems/dungeon';
import type { SocialCommand, SocialEvent } from './systems/social';
import type { RedRoamerEvent } from './systems/redRoamers';
import type { ActivityMode } from './data/modes';
import type { ModesCommand, ModesEvent } from './systems/modes';
import { hasSafeZones, safeZoneAt } from './data/safeZones';
import { skillBarSkills, type SkillBarCommand } from './systems/skillBar';
import type { MoveLocksCommand, MoveLocksEvent } from './systems/moveLocks';
import type { TalkAct, TalkOn } from './data/npcTalk';
import { LADDER, type LadderId } from './data/ladder';
import type { ArenaBuild } from './data/arena';
import { passXpForKill, SEASON, type PassReward, type PassTrack } from './data/pass';
import type { TopUpReceipt } from './data/topup';
import { isPkProtected, jailSeconds, karmaAfterKill, KARMA_LABELS, PK, pkColor, type Karma, type PkColor } from './karma';
import { TITLE_LIST, TITLES } from './data/titles';
import { SKIN_SLOTS, SKINS, type SkinSlot } from './data/skins';
import { getNeigong, neigongLearnBlock, neigongMastered, neigongPassive } from './data/neigong';
import { FINAL_DIALOG, QUESTS, type ObjectiveType, type QuestDef } from './data/quests';
import { LEGEND } from './data/lore';
import {
  artOfSkill,
  getArt,
  getSkill,
  MARTIAL_ARTS,
  RAGE,
  SKILLS,
  type SkillDef,
  type SkillKind,
} from './data/skills';
import { familyOf, isArmed, isSwordFamily } from './data/weaponFamilies';
import { screenFacing, screenHeading } from './iso';
import {
  crossesWall,
  floorAt,
  generateZone,
  inSafeArea,
  hasLevels,
  isBorder,
  levelAt,
  MAX_STEP,
  PX_METRES,
  randomFloorIn,
  standAt,
  waterSurfaceAt,
  STEP_UP,
  surfaceAt,
  Tile,
  tileAt,
  type GameMap,
  type Point,
} from './map';
import { blocksJump, clearAbove, findPath, freeAt, nearestFloor, type Waypoint } from './path';
import { coreOf } from './region/core';
import { routeTo } from './region/roads';
import { newProgress, type PlayerProgress } from './progress';
import { Trades, type TradeCommand, type TradeEvent } from './professions';
import { Carriage, type CarriageCommand, type CarriageEvent } from './carriage';
import type { SecretEvent } from './systems/secrets';
import type { LockEvent } from './systems/lockOn';
import { groundSpot, homingOf, volleyHits } from './data/skillAim';
import type { WorldEventEvent } from './systems/worldEvents';
import type { WorldBossEvent } from './systems/worldBosses';
import type { TrialCommand, TrialEvent } from './systems/trials';
import type { SectActCommand } from './systems/sectActivities';
import type { WalkModeCommand } from './systems/walkMode';
import type { SlideCommand, SlideEvent } from './systems/slide';
import { gearShown, shownSkin, type WardrobeCommand } from './systems/wardrobe';
import { walkPace } from './data/walkModes';
import {
  fallDamage,
  fallHeight,
  isWaterAt,
  qinggongLearnBlock,
  qinggongStats,
  qinggongTrainBlock,
  qinggongTrainCost,
  restsStamina,
  swimming,
  tickQinggong,
  trackFall,
  waterSpeed,
  type QinggongState,
  type QinggongStats,
} from './qinggong';
import { getQinggong, QG_RUN_BASE, WALL_KICK } from './data/qinggong';
import { tickWater, wadesAt } from './systems/water';
import { climbCliff, holdClimb } from './systems/climb';
import { newWeather, type WeatherKind, type WeatherState } from './data/weather';
import { isDaytime, periodAt, type TimePeriod } from './data/time';
import { combatBlockText, enterCombat } from './data/combat';
import { mulberry32, randInt, type Rng } from './rng';
import { FELLOW_LIST, FELLOWS, type FellowDef, PARTY, partyShare } from './data/party';
import {
  type Activity,
  chatOf,
  CRITTERS,
  type CritterKind,
  DAY_SECONDS,
  type DayPart,
  dayPartAt,
  FOLK,
  FOLK_LIST,
  FOLKS,
  type FolkLines,
  GOSSIP,
  NPC_LINES,
  type Station,
} from './data/folk';
import { attributesFor, deriveStats } from './stats';
import { addGearStats } from './gearStats';
import {
  gearAirDashMul,
  gearVolleyExtra,
  gearCdMul,
  gearCritRes,
  gearDefMul,
  gearFallMul,
  gearGuardMul,
  gearHitMul,
  gearQiMul,
  gearQinggong,
  gearQinggongKey,
  gearRageMul,
  gearStunMul,
  gearWindupMul,
} from './systems/gearCombat';
import {
  statusLifesteal,
  getStatus,
  sealedQinggong,
  silenced,
  stacksOf,
  STATUSES,
  statusCrit,
  cleansable,
  statusHealMul,
  statusMul,
  statusSpeed,
  type StatusState,
} from './status';

export type EntityKind = 'player' | 'monster' | 'npc' | 'pet' | 'ambient' | 'rival';

/** A ladder bout under way: looking for a match, counting down, fighting, or showing the result. */
export interface Duel {
  /** The ladder fought on, or null for an arena (ประลองยุทธ) bout. */
  ladder: LadderId | null;
  phase: 'queue' | 'countdown' | 'fight' | 'result';
  /** Seconds left in this phase. */
  t: number;
  foeId: number;
  /** Who the foe is: a ladder contender (with rating), or an arena build (rating = its rank). */
  foe: { name: string; rating: number } | null;
  /** Arena bouts: the rank challenged, and whether auto-fight was on before (it drives the player). */
  arenaRank: number;
  autoWas: boolean;
  /** Where the player stood before being brought to the arena. */
  back: Point;
  won: boolean;
}

/** A fake fellow player roaming the world (until real players arrive online). */
export interface RivalBrain {
  homeX: number;
  homeY: number;
  /** Seconds it keeps fighting back after being struck. */
  aggroT: number;
  /** Time to its next move. */
  skillT: number;
  /** Moves it fights with. */
  skills: string[];
  /** A fellow wanderer (see data/party.ts) who can join the player's party, by id. */
  fellow?: string;
  /** Party members: time to the next path toward the leader. */
  followT?: number;
  /** A companion lent for one place (a dungeon's school helper, systems/dungeon.ts): in the party while it lasts. */
  helper?: boolean;
}

export interface Cast {
  skillId: string;
  kind: SkillKind;
  t: number;
  windup: number;
  targetId: number | null;
  /** Ground-targeted moves: the spot it will land on. */
  at?: Point;
}

export interface MonsterBrain {
  defId: string;
  spawnX: number;
  spawnY: number;
  state: 'idle' | 'chase' | 'return';
  decisionT: number;
  blockT: number;
  /** Who it is fighting when not the player (the party member that struck it last). */
  foeId: number | null;
}

export interface Wander {
  cx: number;
  cy: number;
  r: number;
  t: number;
  tx: number | null;
  ty: number | null;
}

/**
 * A townsperson's day (see data/folk.ts): the station they are at or walking to, what they are
 * doing there, and how they are reacting to whoever is about. Key NPCs (`fixed`) keep their post
 * and only greet, look round and talk.
 */
export interface FolkBrain {
  /** Folk id, or for a key NPC its role in `NPC_LINES`. */
  id: string;
  fixed: boolean;
  part: DayPart;
  /** Place in the part of the day's schedule. */
  idx: number;
  station: Station | null;
  act: Activity;
  /** Seconds left at the station. */
  actT: number;
  /** On the way to the station's spot (tx, ty). */
  walking: boolean;
  tx: number;
  ty: number;
  /** Behind closed doors: not drawn, not about. */
  indoors: boolean;
  /** A greeting or a flinch played in place of the work. */
  gesture: 'bow' | 'wave' | 'cower' | null;
  gestureT: number;
  greetT: number;
  barkT: number;
  /** Running from a fight. */
  fleeT: number;
  scaredT: number;
  /** Turned to look at the player standing near. */
  attending: boolean;
  /** Chats: the next line and time to it. Sparring: the next exchange and time to it. */
  chatLine: number;
  chatT: number;
  sparStep: number;
  blockT: number;
  /** Carrying a lantern (night watch). */
  lantern: boolean;
  /** Where a key NPC faces when nobody is near. */
  homeHeading: number;
  homeFacing: 1 | -1;
}

/** A chicken, cat or bird: pecking about its spot, scattering or flying off when someone comes near. */
export interface CritterBrain {
  kind: CritterKind;
  cx: number;
  cy: number;
  /** `gone`: flown off out of sight, to land again later. */
  state: 'idle' | 'flee' | 'fly' | 'gone' | 'land';
  t: number;
  /** Flight direction. */
  vx: number;
  vy: number;
  /** A village animal's mind (systems/animals.ts), made on its first thought. */
  animal?: AnimalMind;
}

export interface Entity {
  readonly id: number;
  readonly kind: EntityKind;
  x: number;
  y: number;
  radius: number;
  /** 1 = facing screen-right, -1 = screen-left. */
  facing: 1 | -1;
  /** Screen direction in eight 45° steps, 0 = screen-right, clockwise (see `screenHeading`). */
  heading: number;
  dirX: number;
  dirY: number;
  sprite: string;
  /** Appearance keys of worn equipment (weapon, head, body order), for the renderer. */
  outfit: string[];
  /** How the weapon in hand was forged (its lightning and element), or null when unforged. */
  forge: WeaponForge | null;
  /** The emote held (ท่าโพส, data/emotes) and for how long, or null. */
  emote: string | null;
  emoteT: number;
  /** The emoji over the head and how long it has left, or null. */
  emoji: string | null;
  emojiT: number;
  scale: number;
  name: string;
  title: string;
  level: number;

  hp: number;
  maxHp: number;
  qi: number;
  maxQi: number;
  qiRegen: number;
  stamina: number;
  maxStamina: number;
  /** External power, defence and crit chance (used by external arts and by monsters). */
  atk: number;
  def: number;
  crit: number;
  /** Internal power, defence and crit chance (internal arts). */
  magic: number;
  magicDef: number;
  magicCrit: number;
  /** Accuracy for external / internal hits, and evasion against them. */
  hit: number;
  magicHit: number;
  dodge: number;
  /** Shortens stuns taken: stun × 100 / (100 + toughness). */
  toughness: number;
  hpRegen: number;
  speed: number;
  /** ความโกรธ: built by trading blows, spent on power moves. */
  rage: number;
  maxRage: number;
  statuses: StatusState[];

  blocking: boolean;
  cast: Cast | null;
  stunT: number;
  invulnT: number;
  /** Remaining time of the attack pose, for animation. */
  swingT: number;
  /** Remaining time of the hit flinch, for animation. */
  hitT: number;
  /** Seconds since last combat action; regeneration and meditation wait for this. */
  combatT: number;
  attackT: number;
  /** Basic-attack chain: the next cut (0–2) and when the last one was made. */
  combo: number;
  comboAt: number;
  cooldowns: Record<string, number>;
  dashT: number;
  dashVX: number;
  dashVY: number;
  /** Height above the ground in world pixels, and its vertical speed. */
  z: number;
  vz: number;
  /** Jumps used since last touching the ground. */
  jumps: number;
  /** Already dashed in the air since leaving the ground (one air dash per jump). */
  airDashed: boolean;
  /** Running up a wall this tick (for animation). */
  climbing: boolean;
  healT: number;
  healRate: number;
  meditating: boolean;
  /** Mount being ridden (id), or null on foot. */
  mount: string | null;
  /** Weapon put away in its scabbard (drawn again by fighting). */
  sheathed: boolean;
  /** Height a flying mount is climbing or diving to. */
  flyAlt: number;
  /** PK on (เปิดแดง): may attack other players and be attacked by them. */
  pk: boolean;
  karma: Karma;
  /** PK points from killing players (head colour); NPC guards attack red and purple names. */
  pkPoints: number;
  /** Seconds left in jail (players only). */
  jailT: number;
  /** NPC guards: time to the next strike at a red name. */
  guardT: number;
  rival: RivalBrain | null;
  /** Whirlwind in progress: seconds left, the move, and time to its next round of cuts. */
  spinT: number;
  spinSkill: string | null;
  spinTickT: number;
  spinPower: number;
  moving: boolean;
  /** Running with qinggong this tick (for animation). */
  sprinting: boolean;
  dead: boolean;
  deadT: number;
  animT: number;

  targetId: number | null;
  path: Waypoint[] | null;
  stuckT: number;
  brain: MonsterBrain | null;
  wander: Wander | null;
  /** Townsfolk and key NPCs: their day and their manners. */
  folk: FolkBrain | null;
  critter: CritterBrain | null;
}

export interface Telegraph {
  readonly id: number;
  readonly srcId: number;
  readonly skillId: string;
  readonly x: number;
  readonly y: number;
  readonly radius: number;
  readonly life: number;
  t: number;
}

/** `guild`: the guild chat (systems/guild.ts `guildChat`). */
export type LogChannel = 'sys' | 'fight' | 'guild';

export type WorldEvent =
  | {
      type: 'hit';
      srcId: number;
      tgtId: number;
      skillId: string;
      outcome: HitOutcome;
      damage: number;
      crit: boolean;
    }
  /** A basic attack; `step` is its place in the three-cut chain (0, 1, then the finisher 2). */
  | { type: 'basic'; srcId: number; tgtId: number; step: number }
  | {
      type: 'skill';
      srcId: number;
      skillId: string;
      tgtId: number | null;
      volley?: { tgtId: number; at: number }[];
      /** Ground-targeted moves: where it lands. */
      at?: Point;
    }
  | { type: 'castStart'; srcId: number; skillId: string }
  | { type: 'stance'; id: number }
  | { type: 'whiff'; srcId: number }
  | { type: 'death'; id: number }
  | { type: 'reward'; id: number; gold: number; cultivation: number }
  | { type: 'respawn'; id: number }
  | { type: 'dash'; id: number }
  /** `cloud`: the third jump, stepping off the clouds themselves (เหยียบเมฆา). */
  | { type: 'jump'; id: number; air: boolean; cloud?: true }
  /**
   * Came down. A fall past the safe height (data/qinggong.ts `FALL`) also carries its metres and the
   * health it took (`death` follows when it killed).
   */
  | { type: 'land'; id: number; fall?: number; damage?: number }
  | { type: 'telegraph'; tele: Telegraph }
  | { type: 'teleBurst'; x: number; y: number; radius: number }
  | { type: 'notice'; text: string }
  | { type: 'log'; channel: LogChannel; text: string }
  | {
      type: 'dialog';
      npcId: number;
      text: string;
      action: 'accept' | 'turnIn' | 'joinSchool' | 'shop' | 'travel' | null;
      schoolId?: string;
      /** The button's words when not the action's own (systems/plainsStory.ts: the fair's choices). */
      label?: string;
    }
  | { type: 'quest'; status: 'accepted' | 'progress' | 'ready' | 'completed'; questId: string }
  | { type: 'bossSpawn' }
  | { type: 'bossDefeated' }
  | { type: 'meditate'; on: boolean }
  | { type: 'trained'; what: 'skill' | 'neigong'; id: string; level: number }
  | { type: 'joinedSchool'; school: string }
  | { type: 'learnedArt'; art: string }
  | { type: 'progressChanged' }
  | {
      type: 'ladder';
      phase: Duel['phase'] | 'cancel';
      ladder: LadderId | null;
      foe?: string;
      won?: boolean;
      delta?: number;
    }
  /** A monster dropped an item into the player's bag. */
  | { type: 'loot'; id: number; item: string }
  | { type: 'equipped'; slot: EquipSlot; item: string | null }
  /** Someone took up an emote, or let it go (null). */
  | { type: 'emote'; id: number; emote: string | null }
  /** Someone showed an emoji over their head. */
  | { type: 'emoji'; id: number; emoji: string }
  /**
   * The forge struck: raising the + (`plus`) or tempering an element. `item` is the weapon's id
   * afterwards (forging a shop weapon makes it an item of its own); `level` where it now stands.
   */
  | { type: 'forge'; what: 'plus' | 'element'; item: string; ok: boolean; level: number; el?: ForgeElement }
  /** A status gained or changed stacks (0 = removed). */
  | { type: 'status'; id: number; status: string; stacks: number }
  /** A move carried its user across the ground (gap closers, forward dashes). */
  | { type: 'skillDash'; id: number; fromX: number; fromY: number; skillId: string }
  /** A warp-chain move vanished its user from (fromX, fromY) and set it down beside a foe. */
  | { type: 'blink'; id: number; fromX: number; fromY: number; tgtId: number; skillId: string }
  | { type: 'auto'; on: boolean }
  /** The weapon went into its scabbard (on) or was drawn. */
  | { type: 'sheathe'; id: number; on: boolean }
  /** A whirlwind turned once more (a round of cuts). */
  | { type: 'spin'; id: number; skillId: string }
  /** A guard-break in the air gave the jumps back. */
  | { type: 'airReset'; id: number }
  /** A qinggong art learned (level 1) or trained to a new level. */
  | { type: 'qinggong'; id: string; level: number }
  /** Into the water with a splash: out of stamina skimming, or down from the air (then swimming or wading). */
  | { type: 'sink'; id: number; fromX: number; fromY: number }
  /** ร่อนลม opened (on) or folded. */
  | { type: 'glide'; id: number; on: boolean }
  /** The player set off for another zone (the client saves and loads it). */
  | { type: 'travel'; to: ZoneId }
  /** A title was earned. */
  | { type: 'title'; id: string }
  /** PK switched on or off. */
  | { type: 'pk'; id: number; on: boolean }
  /** A player's karma changed from killing another player. */
  | { type: 'karma'; id: number; karma: Karma }
  /** A head colour changed (PK points crossed a line). */
  | { type: 'pkColor'; id: number; color: PkColor }
  /** Jailed (seconds) or set free (0). */
  | { type: 'jail'; seconds: number }
  /** Mounted (mount id) or dismounted (null). */
  | { type: 'mount'; id: number; mount: string | null }
  /** The party changed (fellow ids, in joining order). */
  | { type: 'party'; members: string[] }
  /**
   * Someone said something out loud (a speech bubble over their head, not the chat log), with the
   * gesture asked for with it (data/npcTalk.ts `TalkAct`), if any.
   */
  | { type: 'bark'; id: number; text: string; act?: TalkAct }
  /** A gift to a townsperson (systems/npcMemory.ts). */
  | NpcMemoryEvent
  /** Professions: work begun, done or broken off, goods gathered, things made (professions.ts). */
  | TradeEvent
  /** Group dance (รำกลุ่ม, groupDance.ts). */
  | DanceEvent
  /** Carriages (รถม้า, carriage.ts): the routes menu, boarding, the ride, arriving, stepping down. */
  | CarriageEvent
  /** Interiors (interiors.ts): a door or stairs taken, in, up or down, and out again. */
  | InteriorEvent
  /** Auto-navigation (นำทาง): a walk begun, arrived, or stopped and why (systems/nav.ts). */
  | NavEvent
  | MoveLocksEvent
  /** Long moves (ท่ายาว): an ult's tell with its ground area, the catch, each beat, the end (systems/longMove.ts). */
  | LongMoveEvent
  /** A cutscene began or ended (scene.ts). */
  | SceneEvent
  /** Stepped into or out of a training spot (trainingSpots.ts). */
  | TrainingSpotEvent
  /** Offline work and kidnapping: the job picked, the blow, the sack (systems/offlineWork.ts). */
  | OfflineWorkEvent
  /** Ground loot dropped and gone, and a smith's forge opened (systems/items.ts). */
  | ItemsEvent
  | FishBiteEvent
  | ChopBeatEvent
  | AutoGatherEvent
  /** Into or out of a safe zone (systems/safeZones.ts). */
  | SafeZoneEvent
  /** The duelling ground: a challenge answered (systems/duelGround.ts). */
  | DuelGroundEvent
  /** A duel between two real players: invite, countdown, fight, result (systems/pvpDuel.ts). */
  | PvpDuelEvent
  | TourneyEvent
  /** Typed chat relayed by a zone server (systems/zoneChat.ts). */
  | ZoneChatEvent
  /** กิลด์ and ศึกกิลด์ (systems/guild.ts, guildWar.ts). */
  | GuildEvent
  | GuildWarEvent
  /** กิลด์ศัตรู and ป้ายสังหาร (systems/guildBanner.ts). */
  | GuildBannerEvent
  /** โรงคลัง: the store opened, closed or changed (systems/warehouse.ts). */
  | WarehouseEvent
  | ModesEvent
  /** The dungeon (ถ้ำคลื่นคำราม): rooms, ground telegraphs, boss phases, rescue, wipe, the chest (systems/dungeon.ts). */
  | DungeonEvent
  /** เพื่อน and คู่แค้น (systems/social.ts). */
  | SocialEvent
  | RedRoamerEvent
  /** Qinggong time trials and the secrets of the crags (systems/trials.ts, secrets.ts). */
  | TrialEvent
  | SecretEvent
  | LockEvent
  /** สไลด์: a ground slide set off (systems/slide.ts). */
  | SlideEvent
  | WorldEventEvent
  | WorldBossEvent;

/** Player intents. In the online phase these become network messages validated by the server. */
export type PlayerCommand =
  | { t: 'moveTo'; x: number; y: number }
  | { t: 'moveDir'; dx: number; dy: number }
  | { t: 'engage'; id: number }
  | { t: 'targetNearest' }
  /** Basic attack: fight the target (or the nearest foe), or with nobody near, cut the air. */
  | { t: 'attack' }
  | { t: 'interact'; id: number }
  /** Pick someone as the target without fighting them (another player, to see them or invite them). */
  | { t: 'select'; id: number }
  /** Let go of the target (Esc, or a click on empty ground). */
  | { t: 'untarget' }
  /** Parties: ask a fellow wanderer to join, send one member away, or break the party up. */
  | { t: 'partyInvite'; id: number }
  | { t: 'partyKick'; fellow: string }
  | { t: 'partyLeave' }
  /** `at`: for a ground-targeted move, the spot picked (clamped to the move's range). */
  /** `hold`: the key was held long enough to lock (systems/lockOn.ts). */
  | { t: 'skill'; slot: number; at?: Point; hold?: boolean }
  | { t: 'block'; on: boolean }
  /** Hold to run with qinggong: faster, drains stamina. */
  | { t: 'sprint'; on: boolean }
  | { t: 'dash'; dx: number; dy: number }
  | { t: 'jump' }
  | { t: 'meditate' }
  /** Put the weapon away in its scabbard, or draw it. */
  | { t: 'sheathe' }
  /** PK on (เปิดแดง) or off. */
  | { t: 'pk'; on: boolean }
  /** On a flying mount: keep climbing (1), diving (−1) or hold height (0) while a key is held. */
  | { t: 'fly'; dir: -1 | 0 | 1 }
  /** Go with the guide to the other zone. */
  | { t: 'travel' }
  /** Buy a look from the item mall with jade. */
  | { t: 'mallBuy'; kind: 'skin' | 'mount'; id: string }
  /** Claim the reward for a top-up total (in baht) already reached. */
  | { t: 'topUpClaim'; baht: number }
  /** Battle pass: unlock the premium track with jade, or take tier rewards (one, or every one reached). */
  | { t: 'passBuy' }
  | { t: 'passClaim'; level: number; track: PassTrack }
  | { t: 'passClaimAll' }
  /** Buy from the merchant, or sell an item from the bag (not one being worn). */
  | { t: 'buy'; item: string }
  | { t: 'sell'; id: string }
  /** Pick which owned mount X summons. */
  | { t: 'setMount'; id: string }
  /** Show an earned title over the head. */
  | { t: 'setTitle'; id: string }
  /** Ride the first owned mount, or get off. */
  | { t: 'mount' }
  | { t: 'questAccept' }
  | { t: 'questTurnIn' }
  | { t: 'trainSkill'; id: string }
  | { t: 'trainNeigong'; id: string }
  /** Stop the training under way; what it has cost so far is not given back. */
  | { t: 'trainStop' }
  /** Arena (ประลองยุทธ): challenge one of the offered ranks (1-based), or draw a new five. */
  | { t: 'arenaFight'; rank: number; mode?: ActivityMode }
  | { t: 'arenaRefresh' }
  /** Ladders: queue for a bout, leave the queue, take a tier reward, or buy from its shop. */
  | { t: 'ladderJoin'; ladder: LadderId; mode?: ActivityMode }
  | { t: 'ladderCancel' }
  | { t: 'ladderClaim'; ladder: LadderId; tier: number }
  | { t: 'ladderBuy'; ladder: LadderId; index: number; gear?: string; picks?: string[] }
  /** Auto-training: when a level finishes, start the next one of the same thing. */
  | { t: 'trainAuto'; on: boolean }
  | { t: 'setNeigong'; id: string }
  | { t: 'setArt'; id: string }
  /** Wear an owned skin in a slot, or take it off (null). */
  | { t: 'setSkin'; slot: SkinSlot; id: string | null }
  /** School badge: claim the next tier, or choose which rank of uniform to wear (0 = plain). */
  | { t: 'claimBadge'; tier: number }
  | { t: 'wearUniform'; tier: number }
  | { t: 'joinSchool'; id: string }
  | { t: 'learnArt'; id: string }
  /** วิชาตัวเบา: learn an art from its teacher (or manual), or train a learned one a level up. */
  | { t: 'qinggongLearn'; id: string }
  | { t: 'qinggongTrain'; id: string }
  /** Learn a neigong scripture for cultivation (tier-2 books, once the tier-1 book is mastered). */
  | { t: 'learnNeigong'; id: string }
  | { t: 'equip'; id: string }
  | { t: 'unequip'; slot: EquipSlot }
  /** Forge a weapon in the bag one + higher, or temper it with an element (a new one starts over). */
  /** Take up an emote (ท่าโพส), or stop (null). */
  | { t: 'emote'; id: string | null }
  /** Show an emoji over the head. */
  | { t: 'emoji'; id: string }
  | { t: 'forge'; id: string; protect?: boolean }
  | { t: 'infuse'; id: string; el: ForgeElement }
  | { t: 'gm'; cmd: GmCommand }
  /** Auto-battle: the character hunts and fights around where it was switched on. */
  | { t: 'auto'; on: boolean }
  /** Professions: gather, plant, craft, stop work, use, buy and sell goods (professions.ts). */
  | TradeCommand
  /** The duelling ground (ลานประลอง): challenge someone nearby to a bout (systems/duelGround.ts). */
  | DuelGroundCommand
  /** Breakthrough gate: use the pill to open the trial (systems/breakthrough.ts). */
  | BreakthroughCommand
  /** Answer a duel challenge from another player (the server's; systems/pvpDuel.ts). */
  | PvpDuelCommand
  /** Typed chat: handled by the zone server, ignored by a lone World (systems/zoneChat.ts). */
  | ZoneChatCommand
  | DuelMatchCommand
  | TourneyCommand
  | LockOnCommand
  /** Activity modes: vs NPCs or vs players (systems/modes.ts). */
  | ModesCommand
  /** กิลด์ and ศึกกิลด์ (systems/guild.ts, guildWar.ts). */
  | GuildCommand
  | GuildWarCommand
  | GuildBannerCommand
  /** โรงคลัง: deposit, withdraw, split, tidy, ตำลึง (systems/warehouse.ts). */
  | WarehouseCommand
  /** Group dance (รำกลุ่ม): start a ring, join it, leave it, or press an arrow (groupDance.ts). */
  | DanceCommand
  /** Carriages (รถม้า): hire the coach to another station, or skip the ride (carriage.ts). */
  | CarriageCommand
  /** เดินช้า / เดินเร็ว (systems/walkMode.ts). */
  | WalkModeCommand
  /** สไลด์: Ctrl + a direction, the ground slide (systems/slide.ts). */
  | SlideCommand
  | TrialCommand
  | SectActCommand
  | WardrobeCommand
  /** Interiors: go in by a door, out again, or up and down the stairs (interiors.ts). */
  | InteriorCommand
  /** Auto-navigation (นำทาง): walk to a quest's next step, a person, a place; or stop (systems/nav.ts). */
  | NavCommand
  /** Auto-quest ต่อไป (systems/autoQuest.ts) */
  | AutoQuestCommand
  /** Cutscenes: next line or answer, skip, watch again (scene.ts). */
  | SceneCommand
  /** Weather (GM worlds only): set a kind at once, or null to turn to the next (systems/weather.ts). */
  | { t: 'weather'; kind: WeatherKind | null }
  /** Give a townsperson silver (systems/npcMemory.ts). */
  | NpcMemoryCommand
  /** Offline work and kidnapping: pick the job, blow powder, carry, drop and sell a sack (systems/offlineWork.ts). */
  | OfflineWorkCommand
  | FastTravelCommand
  /** The bag's order, throwing things out, picking up ground loot, the hidden weapon (systems/items.ts). */
  | ItemsCommand
  | FishBiteCommand
  | ChopBeatCommand
  | AutoGatherCommand
  /** The skill bar: put a learned move in a slot, or swap two (systems/skillBar.ts). */
  | SkillBarCommand
  /** Learn a locked move from a master, or all of them (dev): systems/moveLocks.ts. */
  | MoveLocksCommand
  /** เพื่อน and คู่แค้น: add, answer and remove friends; swear and forgive enemies (systems/social.ts). */
  | SocialCommand;

export interface WorldOptions {
  seed?: number;
  progress?: PlayerProgress;
  rng?: Rng;
  /** Accept GM commands (development builds only; a server never sets this for players). */
  gm?: boolean;
  /** Hour of the day to start at (0–24); 8 in the morning if left out. */
  hour?: number;
  /**
   * Play the story's cutscenes (scene.ts): the opening for a new character, the scenes met on the
   * way. The game turns this on; bare simulations and tests leave it off unless they test scenes.
   */
  story?: boolean;
}

/** Development cheats, toggled from the GM menu. */
export type GmFlag = 'god' | 'oneHit' | 'noCooldown' | 'infQi';
export type GmTeleport = 'spawn' | 'plaza' | 'lair' | 'wolves' | 'bandits' | 'arena';

/** Commands for testing during development. Ignored unless the world was created with `gm`. */
export type GmCommand =
  | { op: 'cultivation'; amount: number }
  | { op: 'gold'; amount: number }
  | { op: 'restore' }
  | { op: 'rage' }
  | { op: 'toggle'; flag: GmFlag }
  | { op: 'learnAllArts' }
  | { op: 'maxSkills' }
  | { op: 'maxNeigong' }
  | { op: 'school'; id: string | null }
  | { op: 'giveAllItems' }
  | { op: 'questComplete' }
  | { op: 'questSet'; index: number }
  | { op: 'spawn'; monster: string; count: number }
  | { op: 'killNearby' }
  | { op: 'teleport'; to: GmTeleport }
  | { op: 'status'; id: string; on: 'self' | 'target'; stacks: number }
  | { op: 'clearStatuses' }
  | { op: 'spawnBoss' }
  /** Fighters of every school and badge rank wandering round the player: a guild war's crowd, to test. */
  | { op: 'crowd'; count: number }
  | { op: 'title'; id: string }
  | { op: 'allTitles' }
  | { op: 'rivalPk' }
  | { op: 'jade'; amount: number }
  /** Set the worn weapon's + and element outright. */
  | { op: 'forge'; plus: number; el: ForgeElement | null; lv: number }
  | { op: 'passXp'; amount: number }
  | { op: 'finishTraining' }
  | { op: 'ladderRating'; amount: number }
  | { op: 'arenaNextDay' }
  /** Move the clock forward this many hours. */
  | { op: 'hours'; amount: number };

/** Jumping. Heights are in world pixels; the simulation only uses them to decide what is airborne. */
export const MOVEMENT = {
  /** Height a jump must reach to pass over boulders and trees. */
  clearHeight: 10,
  /** Flying mounts: height gained or lost per press, the ceiling, and the height that clears all. */
  flyStep: 40,
  flyMax: 600,
  /** Climbing or diving speed while Space / Q is held on a flying mount (px/s). */
  flyRate: 140,
  flyClear: 40,
  jumpVelocity: 260,
  airJumpVelocity: 240,
  gravity: 520,
  /** Ground jump, a second leap, and the third step on the clouds (เหยียบเมฆา). */
  maxJumps: 3,
  airJumpStamina: 12,
  /** A guard-break in the air: the stamina it costs and the upward spring it gives. */
  airBreakStamina: 10,
  airBreakLift: 150,
  /** Running up walls: climbing speed (px/s) and stamina per second. */
  climbSpeed: 150,
  climbStamina: 22,
  /** วิ่งพันลี้ (qinggong run): speed multiplier and stamina drained per second. */
  sprintMult: 1.8,
  sprintStamina: 14,
  /** Above this height ground effects (telegraph circles) miss. */
  airDodgeHeight: 8,
} as const;

/**
 * The three cuts of a basic attack: power and time to the next cut (× the art's rate), and how
 * long the swing pose lasts.
 */
export const BASIC_CHAIN = [
  { power: 1, rate: 0.8, swing: 0.22 },
  { power: 1.1, rate: 0.8, swing: 0.22 },
  { power: 1.7, rate: 1.35, swing: 0.32 },
] as const;

const RESPAWN_SECONDS = 4;
const TALK_RANGE = 1.9;
const VOLLEY_LAUNCH = 0.5;
const VOLLEY_STAGGER = 0.07;
const VOLLEY_FLIGHT = 0.15;

interface PendingHit {
  t: number;
  srcId: number;
  tgtId: number;
  skillId: string;
  power: number;
  /** Follow-up strikes of a multi-hit move skip the first-strike effects. */
  followUp?: boolean;
  /**
   * A blow on its way (COMBAT.strikeDelay): it carries its own kind and stun, is cancelled when the striker is
   * stunned, staggered or killed first, and misses a target that has moved more than `reach` away.
   */
  blow?: { kind: SkillKind; stun?: number; reach: number };
  /** A volley's projectile: launched from (ox, oy) at the target's spot then (tx, ty), `flight` s before it lands. */
  volley?: { homing: 'soft' | 'none'; ox: number; oy: number; tx: number; ty: number; flight: number };
}

/** A warp chain in progress. */
interface BlinkChain {
  srcId: number;
  skillId: string;
  power: number;
  /** Where the chain began: foes are sought within the move's reach of this spot. */
  ox: number;
  oy: number;
  /** Slashes still to make. */
  left: number;
  /** Times each foe has been struck (revisits are follow-up strikes). */
  struck: Map<number, number>;
  lastId: number;
  /** Seconds until the next warp. */
  t: number;
}

/**
 * How a character looks from their progress alone: the sprite (school uniform, armed or not) and
 * the appearance keys of their equipment and fashion. Used by the world and by screens that show
 * characters without a world (character select).
 */
export function lookOf(pr: PlayerProgress): { sprite: string; outfit: string[] } {
  // A woman's save wears the female figure of her look (`heroF`, `heroWudangF`…; the client's LOOKS).
  const sex = pr.appearance?.sex === 'f' ? 'F' : '';
  const base = `${pr.school ? getSchool(pr.school).uniform : 'hero'}${sex}`;
  const sprite = isArmed(getArt(pr.activeArt)) ? base : `${base}Palm`;
  const outfit = EQUIP_SLOTS.flatMap((slot) => {
    // A gear slot hidden in the wardrobe (systems/wardrobe.ts) does not show.
    const it = pr.equipment[slot] && gearShown(pr, slot) ? itemOf(pr.rolls, pr.equipment[slot]!) : undefined;
    // Pieces with no look on the figure (trousers, shoes, hidden weapons, treasures, manuals) add stats only.
    return it?.visual ? [it.visual] : [];
  });
  // A school badge's rank of uniform, then fashion over the equipment's look.
  const rank = uniformShown(pr);
  if (rank > 0) outfit.push(`uniform${rank}`);
  for (const slot of SKIN_SLOTS) {
    const look = SKINS[shownSkin(pr, slot) ?? '']?.look;
    if (look) outfit.push(look);
  }
  return { sprite, outfit };
}

export const dist = (a: { x: number; y: number }, b: { x: number; y: number }): number =>
  Math.hypot(a.x - b.x, a.y - b.y);

export class World {
  /** The zone's map, or the floor of a building the player is inside (interiors.ts swaps it). */
  map: GameMap;
  readonly entities = new Map<number, Entity>();
  readonly telegraphs: Telegraph[] = [];
  /** Every player in the zone, by entity id, in join order (playerSlot.ts, docs/ONLINE.md §2). */
  private readonly slots = new Map<number, PlayerSlot>();
  /** The first player: the single-player game, tests, and the replica's own. */
  private primary!: PlayerSlot;
  /** The player the rules are running for now (the primary, or a sender / a ticking player). */
  private cur!: PlayerSlot;
  /** The current player's saved state (the primary's outside `apply(cmd, from)` and its tick). */
  get progress(): PlayerProgress {
    return this.cur.progress;
  }
  /** The current player's body (the primary's outside `apply(cmd, from)` and its tick). */
  get player(): Entity {
    return this.cur.entity;
  }
  readonly pet: Entity;
  readonly elder: Entity;
  /** Recruiter NPC of each school, by school id. */
  readonly schoolMasters = new Map<string, Entity>();
  boss: Entity | null = null;
  /** The fake fellow player. */
  rivalId = -1;
  /** Fellow wanderers in this zone, by fellow id. */
  private readonly fellows = new Map<string, Entity>();
  /** Already setting off for another zone (once is enough). */
  private get travelling(): boolean {
    return this.cur.travelling;
  }
  private set travelling(v: boolean) {
    this.cur.travelling = v;
  }
  /** The travelling merchant in the plaza. */
  readonly merchant: Entity;
  /** The mountain guide who takes the player between zones. */
  readonly guide: Entity;
  /** The world boss (respawns some minutes after it falls). */
  worldBoss: Entity | null = null;
  /** The player's equipment bonuses beyond plain stats: lifesteal and per-art damage. */
  private get gear(): PlayerSlot['gear'] {
    return this.cur.gear;
  }
  private set gear(g: PlayerSlot['gear']) {
    this.cur.gear = g;
  }
  time = 0;
  /** Hour of the day (0–24), turning one day every `DAY_SECONDS`. */
  hour = 8;
  /** The zone's weather (data/weather.ts), turned by systems/weather.ts; a replica takes the server's. */
  weather: WeatherState = newWeather();
  /** Townsfolk in this zone, by folk id. */
  readonly folk = new Map<string, Entity>();
  /** Where and when the last blow landed (townsfolk run from it). */
  private lastFight: { x: number; y: number; t: number } | null = null;

  private events: WorldEvent[] = [];
  /** Who each event in `events` is for, index by index (docs/ONLINE.md §2, 1d). */
  private audiences: Audience[] = [];
  private nextId = 1;
  private readonly rng: Rng;
  private pending: PendingHit[] = [];
  private chains: BlinkChain[] = [];
  private bossWarned = false;
  /** The current player's held controls. */
  private get input(): PlayerInput {
    return this.cur.input;
  }

  /** Auto-battle state: on/off, the spot it hunts around, and time to the next decision. */
  get auto(): PlayerSlot['auto'] {
    return this.cur.auto;
  }

  /** The player's วิชาตัวเบา under way (water, glide, leap), for the rules and the animation. */
  get qinggong(): QinggongState {
    return this.cur.qinggong;
  }
  private get qgCache(): PlayerSlot['qgCache'] {
    return this.cur.qgCache;
  }
  private set qgCache(c: PlayerSlot['qgCache']) {
    this.cur.qgCache = c;
  }

  readonly gmEnabled: boolean;
  readonly gmFlags: Record<GmFlag, boolean> = { god: false, oneHit: false, noCooldown: false, infQi: false };
  /** Professions: this zone's resource nodes and the work under way (professions.ts). */
  readonly trades: Trades;
  /** Group dance (รำกลุ่ม): the ring under way, if any, and its buff (groupDance.ts). */
  readonly dance: GroupDance = new GroupDance(this, {
    emit: (e) => this.emit(e),
    emote: (e, id) => this.startEmote(e, id),
    emoji: (e, id) => this.showEmoji(e, id),
    face: (e, at) => this.face(e, at),
    spawn: (o) => this.add(o),
    random: () => this.rng(),
  });
  /** Cutscenes (scene.ts): the scene under way, its triggers, and the "go here" marker. */
  readonly story: SceneRunner = new SceneRunner({
    map: () => this.map,
    progress: () => this.progress,
    player: () => this.player,
    elder: () => this.elder,
    place: (x, y) => this.setDownForScene(x, y),
    hold: () => this.holdForScene(),
    talkElder: () => this.openDialog(this.elder),
    notice: (t) => this.notice(t),
    emit: (e) => this.emit(e),
  });
  /** Interiors (ภายในอาคาร): the building the player is inside, if any, and its doors (interiors.ts). */
  readonly interiors: Interiors = new Interiors({
    world: this,
    add: (o) => this.add(o),
    spawnMonster: (id, x, y) => this.spawnMonster(id, x, y),
    emit: (e) => this.emit(e),
    notice: (t) => this.notice(t),
    dismount: () => this.dismount(),
    halt: () => Object.assign(this.input, { sprint: false, engage: false, talkTo: null }),
    face: (e, at) => this.face(e, at),
  });
  /** Carriages (รถม้า): this zone's station and the ride under way, if any (carriage.ts). */
  readonly carriage: Carriage = new Carriage(this, {
    add: (o) => this.add(o),
    emit: (e) => this.emit(e),
    notice: (t) => this.notice(t),
    travel: (to) => this.travel(to, false),
    busy: () => this.inArena() || !!this.duel || !!this.dance.me() || this.interiors.inside,
    party: () => [...this.entities.values()].filter((e) => this.inParty(e) && !e.dead),
    settle: () => {
      this.dismount();
      this.stopMeditating();
      this.startEmote(this.player, null);
      this.auto.on = false;
      Object.assign(this.input, { dx: 0, dy: 0, engage: false, talkTo: null, block: false, sprint: false });
    },
    face: (e, at) => this.face(e, at),
  });
  /** What the systems (systems/*) may reach beyond the public API. */
  private readonly host: SystemHost = {
    world: this,
    emit: (e) => this.emit(e),
    log: (c, t) => this.log(c, t),
    notice: (t) => this.notice(t),
    random: () => this.rng(),
    get input() {
      return this.world.cur.input;
    },
    add: (o) => this.add(o),
    spawnMonster: (id, x, y) => this.spawnMonster(id, x, y),
    dismount: () => this.dismount(),
    stopMeditating: () => this.stopMeditating(),
    grantReward: (r) => this.grantReward(r),
    move: (e, dx, dy) => this.moveEntity(e, dx, dy),
    face: (e, at) => this.face(e, at),
    hit: (src, tgt, power, kind, skillId, stun) => this.applyHit(src, tgt, power, kind, skillId, stun),
    kill: (tgt, src) => this.kill(tgt, src),
    spawnBoss: () => this.spawnBoss(),
    joinSchool: (id) => this.joinSchool(id),
    outfitOf: (e) => this.outfitOf(e),
    forgeable: (id) => this.forgeable(id),
    passXp: (n) => this.passXp(n),
  };
  /** The World's systems by name, and in the order their hooks run (systems/index.ts). */
  readonly sys: Systems = createSystems(this.host);
  private readonly systems: readonly WorldSystem[] = Object.values(this.sys);
  /** Which system handles each command it claims. */
  private readonly handlers: ReadonlyMap<PlayerCommand['t'], (cmd: PlayerCommand) => void> = (() => {
    const m = new Map<PlayerCommand['t'], (cmd: PlayerCommand) => void>();
    for (const s of this.systems)
      for (const [t, f] of Object.entries(s.commands ?? {}) as [
        PlayerCommand['t'],
        (cmd: PlayerCommand) => void,
      ][]) {
        if (m.has(t)) throw new Error(`command ${t} claimed twice (system ${s.name})`);
        m.set(t, f);
      }
    return m;
  })();

  constructor(opts: WorldOptions = {}) {
    this.gmEnabled = !!opts.gm;
    this.hour = (((opts.hour ?? 8) % 24) + 24) % 24;
    const seed = opts.seed ?? 9;
    this.rng = opts.rng ?? mulberry32(seed * 7919 + 1);
    const progress = opts.progress ?? newProgress();
    // Each zone is a map of its own (travelling between them reloads the world).
    // A save in a closed zone (data/enabledZones.ts) starts in the hub.
    if (!zoneEnabled(progress.location)) Object.assign(progress, { location: HUB, arriveFrom: null, indoors: null });
    const zone = ZONES[progress.location];
    const hub = zone.kind === 'hub';
    this.map = generateZone(zone.id, seed);
    const m = this.map;

    // Arriving through a warp circle sets the player down beside the circle that leads back,
    // a step toward the middle of the map; otherwise at the spawn.
    const back = m.portals.find((p) => p.to === progress.arriveFrom);
    const arrive = back
      ? (() => {
          const l = Math.hypot(m.plaza.x - back.x, m.plaza.y - back.y) || 1;
          return {
            x: back.x + ((m.plaza.x - back.x) / l) * 1.7,
            y: back.y + ((m.plaza.y - back.y) / l) * 1.7,
          };
        })()
      : m.spawn;
    progress.arriveFrom = null;
    const player = this.add({
      kind: 'player',
      x: arrive.x,
      y: arrive.y,
      name: progress.name,
      title: progress.title,
      karma: progress.karma,
      pkPoints: progress.pkPoints,
      jailT: progress.jailT,
    });
    this.primary = this.cur = newPlayerSlot(player, progress);
    this.slots.set(player.id, this.primary);
    this.refreshPlayerStats(true);
    // The current player's, read at each use (docs/ONLINE.md §2).
    const cur = () => this.cur;
    this.trades = new Trades({
      map: m,
      get progress() {
        return cur().progress;
      },
      get player() {
        return cur().entity;
      },
      rng: this.rng,
      emit: (e) => this.emit(e),
      notice: (t) => this.notice(t),
      log: (t) => this.log('sys', t),
      addStatus: (e, id, n) => this.addStatus(e, id, n),
      nearMerchant: () => this.nearMerchant(),
      busy: () => this.inArena(),
      trainBoost: (b, sec) =>
        this.sys.cultivation.boost.set('item', b, sec),
    });
    this.pet = this.add({
      kind: 'pet',
      x: m.spawn.x + 1,
      y: m.spawn.y - 0.6,
      sprite: getPet(STARTER_PET).sprite ?? 'fox',
      facing: -1,
    });
    followPetPick(this.pet, this.progress.petPick);
    this.elder = this.add({
      kind: 'npc',
      x: m.elder.x,
      y: m.elder.y,
      sprite: 'elder',
      name: 'ผู้เฒ่าหลิว',
      title: hub ? 'ผู้เฝ้าศิลาเก้ากระบี่' : `ตามมาเฝ้า${zone.name}`,
      facing: -1,
    });
    // At his landmark where the map has one (Yanyu), else beside the plaza.
    const guidePost = m.landmarks?.guide ?? { x: m.plaza.x - 4, y: m.plaza.y + 3 };
    this.guide = this.add({
      kind: 'npc',
      x: guidePost.x,
      y: guidePost.y,
      sprite: 'guide',
      name: 'ผู้นำทางเขา',
      title: hub ? 'บอกทางวงวาร์ป' : 'พากลับลานศิลา',
      facing: 1,
    });
    // Each school's master waits in the school's own zone.
    for (const school of Object.values(SCHOOLS))
      if (school.id === zone.school)
        this.schoolMasters.set(
          school.id,
          this.add({
            kind: 'npc',
            x: m.plaza.x + school.post.dx,
            y: m.plaza.y + school.post.dy,
            sprite: school.master.sprite,
            name: school.master.name,
            title: school.master.title,
            facing: 1,
          }),
        );
    // The systems' own people (the recruiters of the six schools, the qinggong teachers).
    for (const s of this.systems) s.setup?.();
    // At his shop front where the map has one (Yanyu), else beside the plaza.
    const stall = m.landmarks?.shop ?? { x: m.plaza.x + 4.2, y: m.plaza.y + 2.6 };
    this.merchant = this.add({
      kind: 'npc',
      x: stall.x,
      y: stall.y,
      sprite: 'merchant',
      name: 'เถ้าแก่ฉาง',
      title: 'พ่อค้าเร่',
      facing: -1,
    });
    // The zone's monsters, where its data puts them.
    for (const s of zone.spawns)
      for (let k = 0; k < s.n; k++) {
        const p = randomFloorIn(m, m.regions[s.region], this.rng);
        this.spawnMonster(s.monster, p.x, p.y);
      }
    // The carriage station (carriage.ts): built, unlocked, and a coach's rider set down at its door.
    this.carriage.setup();
    // Fellow wanderers wait in the hub; party members come along to every zone.
    this.spawnFellows(hub);
    // The zone's own people about their day, and the chickens, cats and birds.
    this.spawnLife();
    // The opening and the story's cutscenes (scene.ts): a new character starts with the legend.
    this.story.enabled = !!opts.story;
    this.story.load();
    // The black sword's thief waits at the lair of the hub, or of the village (its ring of red
    // rock) while his quest is under way; the world boss lives in the hub.
    const q = this.currentQuest();
    if (hub) this.worldBoss = this.spawnMonster('worldboss', m.arena.x, m.arena.y);
    if ((hub || zone.kind === 'village') && q?.spawnsBoss && this.progress.quest.status === 'active')
      this.spawnBoss();
    // Saved indoors: back inside the building (interiors.ts).
    this.interiors.restore();
  }

  // ───────────────────────── public API ─────────────────────────

  drainEvents(): WorldEvent[] {
    const e = this.events;
    this.events = [];
    this.audiences = [];
    return e;
  }

  /** The same events as `drainEvents`, in the same order, each with who it is for (the server's). */
  drainAddressed(): Addressed[] {
    const out = this.events.map((ev, i) => ({ to: this.audiences[i]!, ev }));
    this.events = [];
    this.audiences = [];
    return out;
  }

  /**
   * An event's default audience: the UI's own go to the current player; the rest are seen near the
   * entity (or the spot) they happen at, and zone-wide when they have neither.
   */
  private audienceOf(e: WorldEvent): Audience {
    if (isPersonal(e.type)) return { player: this.cur.entity.id };
    const r = e as { id?: unknown; srcId?: unknown; tgtId?: unknown; x?: unknown; y?: unknown };
    const ref = [r.id, r.srcId, r.tgtId].find((v): v is number => typeof v === 'number');
    const at = ref != null ? this.entities.get(ref) : undefined;
    if (at) return { near: { x: at.x, y: at.y }, r: NEAR_RADIUS };
    if (typeof r.x === 'number' && typeof r.y === 'number') return { near: { x: r.x, y: r.y }, r: NEAR_RADIUS };
    return { zone: true };
  }

  get(id: number | null): Entity | undefined {
    return id == null ? undefined : this.entities.get(id);
  }

  currentQuest(): QuestDef | undefined {
    return QUESTS[this.progress.quest.index];
  }

  activeArt() {
    return getArt(this.progress.activeArt);
  }

  /** The moves on the skill bar, in slot order (systems/skillBar.ts): any learned moves, the active art's by default. */
  activeSkills(): SkillDef[] {
    return skillBarSkills(this.progress);
  }

  skillLevel(id: string): number {
    // A fair-arena bout (tournament): every move at one level (data/tournament.ts).
    if (this.pvpBout?.fair) return FAIR_ARENA.skillLevel;
    return this.progress.skillLevels[id] ?? 1;
  }

  /** A duel between two real players under way (systems/pvpDuel.ts): they may hurt only each other, once `fight`. */
  pvpBout: { a: number; b: number; fight: boolean; fair?: boolean } | null = null;
  /**
   * The bout's own rules for a blow between its two fighters (systems/duelMatch.ts): called with the resolved result BEFORE
   * it is applied and its event is sent, so the number, the blood and the hit-stop show what really happened (a counter, a
   * guard that cuts by half, a guard shattered by points, the burst cap). It may change `res` (damage, stun, stamina,
   * outcome). Set by the duel system; null when none.
   */
  duelMod: ((src: Entity, tgt: Entity, res: HitResult, kind: SkillKind, skillId: string) => void) | null = null;
  /** Red armour (systems/duelArmor.ts): true while a fighter can be neither interrupted nor stunned out of its move. */
  armourHook: ((e: Entity) => boolean) | null = null;

  isHostile(a: Entity, b: Entity): boolean {
    const pb = this.pvpBout;
    if (pb) {
      const in2 = (e: Entity) => e.id === pb.a || e.id === pb.b;
      if (in2(a) || in2(b)) return pb.fight && a !== b && in2(a) && in2(b);
    }
    const d = this.duel;
    if (d && d.phase !== 'queue') {
      const duellist = (e: Entity) => e === this.primary.entity || e.id === d.foeId;
      if (duellist(a) || duellist(b)) return d.phase === 'fight' && a !== b && duellist(a) && duellist(b);
    }
    // NPC guards and red names (over 2000 PK points) are enemies.
    const red = (e: Entity) => e.kind === 'player' && e.pkPoints > PK.red;
    // (One way only: guards cannot be attacked back.)
    if (a.kind === 'npc' && red(b)) return true;
    if ((a.kind === 'player' && b.kind === 'monster') || (a.kind === 'monster' && b.kind === 'player'))
      return true;
    // Party members fight monsters alongside the player, and never the player.
    if ((this.inParty(a) && b.kind === 'monster') || (a.kind === 'monster' && this.inParty(b))) return true;
    if (this.inParty(a) || this.inParty(b)) return false;
    // Players fight each other only when one of them has PK on, and never in the plaza.
    const people = (e: Entity) => e.kind === 'player' || e.kind === 'rival';
    if (a !== b && people(a) && people(b) && (a.kind === 'player' || b.kind === 'player'))
    {
      // A kill banner between their guilds stands in for PK (systems/guildBanner.ts).
      const war = a.pk || b.pk || this.systems.some((s) => s.pvpHostile?.(a, b));
      if (!(war && !this.inSafeZone(a) && !this.inSafeZone(b))) return false;
      // Newbie protection (H26): a player below PK_MIN_RANK cannot be hurt by another player in the open world.
      const pa = this.pkProtectedEntity(a);
      const pbt = this.pkProtectedEntity(b);
      if (pa || pbt) {
        if (this.time - this.pkNoticeT > 3 && (a === this.primary.entity || b === this.primary.entity)) {
          this.pkNoticeT = this.time;
          this.notice('ผู้ฝึกยุทธ์หน้าใหม่ได้รับการคุ้มครอง โจมตีไม่ได้');
        }
        return false;
      }
      return true;
    }
    return false;
  }

  private pkNoticeT = -99;
  /** Players seen under the newbie line: when one crosses it, protection ends with a notice (H26). */
  private readonly pkWasProtected = new Set<number>();
  /** True while this entity is a player below the PK rank line (H26). */
  pkProtectedEntity(e: Entity): boolean {
    if (e.kind !== 'player') return false;
    const s = this.slots.get(e.id);
    return !!s && isPkProtected(rankFor(s.progress.cultivationSpent).index);
  }
  private pkProtectTick(): void {
    for (const s of this.slots.values()) {
      const id = s.entity.id;
      if (isPkProtected(rankFor(s.progress.cultivationSpent).index)) this.pkWasProtected.add(id);
      else if (this.pkWasProtected.delete(id) && s === this.primary)
        this.notice('ก้าวสู่ฝึกลมปราณแล้ว การคุ้มครองผู้ฝึกหน้าใหม่สิ้นสุดลง');
    }
  }

  /** The plaza (a whole village, where its layout draws the safe outline) is a safe zone: no killing of players there. */
  inSafeZone(e: { x: number; y: number }): boolean {
    // Indoors the building decides (a cave below the shrine is not safe).
    const indoors = this.interiors.rules();
    if (indoors) return indoors.safe;
    // A zone with its own safe zones (data/safeZones.ts) uses them in place of its layout's outline.
    if (hasSafeZones(this.map.zone)) {
      const o = coreOf(this.map);
      return !!safeZoneAt(this.map.zone, e.x - o.x, e.y - o.y);
    }
    return inSafeArea(this.map, e.x, e.y);
  }

  /**
   * Credits a paid top-up. Deliberately not a PlayerCommand: only the payment side may call it (the
   * sandbox checkout for now, the payment provider's webhook on the server in phase 2). Refuses an
   * unknown pack, an amount that does not match its price, and a receipt already credited.
   */
  creditTopUp(r: TopUpReceipt): boolean {
    return this.sys.store.creditTopUp(r);
  }

  /** Every player in the zone, by entity id, in join order (docs/ONLINE.md §2). */
  get players(): ReadonlyMap<number, PlayerSlot> {
    return this.slots;
  }

  /** The id of the first player (the single-player game's, the replica's own). */
  get primaryId(): number {
    return this.primary.entity.id;
  }

  /**
   * A player joins the zone (docs/ONLINE.md §2, 1e): their body at `at` (default the spawn), their
   * saved progress, controls of their own. Returns the new player's entity id.
   */
  addPlayer(progress: PlayerProgress, at?: { x: number; y: number }): number {
    const where = at ?? this.map.spawn;
    progress.arriveFrom = null;
    const e = this.add({
      kind: 'player',
      x: where.x,
      y: where.y,
      name: progress.name,
      title: progress.title,
      karma: progress.karma,
      pkPoints: progress.pkPoints,
      jailT: progress.jailT,
    });
    const s = newPlayerSlot(e, progress);
    this.slots.set(e.id, s);
    this.as(s, () => this.refreshPlayerStats(true));
    return e.id;
  }

  /**
   * A player leaves the zone: their body goes, foes chasing them turn home, and their progress
   * (with what the body still held: karma, PK points, jail time) is handed back to be saved.
   * The primary player cannot leave (the World is theirs); an unknown id returns undefined.
   */
  removePlayer(id: number): PlayerProgress | undefined {
    const s = this.slots.get(id);
    if (!s) return undefined;
    if (s === this.primary) throw new Error('the primary player cannot leave the World');
    if (s === this.cur) throw new Error('a player cannot be removed during their own turn');
    this.progressOf(id);
    this.slots.delete(id);
    this.entities.delete(id);
    for (const o of this.entities.values()) {
      if (o.targetId === id) o.targetId = null;
      if (o.brain?.foeId === id) {
        o.brain.foeId = null;
        if (o.brain.state === 'chase') o.brain.state = 'return';
        o.cast = null;
      }
    }
    return s.progress;
  }

  /**
   * A net replica only (net/snapshot.ts `syncStandIns`): an entity the server has and this World did
   * not build (another player), under the server's id. Null when the id is taken.
   */
  replicaAdd(o: Partial<Entity> & { id: number; kind: EntityKind; x: number; y: number }): Entity | null {
    if (this.entities.has(o.id)) return null;
    const next = this.nextId;
    const e = this.add(o);
    this.nextId = next;
    return e;
  }

  /** A net replica only: a stand-in the server no longer shows goes. */
  replicaRemove(id: number): void {
    if (this.slots.has(id)) return;
    this.entities.delete(id);
  }

  /** The primary is the zone's keeper on a server, not a character (`park`). */
  private parked = false;

  /**
   * On a zone server: the primary slot stays as the World's anchor but is nobody's character. Its
   * body and the pet leave the zone (never seen, struck or chased), it never wakes monsters, and it
   * lies down for good. Players join with `addPlayer`.
   */
  park(): void {
    if (this.parked) return;
    this.parked = true;
    const p = this.primary.entity;
    Object.assign(p, { dead: true, deadT: Infinity, path: null, targetId: null });
    this.entities.delete(p.id);
    this.entities.delete(this.pet.id);
  }

  /** A player's progress with what their body still holds (karma, PK points, jail) written back: to save. */
  progressOf(id: number): PlayerProgress | undefined {
    const s = this.slots.get(id);
    if (!s) return undefined;
    const e = s.entity;
    Object.assign(s.progress, { karma: e.karma, pkPoints: e.pkPoints, jailT: Math.max(0, e.jailT) });
    return s.progress;
  }

  /** Runs `f` with `s` as the current player (`player`, `progress`, `input` … are its). */
  private as<T>(s: PlayerSlot, f: () => T): T {
    if (s === this.cur) return f();
    const prev = this.cur;
    this.cur = s;
    try {
      return f();
    } finally {
      this.cur = prev;
    }
  }

  /** The player in the slot of `e`, if `e` is a player of this zone. */
  private slotOf(e: Entity | undefined): PlayerSlot | undefined {
    return e ? this.slots.get(e.id) : undefined;
  }

  /**
   * Applies a player's command. `from` is the sender's entity id (default: the current player, the
   * primary outside the rules); a command from an id that is not a player here is dropped.
   */
  apply(cmd: PlayerCommand, from?: number): void {
    const s = from === undefined ? this.cur : this.slots.get(from);
    if (!s) return;
    this.as(s, () => this.applyNow(cmd));
  }

  private applyNow(cmd: PlayerCommand): void {
    const p = this.player;
    const inp = this.input;
    // Cutscenes, the carriage and interiors are still the primary player's alone (ONLINE.md §2, 1f).
    const first = this.cur === this.primary;
    // A cutscene holds the player: only its own commands are heard.
    if (cmd.t === 'sceneAdvance' || cmd.t === 'sceneSkip' || cmd.t === 'sceneReplay')
      return first ? this.story.command(cmd) : undefined;
    if (first && this.story.active) return;
    // Aboard a carriage (or stepping down from one) the rider only rides.
    if (first && this.carriage.locks(cmd.t)) return;
    // What the systems refuse (systems/*), in their order: in the ring (systems/arena.ts) …
    for (const s of this.systems) if (s.refuse?.(cmd)) return;
    // Indoors: no running, leaping or riding, and fighting only where the building allows it.
    if (first && this.interiors.refuse(cmd)) return;
    // A command a system claims is that system's (systems/index.ts); the rest are handled here.
    const own = this.handlers.get(cmd.t);
    if (own) return own(cmd);
    switch (cmd.t) {
      case 'enter':
      case 'exit':
      case 'changeFloor':
        if (first) this.interiors.apply(cmd);
        break;
      case 'moveTo': {
        if (p.dead) return;
        this.setAuto(false);
        inp.engage = false;
        inp.talkTo = null;
        // Past one path window (a region map), by the road (region/roads.ts).
        p.path = routeTo(this.map, p.x, p.y, cmd.x, cmd.y);
        break;
      }
      case 'moveDir':
        inp.dx = cmd.dx;
        inp.dy = cmd.dy;
        if (cmd.dx || cmd.dy) {
          // Taking the controls switches auto-battle off.
          this.setAuto(false);
          inp.engage = false;
          inp.talkTo = null;
          p.path = null;
        }
        break;
      case 'engage': {
        const t = this.get(cmd.id);
        if (!t || t.dead || !this.isHostile(p, t)) return;
        p.targetId = t.id;
        inp.engage = true;
        inp.talkTo = null;
        inp.repathT = 0;
        break;
      }
      case 'attack': {
        const cur = this.get(p.targetId);
        const t = cur && !cur.dead && this.isHostile(p, cur) ? cur : this.nearestHostile(p, 10);
        if (t) {
          p.targetId = t.id;
          inp.engage = true;
          inp.talkTo = null;
          inp.repathT = 0;
        } else this.swingAir();
        break;
      }
      case 'targetNearest': {
        const t = this.nearestHostile(p, 10);
        if (t) {
          p.targetId = t.id;
          inp.engage = true;
          inp.repathT = 0;
        } else this.notice('ไม่มีศัตรูในระยะ');
        break;
      }
      case 'select': {
        const t = this.get(cmd.id);
        const folk = t?.kind === 'ambient' && !!t.folk && !t.folk.indoors;
        if (!t || t.dead || t === p || (t.kind !== 'rival' && t.kind !== 'npc' && t.kind !== 'player' && !folk)) return;
        p.targetId = t.id;
        inp.engage = false;
        break;
      }
      case 'untarget':
        p.targetId = null;
        inp.engage = false;
        break;
      case 'partyInvite':
        this.partyInvite(cmd.id);
        break;
      case 'partyKick':
        this.partyKick(cmd.fellow);
        break;
      case 'partyLeave':
        for (const id of [...this.progress.party]) this.partyKick(id, true);
        this.log('sys', 'ยุบปาร์ตี้แล้ว');
        break;
      case 'interact': {
        const n = this.get(cmd.id);
        const folk = n?.kind === 'ambient' && !!n.folk && !n.folk.indoors;
        if (!n || (n.kind !== 'npc' && !folk) || p.dead) return;
        inp.engage = false;
        inp.talkTo = n.id;
        // Whoever is spoken to is the target too (their frame shows; the player looks at them).
        p.targetId = n.id;
        p.path = dist(p, n) < TALK_RANGE ? null : routeTo(this.map, p.x, p.y, n.x, n.y + 0.7);
        break;
      }
      case 'block':
        inp.block = cmd.on;
        break;
      case 'sprint':
        inp.sprint = cmd.on;
        break;
      case 'dash':
        this.dash(cmd.dx, cmd.dy);
        break;
      case 'jump':
        this.jump();
        break;
      case 'mount':
        this.toggleMount();
        break;
      case 'setTitle':
        this.wearTitle(cmd.id);
        break;
      case 'buy':
        this.buy(cmd.item);
        break;
      case 'travel':
        if (p.dead || p.jailT > 0 || p.combatT > 0 || dist(p, this.guide) > 5) break;
        // The guide takes the player home to the hub from anywhere (going out is by the warps).
        if (this.map.kind !== 'hub') this.travel(HUB, false);
        break;
      case 'sell':
        this.sell(cmd.id);
        break;
      case 'fly':
        inp.fly = cmd.dir;
        break;
      case 'pk':
        if (p.dead || p.pk === cmd.on) break;
        if (cmd.on && this.pkProtectedEntity(p)) {
          this.notice('ผู้ฝึกยุทธ์หน้าใหม่เปิดแดงไม่ได้ (ต้องถึงฝึกลมปราณ ชั้น 1)');
          break;
        }
        if (cmd.on && p.jailT > 0) {
          this.notice('อยู่ในคุก เปิด PK ไม่ได้');
          break;
        }
        if (!cmd.on && p.combatT > 0) {
          this.notice(combatBlockText(p, 'ปิด PK ไม่ได้'));
          break;
        }
        p.pk = cmd.on;
        this.emit({ type: 'pk', id: p.id, on: cmd.on });
        this.notice(cmd.on ? 'เปิดแดง! โจมตีผู้เล่นอื่นได้ (นอกลานศิลา)' : 'ปิด PK แล้ว');
        break;
      case 'setMount':
        if (this.progress.mounts.includes(cmd.id)) {
          this.progress.mountPick = cmd.id;
          if (p.mount) {
            p.mount = cmd.id;
            this.emit({ type: 'mount', id: p.id, mount: cmd.id });
          }
          this.emit({ type: 'progressChanged' });
        }
        break;
      case 'sheathe':
        if (!p.dead && !p.cast && p.swingT <= 0) this.setSheathed(!p.sheathed);
        break;
      case 'questAccept':
        this.acceptQuest();
        break;
      case 'questTurnIn':
        this.turnInQuest();
        break;
      case 'setNeigong':
        this.setNeigong(cmd.id);
        break;
      case 'setArt':
        this.setArt(cmd.id);
        break;
      case 'setSkin': {
        const pr = this.progress;
        if (cmd.id === null) delete pr.skinWorn[cmd.slot];
        else if (pr.skins.includes(cmd.id) && SKINS[cmd.id]?.slot === cmd.slot)
          pr.skinWorn[cmd.slot] = cmd.id;
        else break;
        p.outfit = this.outfitOf(p);
        this.emit({ type: 'progressChanged' });
        break;
      }
      case 'claimBadge': {
        const pr = this.progress;
        const block = badgeBlock(pr, cmd.tier);
        if (block) {
          this.log('sys', block);
          break;
        }
        claimBadge(pr, cmd.tier);
        this.log(
          'sys',
          `ได้รับ${BADGE_TIERS[cmd.tier - 1]!.name}สำนัก${getSchool(pr.school!).name} พร้อมชุดสำนักขั้นนี้`,
        );
        this.grantTitle(badgeTitleId(pr.school!, cmd.tier));
        p.outfit = this.outfitOf(p);
        this.emit({ type: 'progressChanged' });
        break;
      }
      case 'wearUniform':
        if (!wearUniformTier(this.progress, cmd.tier)) break;
        p.outfit = this.outfitOf(p);
        this.emit({ type: 'progressChanged' });
        break;
      case 'joinSchool':
        this.joinSchool(cmd.id);
        break;
      case 'learnArt':
        this.learnArt(cmd.id);
        break;
      case 'qinggongLearn':
        this.learnQinggong(cmd.id);
        break;
      case 'qinggongTrain':
        this.trainQinggong(cmd.id);
        break;
      case 'learnNeigong':
        this.learnNeigong(cmd.id);
        break;
      case 'equip':
        this.equip(cmd.id);
        break;
      case 'emote':
        this.startEmote(p, cmd.id);
        break;
      case 'emoji':
        this.showEmoji(p, cmd.id);
        break;
      case 'forge':
        this.forge(cmd.id, cmd.protect);
        break;
      case 'infuse':
        this.infuse(cmd.id, cmd.el);
        break;
      case 'unequip':
        this.unequip(cmd.slot);
        break;
      case 'auto':
        this.setAuto(cmd.on);
        break;
      case 'danceStart':
      case 'danceJoin':
      case 'danceLeave':
      case 'danceInput':
        this.dance.command(cmd);
        break;
      case 'carriageBoard':
      case 'carriageSkip':
        this.carriage.command(cmd);
        break;
    }
  }

  step(dt: number): void {
    this.time += dt;
    this.pkProtectTick();
    this.hour = (this.hour + (dt * 24) / DAY_SECONDS) % 24;
    for (const e of this.entities.values()) this.tickTimers(e, dt);
    // A player's cast and spin resolve as that player (their arts, levels and gear).
    for (const e of this.entities.values()) {
      const s = this.slots.get(e.id);
      if (s) this.as(s, () => this.tickCast(e, dt));
      else this.tickCast(e, dt);
    }
    for (const e of this.entities.values()) {
      if (e.spinT <= 0) continue;
      const s = this.slots.get(e.id);
      if (s) this.as(s, () => this.tickSpin(e, dt));
      else this.tickSpin(e, dt);
    }
    // Each player in id order (docs/ONLINE.md §2, 1c): auto-battle, then controls and body.
    for (const s of this.slots.values())
      this.as(s, () => {
        if (this.auto.on) this.autoThink(dt);
        this.updatePlayer(dt);
      });
    // Doors walked into, stairs stepped onto, the fade between (interiors.ts).
    this.interiors.step(dt);
    this.updatePet(dt);
    for (const e of this.entities.values()) {
      // Outside the active cells round the players, monsters, critters and folk think nothing
      // (systems/activity.ts).
      if (
        (e.kind === 'monster' || e.kind === 'ambient' || e.kind === 'npc') &&
        this.sys.activity.asleep(e, this.time)
      )
        continue;
      // A training dummy is kept by its system (systems/dummies.ts).
      if (e.kind === 'monster' && this.sys.dummies.holds(e)) continue;
      // Far from the player and not fighting, a monster sleeps (the big maps hold hundreds).
      if (e.kind === 'monster') {
        if (this.awake(e)) this.updateMonster(e, dt);
      } else if (e.kind === 'ambient') {
        if (e.folk) this.updateFolk(e, dt);
        // The village animals think in their system (systems/animals.ts), the sparrows here.
        else if (e.critter) {
          if (!this.sys.animals.thinkCritter(e, dt)) this.updateCritter(e, dt);
        }
        else this.updateWander(e, dt, 1);
      } else if (e.kind === 'npc' && e.folk) this.updateNpcLife(e, dt);
      else if (e.kind === 'rival' && !this.dance.holds(e)) this.updateRival(e, dt);
    }
    this.separate();
    this.updateChains(dt);
    this.updatePending(dt);
    this.updateTelegraphs(dt);
    // The systems' tick, in their order (systems/index.ts): training, professions, the ring …
    for (const s of this.systems) s.step?.(dt);
    this.dance.step(dt);
    this.carriage.step(dt);
    this.story.step(dt);
  }

  /** Closest two fighters may stand (centre to centre, tiles), scaled up for big monsters. */
  static readonly PERSONAL_SPACE = 1;

  /**
   * Fighters keep a little room between them: anyone standing too close is eased apart (never
   * through walls), so foes face each other at sword's length instead of stacking up.
   */
  /** How far from the player monsters keep thinking and moving (tiles). */
  static readonly AWAKE_RADIUS = 36;

  /** A monster near the player, or one still busy with a fight or on its way home, is awake. */
  private awake(m: Entity): boolean {
    const b = m.brain!;
    if (b.state !== 'idle' || m.combatT > 0 || m.cast) return true;
    for (const { entity: p } of this.slots.values())
      if (!(this.parked && p === this.primary.entity) && Math.abs(m.x - p.x) < World.AWAKE_RADIUS && Math.abs(m.y - p.y) < World.AWAKE_RADIUS) return true;
    return false;
  }

  /** The living player nearest `m` (the primary when all are down or there is only one). */
  private nearestPlayer(m: Entity): Entity {
    let best = this.primary.entity;
    if (this.slots.size === 1) return best;
    let bd = best.dead ? Infinity : dist(m, best);
    for (const { entity: p } of this.slots.values()) {
      if (p.dead) continue;
      const d = dist(m, p);
      if (d < bd) [best, bd] = [p, d];
    }
    return best;
  }

  private separate(): void {
    // Only fighters near a player can be seen crowding; the rest are left be.
    const near = (e: Entity) => {
      for (const { entity: p } of this.slots.values())
        if (Math.abs(e.x - p.x) < 24 && Math.abs(e.y - p.y) < 24) return true;
      return false;
    };
    const movers = [...this.entities.values()].filter(
      (e) => !e.dead && (e.kind === 'player' || e.kind === 'monster') && e.dashT <= 0 && near(e),
    );
    for (let i = 0; i < movers.length; i++)
      for (let j = i + 1; j < movers.length; j++) {
        const a = movers[i]!;
        const b = movers[j]!;
        const min = World.PERSONAL_SPACE * Math.max(a.scale, b.scale);
        let dx = b.x - a.x;
        let dy = b.y - a.y;
        let d = Math.hypot(dx, dy);
        if (d >= min) continue;
        if (d < 1e-4) [dx, dy, d] = [0.7, 0.7, 1];
        // Ease apart over a few ticks; the player gives ground less than monsters do.
        const push = Math.min(0.06, (min - d) * 0.5);
        const share = a.kind === 'player' ? 0.25 : b.kind === 'player' ? 0.75 : 0.5;
        this.nudge(a, (-dx / d) * push * share * 2, (-dy / d) * push * share * 2);
        this.nudge(b, (dx / d) * push * (1 - share) * 2, (dy / d) * push * (1 - share) * 2);
      }
  }

  /** Moves an entity without turning it, stopping at walls (and at the walls between levels). */
  private nudge(e: Entity, dx: number, dy: number): void {
    const m = this.map;
    const r = e.radius;
    if (dx && freeAt(m, e.x + dx, e.y, r) && !crossesWall(m, e.x, e.y, e.x + dx, e.y, r)) e.x += dx;
    if (dy && freeAt(m, e.x, e.y + dy, r) && !crossesWall(m, e.x, e.y, e.x, e.y + dy, r)) e.y += dy;
  }

  // ───────────────────────── entities ─────────────────────────

  private add(o: Partial<Entity> & { kind: EntityKind; x: number; y: number }): Entity {
    const e: Entity = {
      id: this.nextId++,
      radius: 0.28,
      facing: 1,
      heading: 1,
      dirX: 0.7,
      dirY: 0.7,
      sprite: 'hero',
      outfit: [],
      forge: null,
      emote: null,
      emoteT: 0,
      emoji: null,
      emojiT: 0,
      scale: 1,
      name: '',
      title: '',
      level: 0,
      hp: 1,
      maxHp: 1,
      qi: 0,
      maxQi: 0,
      qiRegen: 0,
      stamina: 100,
      maxStamina: 100,
      atk: 0,
      def: 0,
      crit: 0,
      magic: 0,
      magicDef: 0,
      magicCrit: 0,
      hit: 0,
      magicHit: 0,
      dodge: 0,
      toughness: 0,
      hpRegen: 0,
      speed: 3,
      rage: 0,
      maxRage: 0,
      statuses: [],
      blocking: false,
      cast: null,
      stunT: 0,
      invulnT: 0,
      swingT: 0,
      hitT: 0,
      combatT: 0,
      attackT: 0,
      combo: 0,
      comboAt: -9,
      cooldowns: {},
      dashT: 0,
      z: 0,
      vz: 0,
      jumps: 0,
      airDashed: false,
      climbing: false,
      dashVX: 0,
      dashVY: 0,
      healT: 0,
      healRate: 0,
      meditating: false,
      mount: null,
      sheathed: false,
      flyAlt: 0,
      pk: false,
      karma: 'normal',
      pkPoints: 0,
      jailT: 0,
      guardT: 0,
      rival: null,
      spinT: 0,
      spinSkill: null,
      spinTickT: 0,
      spinPower: 0,
      moving: false,
      sprinting: false,
      dead: false,
      deadT: 0,
      animT: this.rng() * 3,
      targetId: null,
      path: null,
      stuckT: 0,
      brain: null,
      wander: null,
      folk: null,
      critter: null,
      ...o,
    };
    this.entities.set(e.id, e);
    return e;
  }

  private spawnMonster(defId: string, x: number, y: number): Entity {
    const d = getMonster(defId);
    return this.add({
      kind: 'monster',
      x,
      y,
      sprite: d.sprite,
      scale: d.scale,
      name: d.name,
      level: d.level,
      hp: d.hp,
      maxHp: d.hp,
      atk: d.atk,
      def: d.def,
      magic: d.atk,
      magicDef: d.def,
      hit: 8 + d.level,
      magicHit: 8 + d.level,
      dodge: 4 + d.level,
      speed: d.speed,
      radius: d.scale > 1 ? 0.4 : 0.28,
      facing: this.rng() < 0.5 ? 1 : -1,
      brain: { defId, spawnX: x, spawnY: y, state: 'idle', decisionT: 0, blockT: 0, foeId: null },
      wander: { cx: x, cy: y, r: 2, t: this.rng() * 2, tx: null, ty: null },
    });
  }

  private spawnBoss(): void {
    if (this.boss && !this.boss.dead) return;
    this.boss = this.spawnMonster('boss', this.map.lair.x, this.map.lair.y);
    this.boss.facing = -1;
    this.emit({ type: 'bossSpawn' });
    this.log('sys', 'ปีศาจกระบี่ทมิฬตื่นขึ้นที่ลานเลือดทางทิศใต้');
  }

  /** Recomputes player stats from the active neigong, keeping current HP/Qi ratios. */
  refreshPlayerStats(fill = false): void {
    const p = this.player;
    const pr = this.progress;
    const d = deriveStats(attributesFor(pr.activeNeigong, pr.neigong[pr.activeNeigong] ?? 1));
    const gear = equipmentStats(pr.equipment, pr.rolls, pvpNormalizerOf(this));
    this.gear = gear;
    addGearStats(d, gear, p);
    d.maxHp += gear.maxHp;
    d.speed = Math.round(d.speed * (1 + gear.speed) * 100) / 100;
    // A zone with its own walking pace (data/zones.ts `walkSpeed`: the demo plaza) sets the player's, slow or fast
    // (data/walkModes.ts, systems/walkMode.ts).
    const zoneWalk = ZONES[this.map.zone as ZoneId]?.walkSpeed;
    if (zoneWalk) d.speed = walkPace(zoneWalk, pr.walkMode);
    d.atk = Math.round((d.atk + gear.atk) * 10) / 10;
    d.def += gear.def;
    d.crit = Math.min(0.6, d.crit + gear.crit);
    const hpR = fill || !p.maxHp ? 1 : p.hp / p.maxHp;
    const qiR = fill || !p.maxQi ? 1 : p.qi / p.maxQi;
    Object.assign(p, {
      maxHp: d.maxHp,
      maxQi: d.maxQi,
      maxStamina: d.maxStamina,
      atk: d.atk,
      def: d.def,
      crit: d.crit,
      magic: d.magic,
      magicDef: d.magicDef,
      magicCrit: Math.min(0.6, d.magicCrit + gear.crit),
      hit: d.hit,
      magicHit: d.magicHit,
      dodge: d.dodge,
      toughness: d.toughness,
      hpRegen: d.hpRegen,
      maxRage: RAGE.max,
      qiRegen: d.qiRegen,
      speed: d.speed,
      hp: Math.max(1, Math.round(d.maxHp * hpR)),
      qi: d.maxQi * qiR,
      stamina: fill ? d.maxStamina : p.stamina,
      sprite: this.playerSprite(),
      outfit: this.outfitOf(p),
      forge: this.forgeOf(),
      title: pr.title,
    });
  }

  // ───────────────────────── per-tick updates ─────────────────────────

  private tickTimers(e: Entity, dt: number): void {
    if (e.emoji && (e.emojiT -= dt) <= 0) e.emoji = null;
    if (e.emote) {
      e.emoteT += dt;
      if (!this.canEmote(e)) this.startEmote(e, null);
    }
    for (const k in e.cooldowns) e.cooldowns[k] = Math.max(0, e.cooldowns[k]! - dt);
    e.stunT = Math.max(0, e.stunT - dt);
    e.invulnT = Math.max(0, e.invulnT - dt);
    e.swingT = Math.max(0, e.swingT - dt);
    e.hitT = Math.max(0, e.hitT - dt);
    e.attackT = Math.max(0, e.attackT - dt);
    e.combatT = Math.max(0, e.combatT - dt);
    e.animT += dt;
    for (let i = e.statuses.length - 1; i >= 0; i--) {
      const st = e.statuses[i]!;
      const dot = STATUSES[st.id]?.dotPct;
      if (dot && !e.dead) {
        // Bleeding and poison: lethal, credited to whoever applied them.
        e.hp -= e.maxHp * dot * st.stacks * dt;
        if (e.hp <= 0) {
          this.kill(e, this.get(st.srcId ?? -1) ?? e);
          return;
        }
      }
      st.t -= dt;
      if (st.t <= 0) {
        e.statuses.splice(i, 1);
        this.emit({ type: 'status', id: e.id, status: st.id, stacks: 0 });
      }
    }
  }

  /** Whirlwind: a round of cuts on every foe in reach each tick, until the time runs out. */
  private tickSpin(e: Entity, dt: number): void {
    const s = SKILLS[e.spinSkill ?? ''];
    if (!s?.spin || e.dead) {
      e.spinT = 0;
      return;
    }
    e.spinT = Math.max(0, e.spinT - dt);
    e.spinTickT -= dt;
    e.swingT = Math.max(e.swingT, 0.1);
    if (e.spinTickT > 0) return;
    e.spinTickT = s.spin.tick;
    this.emit({ type: 'spin', id: e.id, skillId: s.id });
    for (const o of this.hostilesWithin(e, s.aoe ?? 2))
      this.applyHit(e, o, e.spinPower, s.kind, s.id, undefined, true);
    if (e.spinT <= 0) e.spinSkill = null;
  }

  private tickCast(e: Entity, dt: number): void {
    const c = e.cast;
    if (!c || e.dead) return;
    if (e.stunT > 0) {
      e.cast = null;
      return;
    }
    c.t += dt;
    if (c.t < c.windup) return;
    e.cast = null;
    const tgt = this.get(c.targetId);
    this.executeSkill(e, getSkill(c.skillId), tgt && !tgt.dead ? tgt : null, c.at);
  }

  private updatePlayer(dt: number): void {
    const p = this.player;
    const inp = this.input;
    if (p.dead) {
      p.deadT -= dt;
      if (p.deadT <= 0) this.respawnPlayer();
      return;
    }

    // Regeneration × every system's factor (meditating: systems/meditation.ts).
    let med = 1;
    for (const s of this.systems) if (s.regenMult) med *= s.regenMult();
    p.qi = Math.min(p.maxQi, p.qi + p.qiRegen * med * dt);
    // Qinggong is spent in the air and only recovers with the feet on the ground.
    // (Nor on the water, in deep water or on a cliff: skimming, swimming and climbing spend it.)
    if (!p.sprinting && p.z <= 0 && restsStamina(this.qinggong))
      p.stamina = Math.min(p.maxStamina, p.stamina + (p.blocking ? 5 : 24) * dt);
    // Healing × the statuses' healMul (poison: status.ts).
    const healK = statusHealMul(p.statuses);
    if (p.combatT <= 0) p.hp = Math.min(p.maxHp, p.hp + p.maxHp * 0.02 * med * healK * dt);
    p.hp = Math.min(p.maxHp, p.hp + p.hpRegen * healK * dt);
    if (p.combatT <= 0) p.rage = Math.max(0, p.rage - RAGE.decayPerSecond * dt);
    if (p.healT > 0) {
      p.hp = Math.min(p.maxHp, p.hp + p.healRate * healK * Math.min(dt, p.healT));
      p.healT -= dt;
    }
    // The systems' share of the living player's tick: cultivation paid out (systems/cultivation.ts) …
    for (const s of this.systems) s.playerStep?.(dt);

    this.syncNeigongPassive(p);
    // Stepping into the warp circle, out of a fight, carries the player to the other zone.
    if (!p.dead && p.jailT <= 0 && p.combatT <= 0 && p.z <= 0 && !this.duel) {
      const warp = this.map.portals.find((w) => dist(p, w) < 0.9);
      if (warp) this.travel(warp.to, true);
    }
    this.updateGuards(dt);
    if (p.jailT > 0) {
      // In jail: the time runs down; the walls hold the prisoner in.
      p.jailT = Math.max(0, p.jailT - dt);
      this.progress.jailT = p.jailT;
      const J = this.map.jail;
      p.x = Math.min(J.x + 2.4, Math.max(J.x - 2.4, p.x));
      p.y = Math.min(J.y + 2.4, Math.max(J.y - 2.4, p.y));
      p.pk = false;
      if (p.jailT <= 0) {
        // Served: the slate is wiped down below red, and back to the plaza.
        p.pkPoints = Math.min(p.pkPoints, PK.red);
        this.progress.pkPoints = p.pkPoints;
        Object.assign(p, { x: this.map.spawn.x, y: this.map.spawn.y, path: null });
        this.emit({ type: 'jail', seconds: 0 });
        this.emit({ type: 'pkColor', id: p.id, color: pkColor(p.pkPoints) });
        this.log('sys', 'พ้นโทษ ออกจากคุกแล้ว');
      }
    }
    p.moving = false;
    p.sprinting = false;
    // In the air (or just set down on a slope by walking up it mid-jump, which still counts as a
    // landing: without it the jumps were never given back and a roof became a trap).
    const flying = !!p.mount && !!MOUNTS[p.mount]?.fly;
    if (flying) {
      // Holding Space or Q keeps climbing or diving; otherwise the rider glides to the chosen height.
      if (inp.fly)
        p.flyAlt = Math.min(MOVEMENT.flyMax, Math.max(0, p.flyAlt + inp.fly * MOVEMENT.flyRate * dt));
      p.z += (p.flyAlt - p.z) * Math.min(1, dt * 3);
      if (Math.abs(p.z - p.flyAlt) < 0.2) p.z = p.flyAlt;
      p.vz = 0;
      p.jumps = 0;
      // Held up by the mount: losing it in the air is no fall (the count starts on the ground).
      trackFall(this.qinggong, 0, false, floorAt(this.map, p.x, p.y) * PX_METRES, dt);
    } else if (p.z > 0 || p.vz > 0 || p.jumps > 0) {
      // ร่อนลม: arms spread, the fall held to a drift; in an updraft (systems/wind.ts) carried up.
      const glide = this.qinggong.gliding ? this.qgStats().glide : null;
      const lift = glide ? this.sys.wind.liftAt(p) : null;
      if (lift !== null) p.vz += (lift - p.vz) * Math.min(1, WIND.grip * dt);
      else p.vz -= MOVEMENT.gravity * dt;
      if (glide) p.vz = Math.max(p.vz, -glide.fall);
      p.z += p.vz * dt;
      const floor = floorAt(this.map, p.x, p.y);
      trackFall(this.qinggong, (floor + Math.max(0, p.z)) * PX_METRES, p.vz >= 0 || p.climbing, null, dt);
      if (p.z <= 0) {
        p.z = 0;
        p.vz = 0;
        p.jumps = 0;
        p.airDashed = false;
        // Coming down on a tree (or anything else with no footing): step off to open ground. Water
        // a skimmer can stand on is footing; other water is left to the qinggong tick (sinking).
        const wet = isWaterAt(this.map, p.x, p.y);
        if (surfaceAt(this.map, p.x, p.y) === null && !wet) this.settle(p);
        this.land(p, fallHeight(this.qinggong, floor * PX_METRES, wet));
      }
    }
    this.tickQinggongArts(dt);
    if (p.stunT > 0 || this.blinking(p)) {
      p.blocking = false;
      return;
    }
    p.blocking = inp.block && !p.cast && p.dashT <= 0 && p.z <= 0 && p.spinT <= 0;
    if (p.blocking) {
      this.stopMeditating();
      this.dismount();
      this.setSheathed(false);
    }

    if (p.dashT > 0) {
      p.dashT -= dt;
      this.moveEntity(p, p.dashVX * dt, p.dashVY * dt);
      p.moving = true;
      return;
    }
    // Rooted while charging a move, except a guard-break, which can be wound up on the move.
    if (p.cast && p.cast.kind !== 'break') return;

    // Qinggong sprint (วิ่งพันลี้, once learned): only while moving, not guarding, and with stamina
    // and qinggong to spare.
    const qg = this.qgStats();
    const wantsSprint =
      inp.sprint &&
      !!qg.sprint &&
      !p.mount &&
      !p.blocking &&
      (inp.dx || inp.dy || p.path) &&
      p.stamina > 0 &&
      !sealedQinggong(p.statuses) &&
      waterSpeed(this.qinggong) === 1;
    p.sprinting = !!wantsSprint;
    if (p.sprinting && qg.sprint) p.stamina = Math.max(0, p.stamina - qg.sprint.stamina * dt);
    // In the air: a running leap carries on (กระโจนข้ามเหว), a glide drifts on (ร่อนลม).
    const air =
      p.z > 0 ? Math.max(this.qinggong.leap, this.qinggong.gliding && qg.glide ? qg.glide.speed : 1) : 1;
    // In a walking zone the run is QG_RUN_BASE times the art's mult (6 m/s at ขั้น 1 up to 8 at ขั้น 5), not the walk's.
    const running = p.sprinting && !!qg.sprint && !!ZONES[this.map.zone as ZoneId]?.walkSpeed;
    const speed =
      (running ? QG_RUN_BASE : this.spd(p)) *
      (p.blocking ? 0.45 : p.sprinting && qg.sprint ? qg.sprint.mult : 1) *
      air *
      waterSpeed(this.qinggong);
    const target = this.get(p.targetId);
    if (inp.dx || inp.dy) {
      const [bx, by] = [p.x, p.y];
      this.moveEntity(p, inp.dx * speed * dt, inp.dy * speed * dt);
      p.moving = true;
      const blocked = Math.hypot(p.x - bx, p.y - by) < speed * dt * 0.5;
      p.climbing = false;
      // Running with qinggong into a wall: run straight up it (until the roof can be stepped onto).
      // A rock face too tall to run up is climbed hand over hand (systems/climb.ts).
      const cliff =
        blocked && climbCliff(this.map, p, this.qinggong, qg, inp.dx, inp.dy, MOVEMENT.gravity, dt);
      if (!cliff && blocked && p.sprinting && qg.climb) this.climbWall(p, inp.dx, inp.dy, dt, qg.climb);
      // Walking into a boulder or tree with open ground beyond it: vault over without a thought.
      else if (!cliff && p.z <= 0 && blocked) this.autoVault(p, inp.dx, inp.dy);
    } else if (inp.engage && target && !target.dead) {
      const art = this.activeArt();
      const reach = art.basic.range + (target.scale - 1) * 0.4;
      if (dist(p, target) > reach) {
        inp.repathT -= dt;
        if (inp.repathT <= 0 || !p.path) {
          inp.repathT = 0.35;
          p.path = findPath(this.map, p.x, p.y, target.x, target.y);
        }
        if (p.path) this.followPath(p, speed, dt);
      } else {
        p.path = null;
        this.face(p, target);
        // Blows can be struck in mid-air too.
        if (!p.blocking && !p.cast && p.attackT <= 0 && p.spinT <= 0) {
          this.dismount();
          this.setSheathed(false);
          const step = this.nextCut(p);
          this.emit({ type: 'basic', srcId: p.id, tgtId: target.id, step });
          this.strike(p, target, art.basic.power * BASIC_CHAIN[step]!.power, 'attack', 'basic', reach);
        }
      }
    } else {
      if (inp.engage && (!target || target.dead)) inp.engage = false;
      if (p.path) this.followPath(p, speed, dt);
    }

    if (inp.talkTo != null) {
      const n = this.get(inp.talkTo);
      if (n && dist(p, n) < TALK_RANGE) {
        inp.talkTo = null;
        p.path = null;
        this.face(p, n);
        this.face(n, p);
        if (n.kind === 'ambient') this.greet(n, true);
        else this.openDialog(n);
      }
    }
    if (p.moving) this.stopMeditating();
  }

  private updatePet(dt: number): void {
    const p = this.player;
    const pet = this.pet;
    const tx = p.x - p.dirX * 0.9 + 0.35;
    const ty = p.y - p.dirY * 0.9 + 0.35;
    const dx = tx - pet.x;
    const dy = ty - pet.y;
    const d = Math.hypot(dx, dy);
    pet.moving = false;
    if (d > 9) {
      pet.x = p.x;
      pet.y = p.y;
    } else if (d > 0.45) {
      const sp = Math.min(this.spd(p) * 1.2, d * 4);
      this.moveEntity(pet, (dx / d) * sp * dt, (dy / d) * sp * dt);
      pet.moving = sp > 0.8;
    } else {
      pet.facing = p.facing;
      pet.heading = p.heading;
    }
  }

  // ───────────────────────── cutscenes ─────────────────────────

  /** The cutscene under way (`{ id, t, waiting }`), or null (scene.ts). */
  get scene(): SceneState | null {
    return this.story.state;
  }

  /** A cutscene begins: the player lets go of everything they were doing. */
  private holdForScene(): void {
    const p = this.player;
    const inp = this.input;
    Object.assign(inp, { dx: 0, dy: 0, block: false, sprint: false, engage: false, talkTo: null, fly: 0 });
    this.setAuto(false);
    this.stopMeditating();
    p.path = null;
    p.blocking = false;
    p.sprinting = false;
    p.targetId = null;
  }

  /** A cutscene sets the player down (the pet beside them). */
  private setDownForScene(x: number, y: number): void {
    const p = this.player;
    Object.assign(p, { x, y, z: 0, path: null });
    const pet = this.pet;
    const [px, py] = freeAt(this.map, x + 0.8, y + 0.4) ? [x + 0.8, y + 0.4] : [x, y];
    Object.assign(pet, { x: px, y: py, path: null });
  }

  // ───────────────────────── townsfolk ─────────────────────────

  /** The part of the day: dawn, day, dusk or night. */
  get dayPart(): DayPart {
    return dayPartAt(this.hour);
  }

  /** The named time of day (รุ่งเช้า, ช่วงสาย, หัวค่ำ…; data/time.ts). */
  get period(): TimePeriod {
    return periodAt(this.hour);
  }

  /** Whether the sun is up (06:00–18:00: 30 real minutes light, then 30 dark). */
  get daytime(): boolean {
    return isDaytime(this.hour);
  }

  /** A landmark of this map (or the plaza) shifted by (dx, dy), on open ground; null if unknown. */
  private landmark(at: string, dx = 0, dy = 0, r = 0): Point | null {
    const m = this.map;
    const base = at === 'plaza' ? m.plaza : m.landmarks?.[at];
    if (!base) return null;
    const a = this.rng() * Math.PI * 2;
    const k = r * Math.sqrt(this.rng());
    const x = base.x + dx + Math.cos(a) * k;
    const y = base.y + dy + Math.sin(a) * k;
    if (freeAt(m, x, y)) return { x, y };
    const alt = nearestFloor(m, x, y, 3);
    return alt ? { x: alt[0], y: alt[1] } : null;
  }

  private newFolkBrain(id: string, fixed: boolean, e?: Entity): FolkBrain {
    return {
      id,
      fixed,
      part: this.dayPart,
      idx: -1,
      station: null,
      act: 'idle',
      actT: 0,
      walking: false,
      tx: 0,
      ty: 0,
      indoors: false,
      gesture: null,
      gestureT: 0,
      greetT: 0,
      barkT: FOLK.barkEvery[0] * this.rng(),
      fleeT: 0,
      scaredT: 0,
      attending: false,
      chatLine: 0,
      chatT: 0,
      sparStep: 0,
      blockT: 0,
      lantern: false,
      homeHeading: e?.heading ?? 2,
      homeFacing: e?.facing ?? 1,
    };
  }

  /** The zone's townsfolk at their stations for the hour, its critters, and manners for key NPCs. */
  private spawnLife(): void {
    const zone = this.map.zone;
    for (const def of FOLK_LIST) {
      if (def.zone !== zone) continue;
      const e = this.add({
        kind: 'ambient',
        x: this.map.plaza.x,
        y: this.map.plaza.y,
        name: def.name,
        title: def.title,
        sprite: def.sprite,
        speed: def.speed,
        radius: def.child ? 0.2 : 0.28,
      });
      e.folk = this.newFolkBrain(def.id, false);
      this.folk.set(def.id, e);
      // Already somewhere in the day's round, as if the village had been going on all along.
      const sched = def.schedule[e.folk.part];
      e.folk.idx = Math.floor(this.rng() * sched.length) - 1;
      this.nextStation(e, true);
    }
    // Partners in a chat or a bout meet up: someone whose partner is already waiting on them joins in.
    for (const e of this.folk.values()) {
      const f = e.folk!;
      const sched = FOLKS[f.id]!.schedule[f.part];
      const k = sched.findIndex((st) => st.with && this.folk.get(st.with)?.folk?.station?.with === f.id);
      if (k < 0 || f.station?.with) continue;
      f.idx = k - 1;
      this.nextStation(e, true);
    }
    for (const c of CRITTERS) {
      if (c.zone !== zone) continue;
      for (let k = 0; k < c.n; k++) {
        const at = this.landmark(c.at, c.dx, c.dy, 1.5);
        if (!at) continue;
        const e = this.add({
          kind: 'ambient',
          x: at.x,
          y: at.y,
          name: '',
          title: '',
          sprite: c.kind,
          speed: c.kind === 'cat' ? 1.4 : 1.2,
          radius: 0.14,
          facing: this.rng() < 0.5 ? 1 : -1,
          wander: {
            cx: at.x,
            cy: at.y,
            r: c.kind === 'cat' ? 2.5 : 1.6,
            t: this.rng() * 3,
            tx: null,
            ty: null,
          },
        });
        e.critter = { kind: c.kind, cx: at.x, cy: at.y, state: 'idle', t: 0, vx: 0, vy: 0 };
      }
    }
    this.elder.folk = this.newFolkBrain('elder', true, this.elder);
    this.guide.folk = this.newFolkBrain('guide', true, this.guide);
    this.merchant.folk = this.newFolkBrain('merchant', true, this.merchant);
    for (const m of this.schoolMasters.values()) m.folk = this.newFolkBrain('master', true, m);
  }

  private linesOf(f: FolkBrain): FolkLines {
    return f.fixed ? NPC_LINES[f.id as keyof typeof NPC_LINES] : FOLKS[f.id]!.lines;
  }

  /**
   * On to the next station of the part of the day: indoors folk come out of their door first.
   * `now` puts them straight there (when the zone loads) instead of walking.
   */
  private nextStation(e: Entity, now = false): void {
    const f = e.folk!;
    const def = FOLKS[f.id]!;
    const sched = def.schedule[f.part];
    f.idx = (f.idx + 1) % sched.length;
    const st = sched[f.idx]!;
    f.station = st;
    f.gesture = null;
    f.lantern = !!st.lantern;
    e.path = null;
    e.wander = null;
    if (st.act === 'home' && f.indoors) return;
    if (f.indoors) {
      // Out of the door.
      const door = this.landmark(def.home);
      if (door) {
        e.x = door.x;
        e.y = door.y;
      }
      f.indoors = false;
    }
    const spot = this.landmark(st.at, st.dx, st.dy, st.r ? Math.min(st.r, 1.5) : 0) ?? { x: e.x, y: e.y };
    f.tx = spot.x;
    f.ty = spot.y;
    if (now) {
      e.x = spot.x;
      e.y = spot.y;
      this.arrive(e);
      f.actT *= 0.3 + this.rng() * 0.7;
      return;
    }
    f.walking = true;
    f.act = st.carry ? 'haul' : 'idle';
  }

  private arrive(e: Entity): void {
    const f = e.folk!;
    const st = f.station!;
    f.walking = false;
    e.path = null;
    e.moving = false;
    if (st.act === 'home') {
      f.indoors = true;
      f.act = 'home';
      f.actT = Infinity;
      return;
    }
    f.act = st.act;
    f.actT = st.dur[0] + this.rng() * (st.dur[1] - st.dur[0]);
    if (st.face) this.face(e, { x: e.x + st.face[0], y: e.y + st.face[1] });
    if (st.act === 'stroll' || st.act === 'play')
      e.wander = { cx: e.x, cy: e.y, r: st.r ?? 2, t: 0, tx: null, ty: null };
    f.chatLine = 0;
    f.chatT = 1.5 + this.rng() * 2;
  }

  /** Where a townsperson should run from: a blow struck near them, or a red name coming close. */
  private dangerFor(e: Entity): Point | null {
    const lf = this.lastFight;
    if (lf && this.time - lf.t < 3 && Math.hypot(lf.x - e.x, lf.y - e.y) < FOLK.danger) return lf;
    const p = this.player;
    const red = pkColor(p.pkPoints);
    if (!p.dead && (red === 'red' || red === 'purple' || p.pk) && dist(p, e) < 4) return p;
    return null;
  }

  /** Says hello to the player: a bow (a wave from a child) and a greeting. */
  private greet(e: Entity, asked = false): void {
    const f = e.folk;
    if (!f) return;
    if (!asked && f.greetT > 0) return;
    f.greetT = FOLK.greetEvery;
    f.gesture = !f.fixed && FOLKS[f.id]?.child ? 'wave' : 'bow';
    f.gestureT = 1.3;
    this.face(e, this.player);
    // A line for the moment and the player (systems/npcTalk.ts), else the person's own.
    const said = this.folkLine(e, 'greet');
    if (said) return this.bark(e, said.text, said.act);
    const lines = this.linesOf(f);
    // A disciple of a school, or a name feared for killing, is greeted for it now and then.
    const pr = this.progress;
    let pool: readonly string[] = lines.greet;
    if (pr.karma === 'evil' || pr.karma === 'mad') pool = this.rng() < 0.6 ? GOSSIP.evil : pool;
    else if (pr.school && this.rng() < 0.3) pool = GOSSIP.school;
    this.bark(e, this.pick(pool));
  }

  private pick<T>(xs: readonly T[]): T {
    return xs[Math.floor(this.rng() * xs.length)]!;
  }

  private bark(e: Entity, text: string, act?: TalkAct): void {
    const school = this.progress.school ? getSchool(this.progress.school).name : '';
    this.emit({
      type: 'bark',
      id: e.id,
      text: text.replaceAll('{name}', this.progress.name).replaceAll('{school}', school),
      ...(act ? { act } : {}),
    });
  }

  /** The first system's line for this person (systems/npcTalk.ts), or null. */
  private folkLine(e: Entity, on: TalkOn): { text: string; act?: TalkAct } | null {
    for (const s of this.systems) {
      const l = s.folkLine?.(e, on);
      if (l) return l;
    }
    return null;
  }

  /** Something to say out of the blue: about the work, the hour, the news. */
  private barkLine(e: Entity): { text: string; act?: TalkAct } | null {
    const said = this.folkLine(e, 'idle');
    if (said) return said;
    const f = e.folk!;
    const lines = this.linesOf(f);
    const pools: [number, readonly string[] | undefined][] = [
      [4, lines.work?.[f.act]],
      [f.part === 'night' ? 3 : 0, lines.night ?? GOSSIP.night],
      [2, this.questGossip()],
      [this.worldBoss && !this.worldBoss.dead ? 1 : 0, GOSSIP.boss],
    ];
    const live = pools.filter(([w, xs]) => w > 0 && xs?.length) as [number, readonly string[]][];
    let r = this.rng() * live.reduce((a, [w]) => a + w, 0);
    for (const [w, xs] of live) if ((r -= w) <= 0) return { text: this.pick(xs) };
    return null;
  }

  private questGossip(): readonly string[] | undefined {
    const q = this.currentQuest();
    return q && this.progress.quest.status === 'active' ? GOSSIP.quest[q.id] : undefined;
  }

  /** Townsfolk and critters: timers that every one of them counts down. */
  private tickFolk(f: FolkBrain, dt: number): void {
    f.gestureT = Math.max(0, f.gestureT - dt);
    if (f.gestureT <= 0) f.gesture = null;
    f.greetT = Math.max(0, f.greetT - dt);
    f.barkT -= dt;
    f.fleeT = Math.max(0, f.fleeT - dt);
    f.scaredT = Math.max(0, f.scaredT - dt);
    f.chatT -= dt;
    f.blockT = Math.max(0, f.blockT - dt);
  }

  private updateFolk(e: Entity, dt: number): void {
    const f = e.folk!;
    this.tickFolk(f, dt);
    e.moving = false;
    if (f.blockT <= 0) e.blocking = false;
    if (f.part !== this.dayPart) {
      // A new part of the day: start its round (indoors folk come out, or go home).
      f.part = this.dayPart;
      f.idx = -1;
      this.nextStation(e);
    }
    if (f.indoors) return;
    const p = this.player;
    const d = dist(e, p);

    // Trouble: run from it, then cower where they stopped.
    const danger = this.dangerFor(e);
    if (danger || f.fleeT > 0) {
      if (danger) {
        if (f.fleeT <= 0 && f.scaredT <= 0 && d < FOLK.hearing) {
          this.bark(e, this.pick(this.linesOf(f).scared));
          f.scaredT = 8;
        }
        f.fleeT = 2.5;
        e.path = null;
        e.blocking = false;
        const dx = e.x - danger.x;
        const dy = e.y - danger.y;
        const l = Math.hypot(dx, dy) || 1;
        if (l < FOLK.danger) {
          const sp = e.speed * 1.8 * dt;
          if (this.moveEntity(e, (dx / l) * sp, (dy / l) * sp)) {
            e.moving = true;
            f.gesture = null;
            return;
          }
        }
        this.face(e, danger);
      }
      f.gesture = 'cower';
      f.gestureT = Math.max(f.gestureT, 0.3);
      // Back on the way to their station once it is over.
      if (f.fleeT <= dt) f.walking = true;
      return;
    }
    if (f.gestureT > 0) return;

    // Someone standing close: stop and look at them; say hello once in a while.
    if (!p.dead && d < FOLK.notice && f.act !== 'spar' && p.jailT <= 0) {
      this.face(e, p);
      e.path = null;
      f.attending = true;
      this.greet(e);
      return;
    }
    if (f.attending) {
      f.attending = false;
      if (!f.walking && f.station?.face)
        this.face(e, { x: e.x + f.station.face[0], y: e.y + f.station.face[1] });
    }

    if (f.walking) {
      if (!e.path) e.path = findPath(this.map, e.x, e.y, f.tx, f.ty);
      if (e.path) this.followPath(e, e.speed, dt);
      if (!e.path || Math.hypot(f.tx - e.x, f.ty - e.y) < 0.25) this.arrive(e);
      return;
    }

    f.actT -= dt;
    switch (f.act) {
      case 'stroll':
        this.updateWander(e, dt, 0.5);
        break;
      case 'play':
        this.play(e, dt);
        break;
      case 'chat':
        this.chat(e);
        break;
      case 'spar':
        this.spar(e);
        break;
    }
    if (f.actT <= 0) this.nextStation(e);

    if (f.barkT <= 0) {
      f.barkT = FOLK.barkEvery[0] + this.rng() * (FOLK.barkEvery[1] - FOLK.barkEvery[0]);
      if (d < FOLK.hearing && f.act !== 'chat') {
        const line = this.barkLine(e);
        if (line) this.bark(e, line.text, line.act);
      }
    }
  }

  /** A child chasing the nearest chicken about its station, or ambling if there is none. */
  private play(e: Entity, dt: number): void {
    const w = e.wander!;
    let best: Entity | null = null;
    let bd = 5;
    for (const c of this.entities.values()) {
      if (c.critter?.kind !== 'chicken' || c.critter.state === 'gone') continue;
      const d = Math.hypot(c.x - w.cx, c.y - w.cy);
      if (d < bd && dist(c, e) < 6) [best, bd] = [c, d];
    }
    if (!best) return this.updateWander(e, dt, 0.7);
    const dx = best.x - e.x;
    const dy = best.y - e.y;
    const d = Math.hypot(dx, dy);
    if (d < 0.6) return;
    const sp = e.speed * 0.9 * dt;
    if (this.moveEntity(e, (dx / d) * sp, (dy / d) * sp)) e.moving = true;
  }

  /** The partner of a chat or bout when they are there for it too. */
  private partnerOf(e: Entity, act: Activity): Entity | null {
    const id = e.folk!.station?.with;
    const o = id ? this.folk.get(id) : undefined;
    if (!o?.folk || o.folk.indoors || o.folk.act !== act || o.folk.walking || dist(o, e) > 3) return null;
    return o;
  }

  /** Two people talking: facing each other, the one who leads the chat says its lines in turn. */
  private chat(e: Entity): void {
    const f = e.folk!;
    const o = this.partnerOf(e, 'chat');
    if (!o) return;
    this.face(e, o);
    const c = chatOf(f.id, o.folk!.id);
    if (!c || f.chatT > 0) return;
    if (f.chatLine >= c.lines.length) {
      // Talked out: a pause, then it starts over (they have known each other for years).
      f.chatLine = 0;
      f.chatT = 25;
      return;
    }
    const [who, text] = c.lines[f.chatLine++]!;
    if (dist(e, this.player) < FOLK.hearing) this.bark(who ? o : e, text);
    f.chatT = FOLK.chatGap;
  }

  /** A friendly bout: blows in turn, the other side catching them on the guard. */
  private spar(e: Entity): void {
    const f = e.folk!;
    const o = this.partnerOf(e, 'spar');
    if (!o) return;
    this.face(e, o);
    e.combatT = Math.max(e.combatT, 1);
    // One side calls the bout; the player has to be near enough to watch it.
    if (f.id > o.folk!.id || f.chatT > 0 || dist(e, this.player) > 18) return;
    const step = f.sparStep++;
    const [atk, def] = step % 6 < 3 ? [e, o] : [o, e];
    atk.swingT = 0.32;
    atk.combatT = 2;
    def.combatT = 2;
    def.blocking = true;
    def.folk!.blockT = 0.3;
    this.emit({ type: 'basic', srcId: atk.id, tgtId: def.id, step: step % 3 });
    // A breath between exchanges.
    f.chatT = step % 3 === 2 ? 1.6 : 0.62;
  }

  /** Key NPCs keep their post: they turn to someone coming close, greet them, and talk a little. */
  private updateNpcLife(e: Entity, dt: number): void {
    const f = e.folk!;
    this.tickFolk(f, dt);
    f.part = this.dayPart;
    // The merchant sits up by a lantern at night (the stall stays open).
    const night = f.part === 'night' && f.id === 'merchant';
    f.act = night ? 'sit' : 'idle';
    f.lantern = night;
    if (e.guardT > 0 || e.combatT > 0) return;
    const p = this.player;
    const d = dist(e, p);
    if (!p.dead && d < FOLK.notice + 0.6) {
      this.face(e, p);
      this.greet(e);
      f.attending = true;
    } else if (f.attending && f.gestureT <= 0) {
      f.attending = false;
      e.heading = f.homeHeading;
      e.facing = f.homeFacing;
    }
    if (f.barkT <= 0) {
      f.barkT = FOLK.barkEvery[0] * 1.5 + this.rng() * FOLK.barkEvery[1];
      if (d < FOLK.hearing && d > FOLK.notice) {
        const line = this.barkLine(e);
        if (line) this.bark(e, line.text, line.act);
      }
    }
  }

  /** Chickens scatter, birds take wing and come back later, cats mostly lie about. */
  private updateCritter(e: Entity, dt: number): void {
    const c = e.critter!;
    c.t -= dt;
    e.moving = false;
    // Who it is wary of: the player, and any child chasing about.
    const reach = c.kind === 'bird' ? 3 : c.kind === 'chicken' ? 1.8 : 1.1;
    let threat: Entity | null = null;
    const p = this.player;
    if (!p.dead && dist(p, e) < reach) threat = p;
    else
      for (const k of this.folk.values())
        if (!k.folk!.indoors && FOLKS[k.folk!.id]?.child && dist(k, e) < reach) threat = k;
    switch (c.state) {
      case 'gone':
        if (c.t <= 0) {
          // Back down to the old spot.
          const at = this.landmarkNear(c.cx, c.cy);
          e.x = at.x;
          e.y = at.y;
          e.z = 46;
          c.state = 'land';
        }
        return;
      case 'land':
        e.z = Math.max(0, e.z - 34 * dt);
        e.moving = true;
        if (e.z <= 0) c.state = 'idle';
        return;
      case 'fly':
        // Up and away over roofs and trees alike.
        e.z += 30 * dt;
        e.x += c.vx * dt;
        e.y += c.vy * dt;
        e.moving = true;
        if (c.t <= 0) {
          c.state = 'gone';
          c.t = 14 + this.rng() * 18;
        }
        return;
      case 'flee':
        if (threat) {
          const dx = e.x - threat.x;
          const dy = e.y - threat.y;
          const l = Math.hypot(dx, dy) || 1;
          const sp = 3.4 * dt;
          // Darting this way and that, as chickens do.
          const jig = Math.sin(this.time * 9 + e.id) * 0.7;
          if (this.moveEntity(e, ((dx - dy * jig) / l) * sp, ((dy + dx * jig) / l) * sp)) e.moving = true;
        }
        if (c.t <= 0) c.state = 'idle';
        return;
    }
    if (threat && c.kind !== 'cat') {
      if (c.kind === 'bird') {
        const dx = e.x - threat.x + (this.rng() - 0.5);
        const dy = e.y - threat.y + (this.rng() - 0.5);
        const l = Math.hypot(dx, dy) || 1;
        c.vx = (dx / l) * 5;
        c.vy = (dy / l) * 5;
        c.state = 'fly';
        c.t = 2.4;
        if (e.wander) e.wander.tx = e.wander.ty = null;
        this.face(e, { x: e.x + c.vx, y: e.y + c.vy });
      } else {
        c.state = 'flee';
        c.t = 0.9;
      }
      return;
    }
    if (threat && c.kind === 'cat' && threat !== p) {
      c.state = 'flee';
      c.t = 0.8;
      return;
    }
    // Pecking and pottering about; cats roam more at night.
    const mult = c.kind === 'cat' ? (this.dayPart === 'night' ? 0.6 : 0.25) : 0.45;
    this.updateWander(e, dt, mult);
  }

  private landmarkNear(x: number, y: number): Point {
    const a = this.rng() * Math.PI * 2;
    const tx = x + Math.cos(a) * this.rng();
    const ty = y + Math.sin(a) * this.rng();
    if (freeAt(this.map, tx, ty)) return { x: tx, y: ty };
    return { x, y };
  }

  private updateWander(e: Entity, dt: number, speedMult: number): void {
    const w = e.wander;
    if (!w) return;
    w.t -= dt;
    e.moving = false;
    if (w.t <= 0) {
      w.t = 2.5 + this.rng() * 4;
      if (this.rng() < 0.6) {
        const a = this.rng() * Math.PI * 2;
        const r = this.rng() * w.r;
        w.tx = w.cx + Math.cos(a) * r;
        w.ty = w.cy + Math.sin(a) * r;
      } else w.tx = w.ty = null;
    }
    if (w.tx == null || w.ty == null) return;
    const dx = w.tx - e.x;
    const dy = w.ty - e.y;
    const d = Math.hypot(dx, dy);
    if (d < 0.15) {
      w.tx = w.ty = null;
      return;
    }
    const sp = this.spd(e) * speedMult;
    if (this.moveEntity(e, (dx / d) * sp * dt, (dy / d) * sp * dt)) e.moving = true;
    else w.tx = w.ty = null;
  }

  private updateMonster(m: Entity, dt: number): void {
    const b = m.brain!;
    const def = getMonster(b.defId);
    if (m.dead) {
      m.deadT += dt;
      if (def.respawn != null && m.deadT >= def.respawn && def.id === 'worldboss')
        this.notice(`${def.name}ปรากฏตัวอีกครั้งที่ลานเหนือสุดของถนน!`);
      if (def.respawn != null && m.deadT >= def.respawn)
        Object.assign(m, {
          dead: false,
          deadT: 0,
          hp: m.maxHp,
          x: b.spawnX,
          y: b.spawnY,
          stunT: 0,
          cast: null,
        });
      if (def.respawn != null && !m.dead) b.state = 'idle';
      return;
    }
    // In a fight it turns on whoever struck it last (the player or a party member).
    const p = b.state === 'chase' ? this.foeOf(m) : this.nearestPlayer(m);
    const d = dist(m, p);
    const home = Math.hypot(m.x - b.spawnX, m.y - b.spawnY);
    m.moving = false;
    if (m.stunT > 0) {
      m.blocking = false;
      return;
    }

    if (b.state === 'idle') {
      this.updateWander(m, dt, 0.35);
      if (!p.dead && d < def.aggro && !(p === this.primary.entity && this.story.active)) {
        if (p !== this.primary.entity) b.foeId = p.id;
        this.aggro(m);
      }
      return;
    }
    if (b.state === 'return') {
      m.blocking = false;
      if (home > 0.3) {
        this.moveEntity(
          m,
          ((b.spawnX - m.x) / home) * this.spd(m) * 1.4 * dt,
          ((b.spawnY - m.y) / home) * this.spd(m) * 1.4 * dt,
        );
        m.moving = true;
      } else b.state = 'idle';
      m.hp = Math.min(m.maxHp, m.hp + m.maxHp * 0.4 * dt);
      return;
    }

    // chase
    // (A player in a cutscene is let be.)
    if (p.dead || home > def.leash || (p === this.primary.entity && this.story.active)) {
      b.state = 'return';
      m.cast = null;
      return;
    }
    if (m.blocking) {
      b.blockT -= dt;
      this.face(m, p);
      if (b.blockT > 0) return;
      m.blocking = false;
    }
    if (m.cast) return;
    b.decisionT -= dt;

    if (
      def.special &&
      !m.cooldowns[def.special] &&
      d < getSkill(def.special).range &&
      b.decisionT <= 0 &&
      this.rng() < 0.3
    ) {
      b.decisionT = def.decisionRate;
      this.useSkill(m, def.special, p);
      return;
    }
    // Big monsters reach further with their size.
    const reach = getSkill(def.attack).range + (Math.max(1, m.scale) - 1);
    // Take up a fighting spot beside the player on screen (left or right, whichever side it is on),
    // a sword's length away. Squaring up side by side reads clearly in the isometric view, where
    // standing in front or behind would hide one fighter behind the other.
    const side = screenFacing(m.x - p.x, m.y - p.y) >= 0 ? 1 : -1;
    // ...standing just outside the space fighters keep from each other, so nobody is pushed about.
    const stand = Math.max(1.1, reach - 0.35, World.PERSONAL_SPACE * Math.max(1, m.scale) + 0.05);
    const gx = p.x + side * Math.SQRT1_2 * stand;
    const gy = p.y - side * Math.SQRT1_2 * stand;
    const gd = Math.hypot(gx - m.x, gy - m.y);
    if (d > reach - 0.15 || gd > 0.6) {
      const sp = this.spd(m) * (d > reach ? 1 : 0.6);
      const step = Math.min(gd, sp * dt);
      if (gd > 0.05) this.moveEntity(m, ((gx - m.x) / gd) * step, ((gy - m.y) / gd) * step);
      m.moving = true;
      if (d > reach - 0.15) return;
    }
    this.face(m, p);
    if (b.decisionT > 0) return;
    b.decisionT = def.decisionRate;
    // A system may act for its own monsters (systems/dungeon.ts); else the triangle AI below.
    if (!this.systems.some((s) => s.monsterDecide?.(m, p))) this.decide(m, def, p);
  }

  /** Fighter AI: reads the player's stance and answers through the triangle, with some randomness. */
  private decide(m: Entity, def: MonsterDef, p: Entity): void {
    const r = this.rng;
    const breakId = def.break && !m.cooldowns[def.break] ? def.break : null;
    if (breakId && p.blocking && r() < 0.8) {
      this.useSkill(m, breakId, p);
    } else if (p.cast?.kind === 'break' && r() < 0.85) {
      this.useSkill(m, def.attack, p);
    } else if (def.canBlock && r() < 0.22) {
      m.blocking = true;
      m.brain!.blockT = 1.2 + r();
      this.emit({ type: 'stance', id: m.id });
    } else if (breakId && r() < 0.3) {
      this.useSkill(m, breakId, p);
    } else {
      this.useSkill(m, def.attack, p);
    }
  }

  private aggro(m: Entity): void {
    const b = m.brain!;
    if (b.state === 'chase') return;
    b.state = 'chase';
    b.decisionT = 0.5;
  }

  private updatePending(dt: number): void {
    for (const h of this.pending) h.t -= dt;
    const due = this.pending.filter((h) => h.t <= 0);
    if (!due.length) return;
    this.pending = this.pending.filter((h) => h.t > 0);
    for (const h of due) {
      const src = this.get(h.srcId);
      const tgt = this.get(h.tgtId);
      if (h.blow) {
        // A blow is interrupted by a stun or a parry's stagger taken during its wind-up (the triangle).
        if (!src || src.dead || src.stunT > 0) continue;
        if (!tgt || tgt.dead || dist(src, tgt) > h.blow.reach) {
          if (!h.followUp) this.emit({ type: 'whiff', srcId: src.id });
          continue;
        }
        this.applyHit(src, tgt, h.power, h.blow.kind, h.skillId, h.blow.stun, h.followUp);
        continue;
      }
      // A volley's projectile misses a target that stepped off its line or out-turned it (data/skillAim.ts).
      if (h.volley && tgt && !tgt.dead) {
        const v = h.volley;
        if (!volleyHits(v.homing, { x: v.ox, y: v.oy }, { x: v.tx, y: v.ty }, tgt, v.flight)) continue;
      }
      if (src && !src.dead && tgt && !tgt.dead)
        this.applyHit(src, tgt, h.power, getSkill(h.skillId).kind, h.skillId, undefined, h.followUp);
    }
  }

  /** A fighter's main-weapon family multiplies the damage it takes (data/weaponFamilies.ts `takenMul`). */
  private familyTakenMul(tgt: Entity): number {
    if (tgt.kind !== 'player') return 1;
    const art = this.players.get(tgt.id)?.progress.activeArt;
    return art ? familyOf(getArt(art)).takenMul : 1;
  }

  /**
   * A blow that lands when the blade reaches the target (`COMBAT.strikeDelay` after the swing starts), not at
   * once: see `PendingHit.blow`. `hits` follow-ups come 0.15 s apart after it.
   */
  private strike(
    src: Entity,
    tgt: Entity,
    power: number,
    kind: SkillKind,
    skillId: string,
    range: number,
    stun?: number,
    hits = 1,
  ): void {
    const s = SKILLS[skillId];
    const delay =
      s?.kind === 'power'
        ? COMBAT.strikeDelayPower
        : s?.fx === 'quake'
          ? COMBAT.strikeDelayLeap
          : COMBAT.strikeDelay;
    const reach = range + COMBAT.strikeSlack + (tgt.scale - 1) * 0.4 + (Math.max(1, src.scale) - 1);
    for (let k = 0; k < hits; k++)
      this.pending.push({
        t: delay + k * 0.15,
        srcId: src.id,
        tgtId: tgt.id,
        skillId,
        power,
        ...(k ? { followUp: true } : {}),
        blow: { kind, ...(stun !== undefined ? { stun } : {}), reach },
      });
  }

  private updateTelegraphs(dt: number): void {
    for (let i = this.telegraphs.length - 1; i >= 0; i--) {
      const t = this.telegraphs[i]!;
      t.t += dt;
      if (t.t < t.life) continue;
      this.telegraphs.splice(i, 1);
      this.emit({ type: 'teleBurst', x: t.x, y: t.y, radius: t.radius });
      const src = this.get(t.srcId);
      const p = this.player;
      if (src && !src.dead && !p.dead && dist(p, t) <= t.radius) {
        const s = getSkill(t.skillId);
        if (p.z > MOVEMENT.airDodgeHeight)
          this.emit({
            type: 'hit',
            srcId: src.id,
            tgtId: p.id,
            skillId: s.id,
            outcome: 'dodge',
            damage: 0,
            crit: false,
          });
        else this.applyHit(src, p, s.power, s.kind, s.id, s.stun);
      }
    }
  }

  // ───────────────────────── combat ─────────────────────────

  private nearestHostile(e: Entity, range: number): Entity | null {
    let best: Entity | null = null;
    let bd = range;
    for (const o of this.entities.values()) {
      if (o.dead || !this.isHostile(e, o)) continue;
      const d = dist(e, o);
      if (d < bd) {
        bd = d;
        best = o;
      }
    }
    return best;
  }

  /** Foes of `of` (default: the one at the centre) within range of a point, nearest first. */
  private hostilesWithin(center: Entity | Point, range: number, of: Entity = center as Entity): Entity[] {
    return [...this.entities.values()]
      .filter((o) => !o.dead && this.isHostile(of, o) && dist(center, o) <= range)
      .sort((a, b) => dist(center, a) - dist(center, b));
  }

  /** Starts a skill. Returns false (with a notice for the player) when it cannot be used. */
  useSkill(e: Entity, skillId: string, forcedTarget?: Entity, at?: Point): boolean {
    const s = SKILLS[skillId];
    if (!s || e.dead) return false;
    const isPlayer = e.kind === 'player';
    // Cleansing moves break free of a stun; everything else waits it out.
    if ((e.stunT > 0 && !s.cleanse) || e.cast || e.dashT > 0 || e.spinT > 0 || this.blinking(e)) return false;
    if (silenced(e.statuses) && !s.cleanse) {
      if (isPlayer) this.notice('ถูกปิดจุด ใช้กระบวนท่าไม่ได้');
      return false;
    }
    if (e.cooldowns[skillId]) return false;
    const cheat = isPlayer && this.gmFlags.infQi;
    if (e.qi < s.qi * gearQiMul(this, e, s) && !cheat) {
      if (isPlayer) this.notice('ลมปราณไม่พอ');
      return false;
    }
    if (s.rage && e.rage < s.rage && !cheat) {
      if (isPlayer) this.notice(`ความโกรธไม่พอ (ต้องการ ${s.rage})`);
      return false;
    }

    let target: Entity | null = null;
    const needsTarget =
      (s.kind !== 'heal' && s.kind !== 'buff' && !s.aoe && !s.volley && s.dash !== 'forward') ||
      s.dash === 'target';
    if (needsTarget) {
      const cur = forcedTarget ?? this.get(e.targetId);
      target =
        cur && !cur.dead && this.isHostile(e, cur) && dist(e, cur) <= s.range + 0.4
          ? cur
          : this.nearestHostile(e, s.range + 0.4);
      // The player may loose a single-target move at nobody (it cuts the air); monsters never do.
      if (!target && !isPlayer) return false;
    }
    // Ground-targeted: the picked spot (or the target, or straight ahead), no farther than range.
    let spot: Point | undefined;
    if (s.ground) {
      // The picked point, the foe's feet, or full range ahead; clamped to range (data/skillAim.ts).
      const foe = target ?? this.nearestHostile(e, s.range);
      spot = groundSpot(e, s.range, at, foe);
      if (Math.hypot(spot.x - e.x, spot.y - e.y) > 0.05) this.face(e, spot);
    }
    if (s.blink && !this.hostilesWithin(e, s.aoe ?? s.range).length) {
      if (isPlayer) this.notice('ไม่มีศัตรูในระยะ');
      return false;
    }

    const free = isPlayer && this.gmFlags.infQi;
    if (!free) e.qi -= s.qi * gearQiMul(this, e, s);
    if (s.rage && !free) e.rage -= s.rage;
    if (s.cooldown && !(isPlayer && this.gmFlags.noCooldown)) e.cooldowns[skillId] = s.cooldown * gearCdMul(this, e);
    e.blocking = false;
    if (isPlayer) {
      this.stopMeditating();
      this.dismount();
      this.setSheathed(false);
      if (target) {
        e.targetId = target.id;
        this.input.engage = true;
      }
      if (s.kind !== 'heal' && s.kind !== 'buff') enterCombat(e);
    }
    if (target) this.face(e, target);
    // A guard-break in mid-air is struck at once and springs the fighter up off it: a jump and the
    // air dash come back, so jump → guard-break → jump … chains for as long as qinggong (stamina)
    // lasts.
    const airBreak = s.kind === 'break' && isPlayer && e.z > 0;
    // (The spring back up is เท้าผ่านฟ้า's; without it the break still flies at once.)
    const qgAir = airBreak ? this.qgStats() : null;
    if (qgAir?.airReset) {
      if (e.stamina >= MOVEMENT.airBreakStamina) {
        e.stamina -= MOVEMENT.airBreakStamina;
        e.jumps = Math.min(e.jumps, qgAir.maxJumps - 1);
        e.airDashed = false;
        e.vz = Math.max(e.vz, MOVEMENT.airBreakLift);
        this.emit({ type: 'airReset', id: e.id });
      } else this.notice('พลังกายหมด');
    }

    // (In a bout the break keeps its wind-up in the air: the spring above stays, but the blow is not tell-less. P1.5e)
    const castS = s.ground && s.land ? Math.max(s.windup, s.land) : s.windup; // ground: cast open until it lands
    if (castS > 0 && (!airBreak || (DUEL_FLAGS.v2 && !!this.pvpBout?.fight))) {
      e.cast = {
        skillId,
        kind: s.kind,
        t: 0,
        windup: castS * gearWindupMul(this, e, s.kind),
        targetId: target?.id ?? null,
        ...(spot ? { at: spot } : {}),
      };
      e.path = null;
      this.emit({ type: 'castStart', srcId: e.id, skillId });
    } else this.executeSkill(e, s, target, spot);
    return true;
  }

  private executeSkill(e: Entity, s: SkillDef, target: Entity | null, spot?: Point): void {
    e.swingT = 0.25;
    let power = s.power * (e.kind === 'player' ? skillPowerMult(this.skillLevel(s.id)) : 1);

    if (s.cleanse) {
      e.statuses = e.statuses.filter((st) => {
        if (!cleansable(st)) return true; // status.ts: the `cleanse` field
        this.emit({ type: 'status', id: e.id, status: st.id, stacks: 0 });
        return false;
      });
      e.stunT = 0;
    }
    if (s.self) this.addStatus(e, s.self.status, s.self.stacks ?? 1);
    if (s.empoweredBy && stacksOf(e.statuses, s.empoweredBy.status) > 0) {
      // ร่างผยอง: spend one stack for a stronger version of the move.
      power *= 1 + s.empoweredBy.bonus;
      this.addStatus(e, s.empoweredBy.status, -1);
    }
    if (s.kind === 'buff') {
      this.emit({ type: 'skill', srcId: e.id, skillId: s.id, tgtId: null });
      return;
    }
    // A scripted move only winds up here: the system that owns it resolves what it does from this event.
    if (s.scripted) {
      this.emit({ type: 'skill', srcId: e.id, skillId: s.id, tgtId: target?.id ?? null });
      return;
    }
    if (s.dash === 'forward') return this.dashThrough(e, s, power, target);
    if (s.dash === 'target' && target) this.closeIn(e, target, s.id);
    if (s.blink) return this.startChain(e, s, power);
    if (s.spin) {
      e.spinT = s.spin.duration;
      e.spinSkill = s.id;
      e.spinTickT = 0;
      e.spinPower = power;
      this.emit({ type: 'skill', srcId: e.id, skillId: s.id, tgtId: null });
      return;
    }

    if (s.kind === 'heal') {
      e.healT = 3;
      e.healRate = (e.maxHp * (s.healPct ?? 0)) / 3;
      this.emit({ type: 'skill', srcId: e.id, skillId: s.id, tgtId: null });
      return;
    }
    if (s.telegraph) {
      const at = target ?? this.player;
      const tele: Telegraph = {
        id: this.nextId++,
        srcId: e.id,
        skillId: s.id,
        x: at.x,
        y: at.y,
        radius: s.telegraph.radius,
        life: s.telegraph.delay,
        t: 0,
      };
      this.telegraphs.push(tele);
      this.emit({ type: 'telegraph', tele });
      if (e.brain?.defId === 'boss' && !this.bossWarned) {
        this.bossWarned = true;
        this.notice('ปีศาจรวมปราณ! ออกจากวงแดงด้วยวิชาตัวเบา (Space)');
      }
      return;
    }
    if (s.volley) {
      // A held lock sends every projectile to the locked foe (systems/lockOn.ts); a tap splits them.
      const locked = e.kind === 'player' ? this.get(this.sys.lockOn.lockedId()) : undefined;
      const foes = locked && !locked.dead ? [locked] : this.hostilesWithin(e, s.range);
      const homing = homingOf(s);
      // กระบี่ซ่อนเงา (systems/gearCombat.ts): more swords, the extra ones weaker.
      const more = gearVolleyExtra(this, e);
      const volley = Array.from({ length: s.volley + (more?.extra ?? 0) }, (_, k) => ({
        tgtId: foes.length ? foes[k % foes.length]!.id : -1,
        at: VOLLEY_LAUNCH + k * VOLLEY_STAGGER + VOLLEY_FLIGHT,
      }));
      for (const v of volley)
        if (v.tgtId >= 0) {
          const t = this.get(v.tgtId)!;
          this.pending.push({
            t: v.at,
            srcId: e.id,
            tgtId: v.tgtId,
            skillId: s.id,
            power: more && volley.indexOf(v) >= s.volley ? power * more.pct : power,
            volley: { homing, ox: e.x, oy: e.y, tx: t.x, ty: t.y, flight: v.at },
          });
        }
      this.emit({ type: 'skill', srcId: e.id, skillId: s.id, tgtId: null, volley });
      return;
    }
    if (s.aoe) {
      const center = s.ground && spot ? spot : e;
      this.emit({
        type: 'skill',
        srcId: e.id,
        skillId: s.id,
        tgtId: null,
        ...(s.ground && spot ? { at: spot } : {}),
      });
      for (const o of this.hostilesWithin(center, s.aoe, e)) {
        let pw = power;
        if (s.perTargetStack) {
          const n = stacksOf(o.statuses, s.perTargetStack.status);
          if (n) {
            pw *= 1 + s.perTargetStack.bonus * n;
            this.addStatus(o, s.perTargetStack.status, -n);
          }
        }
        this.strike(e, o, pw, s.kind, s.id, s.aoe + dist(center, e), s.stun, s.hits ?? 1);
      }
      return;
    }
    if (!target || dist(e, target) > s.range + 0.6 + (target.scale - 1) * 0.4 + (Math.max(1, e.scale) - 1)) {
      this.emit({ type: 'whiff', srcId: e.id });
      return;
    }
    this.emit({ type: 'skill', srcId: e.id, skillId: s.id, tgtId: target.id });
    if (s.perTargetStack) {
      const n = stacksOf(target.statuses, s.perTargetStack.status);
      if (n) {
        power *= 1 + s.perTargetStack.bonus * n;
        this.addStatus(target, s.perTargetStack.status, -n);
      }
    }
    this.strike(e, target, power, s.kind, s.id, s.range, s.stun, s.hits ?? 1);
  }

  /** Movement speed after slows and hastes. */
  /** Movement speed after slows, hastes and the mount being ridden (which replaces qinggong running). */
  private spd(e: Entity): number {
    return e.speed * statusSpeed(e.statuses) * (e.mount ? (MOUNTS[e.mount]?.speedMul ?? 1) : 1);
  }

  /** Jumping and dashing: blocked by seals, and springs frost traps. */
  private qinggongAllowed(p: Entity): boolean {
    if (sealedQinggong(p.statuses)) {
      this.notice('ลมหนาวเข้าแทรก ใช้วิชาตัวเบาไม่ได้');
      return false;
    }
    const trap = p.statuses.find((st) => STATUSES[st.id]?.trapStun);
    if (trap) {
      p.stunT = Math.max(p.stunT, getStatus(trap.id).trapStun!);
      this.addStatus(p, trap.id, -trap.stacks);
      this.notice('ผนึกน้ำแข็งแตก! มึนงง');
      return false;
    }
    return true;
  }

  /**
   * Adds (or with a negative count removes) stacks of a status, refreshing its timer.
   * Reaching the cap of a stun-at-max status stuns the bearer and clears it.
   */
  addStatus(e: Entity, id: string, stacks: number, srcId?: number): void {
    const def = STATUSES[id];
    if (!def || e.dead) return;
    let st = e.statuses.find((x) => x.id === id);
    if (!st) {
      if (stacks <= 0) return;
      st = { id, stacks: 0, t: def.duration };
      e.statuses.push(st);
    }
    if (srcId !== undefined) st.srcId = srcId;
    st.stacks = Math.min(def.maxStacks, st.stacks + stacks);
    st.t = def.duration;
    if (st.stacks <= 0 || (def.stunAtMax && st.stacks >= def.maxStacks)) {
      if (def.stunAtMax && st.stacks >= def.maxStacks) {
        e.stunT = Math.max(e.stunT, def.stunAtMax);
        e.blocking = false;
        e.cast = null;
      }
      e.statuses = e.statuses.filter((x) => x !== st);
      this.emit({ type: 'status', id: e.id, status: id, stacks: 0 });
      return;
    }
    this.emit({ type: 'status', id: e.id, status: id, stacks: st.stacks });
  }

  private blinking(e: Entity): boolean {
    return this.chains.some((c) => c.srcId === e.id);
  }

  /** Warp chain: up to `hits` slashes, the first one at once. */
  private startChain(e: Entity, s: SkillDef, power: number): void {
    const c: BlinkChain = {
      srcId: e.id,
      skillId: s.id,
      power,
      ox: e.x,
      oy: e.y,
      left: s.hits ?? 1,
      struck: new Map(),
      lastId: -1,
      t: 0,
    };
    // Untouchable for the whole chain, plus a moment to recover after the last slash.
    e.invulnT = Math.max(e.invulnT, c.left * s.blink! + 0.3);
    e.path = null;
    this.chains.push(c);
    this.emit({ type: 'skill', srcId: e.id, skillId: s.id, tgtId: this.nextInChain(e, c, s)?.id ?? null });
    this.blinkStep(c);
  }

  /**
   * The next foe of a chain: a living foe within reach of where it began that has been struck the
   * fewest times (so everyone is cut once before anyone twice), not the one just cut if there is
   * another, and the nearest of those.
   */
  private nextInChain(e: Entity, c: BlinkChain, s: SkillDef): Entity | null {
    const reach = s.aoe ?? s.range;
    const foes = [...this.entities.values()].filter(
      (o) => !o.dead && this.isHostile(e, o) && dist({ x: c.ox, y: c.oy }, o) <= reach,
    );
    const others = foes.length > 1 ? foes.filter((o) => o.id !== c.lastId) : foes;
    let best: Entity | null = null;
    for (const o of others) {
      if (!best) {
        best = o;
        continue;
      }
      const dn = (c.struck.get(o.id) ?? 0) - (c.struck.get(best.id) ?? 0);
      if (dn < 0 || (dn === 0 && dist(e, o) < dist(e, best))) best = o;
    }
    return best;
  }

  private updateChains(dt: number): void {
    for (const c of [...this.chains]) {
      c.t -= dt;
      if (c.t <= 0) this.blinkStep(c);
    }
  }

  /** One warp of a chain: vanish, reappear beside the next foe, slash it. */
  private blinkStep(c: BlinkChain): void {
    const e = this.get(c.srcId);
    const s = getSkill(c.skillId);
    const tgt = e && !e.dead && e.stunT <= 0 && c.left > 0 ? this.nextInChain(e, c, s) : null;
    if (!e || !tgt) {
      this.chains = this.chains.filter((x) => x !== c);
      if (e) e.invulnT = Math.min(e.invulnT, 0.3);
      return;
    }
    const [fx, fy] = [e.x, e.y];
    this.warpBeside(e, tgt);
    this.face(e, tgt);
    // Hold the strike pose until just before the next warp.
    e.swingT = Math.max(0.2, s.blink! - 0.04);
    this.emit({ type: 'blink', id: e.id, fromX: fx, fromY: fy, tgtId: tgt.id, skillId: s.id });

    const times = c.struck.get(tgt.id) ?? 0;
    let pw = c.power;
    if (s.perTargetStack && !times) {
      const n = stacksOf(tgt.statuses, s.perTargetStack.status);
      if (n) {
        pw *= 1 + s.perTargetStack.bonus * n;
        this.addStatus(tgt, s.perTargetStack.status, -n);
      }
    }
    c.struck.set(tgt.id, times + 1);
    c.lastId = tgt.id;
    c.left--;
    this.applyHit(e, tgt, pw, s.kind, s.id, s.stun, times > 0);
    c.t = s.blink!;
    if (!c.left) this.chains = this.chains.filter((x) => x !== c);
  }

  /**
   * Sets the fighter down just past the foe, as if it cut straight through; if that spot is
   * blocked, tries the other sides in turn, and stays put if none is free.
   */
  private warpBeside(e: Entity, tgt: Entity): void {
    const gap = Math.max(World.PERSONAL_SPACE, 0.6 + tgt.radius + e.radius);
    let a = Math.atan2(tgt.y - e.y, tgt.x - e.x);
    if (dist(e, tgt) < 0.05) a = Math.atan2(e.dirY, e.dirX);
    for (const da of [0, 0.8, -0.8, 1.6, -1.6, 2.4, -2.4, Math.PI]) {
      const x = tgt.x + Math.cos(a + da) * gap;
      const y = tgt.y + Math.sin(a + da) * gap;
      // Never through to another level (off a terrace, down past its wall).
      const level = Math.abs(levelAt(this.map, x, y) - levelAt(this.map, tgt.x, tgt.y)) <= MAX_STEP;
      if (level && freeAt(this.map, x, y, e.radius)) {
        e.x = x;
        e.y = y;
        return;
      }
    }
  }

  /** Gap closer: slide up to the target (stopping just short of it) along walkable ground. */
  private closeIn(e: Entity, target: Entity, skillId: string): void {
    const d = dist(e, target);
    const stop = 1.3 + target.radius;
    if (d <= stop) return;
    const [fx, fy] = [e.x, e.y];
    const steps = Math.ceil((d - stop) / 0.2);
    const sx = ((target.x - e.x) / d) * ((d - stop) / steps);
    const sy = ((target.y - e.y) / d) * ((d - stop) / steps);
    for (let i = 0; i < steps; i++) if (!this.moveEntity(e, sx, sy)) break;
    this.face(e, target);
    e.path = null;
    this.emit({ type: 'skillDash', id: e.id, fromX: fx, fromY: fy, skillId });
  }

  /** Forward dash: rush up to the move's range toward the target (or ahead), striking everyone in the way. */
  private dashThrough(e: Entity, s: SkillDef, power: number, target: Entity | null): void {
    const [fx, fy] = [e.x, e.y];
    let [dx, dy] = target ? [target.x - e.x, target.y - e.y] : [e.dirX, e.dirY];
    const l = Math.hypot(dx, dy) || 1;
    [dx, dy] = [dx / l, dy / l];
    const struck = new Set<number>();
    for (let k = 0; k < s.range / 0.2; k++) {
      if (!this.moveEntity(e, dx * 0.2, dy * 0.2)) break;
      for (const o of this.hostilesWithin(e, 0.9)) struck.add(o.id);
    }
    e.path = null;
    this.emit({ type: 'skillDash', id: e.id, fromX: fx, fromY: fy, skillId: s.id });
    this.emit({ type: 'skill', srcId: e.id, skillId: s.id, tgtId: null });
    for (const id of struck) {
      const o = this.get(id);
      if (o && !o.dead) this.applyHit(e, o, power, s.kind, s.id, s.stun);
    }
  }

  /** A blow, run as the player who struck it (or, from a monster, the player struck). */
  private applyHit(
    src: Entity,
    tgt: Entity,
    power: number,
    kind: SkillKind,
    skillId: string,
    stun?: number,
    followUp = false,
  ): HitOutcome | null {
    const s = this.slotOf(src) ?? this.slotOf(tgt) ?? this.cur;
    return this.as(s, () => this.applyHitNow(src, tgt, power, kind, skillId, stun, followUp));
  }

  private applyHitNow(
    src: Entity,
    tgt: Entity,
    power: number,
    kind: SkillKind,
    skillId: string,
    stun: number | undefined,
    followUp: boolean,
  ): HitOutcome | null {
    // Nobody can strike a player in a cutscene.
    if (tgt === this.primary.entity && this.story.active) return null;
    // A move with armour (\`SkillDef.armour\`) is neither interrupted nor stunned out of while it winds up.
    const armoured = !!(tgt.cast && SKILLS[tgt.cast.skillId]?.armour) || !!this.armourHook?.(tgt);
    // External arts (and monsters) hit with body and blade; internal arts with inner energy.
    const art = skillId === 'basic' && src.kind === 'player' ? this.activeArt() : artOfSkill(skillId);
    const inner = art?.damage === 'internal';
    let mult = 1;
    if (art && src.kind === 'player' && art.element === getNeigong(this.progress.activeNeigong).element)
      mult = ELEMENT_MATCH_BONUS;
    // A legendary art's prize rule (data/legendary.ts): full power on NPCs and monsters, normalised on players.
    power *= legendaryPower(art, tgt.kind === 'player');
    const res = resolveHit(
      {
        atk: (inner ? src.magic : src.atk) * mult,
        power,
        kind,
        stun,
        critChance: (inner ? src.magicCrit : src.crit) + statusCrit(src.statuses) - gearCritRes(this, tgt),
        hit: inner ? src.magicHit : src.hit,
      },
      {
        blocking: tgt.blocking,
        stamina: tgt.stamina,
        invulnerable: tgt.invulnT > 0,
        castingKind: armoured ? null : (tgt.cast?.kind ?? null),
        def: (inner ? tgt.magicDef : tgt.def) * gearDefMul(this, src),
        dodge: tgt.dodge,
      },
      this.rng,
    );
    if (res.stun > 0) res.stun *= (100 / (100 + tgt.toughness)) * gearStunMul(this, src);
    if (res.damage > 0)
      res.damage = Math.max(
        1,
        Math.round(
          res.damage *
            legendaryDealt(art, tgt.kind === 'player', statusMul(src.statuses, 'damageDealtMul')) *
            statusMul(tgt.statuses, 'damageTakenMul') *
            this.familyTakenMul(tgt) *
            (src.kind === 'player' ? this.sys.codex.mul(tgt) : 1) *
            (src.kind === 'player' ? gearHitMul(this, src, this.artOfHit(skillId), res) : 1),
        ),
      );
    if (tgt.kind === 'player' && this.gmFlags.god) Object.assign(res, { damage: 0, stun: 0 });
    if (src.kind === 'player' && this.gmFlags.oneHit && res.outcome !== 'dodge')
      res.damage = Math.max(res.damage, tgt.hp);
    // A bout's own rules, before anything is applied or told.
    const pbm = this.pvpBout;
    const dueled =
      !!this.duelMod &&
      !!pbm?.fight &&
      (pbm.a === tgt.id || pbm.b === tgt.id) &&
      (pbm.a === src.id || pbm.b === src.id);
    if (dueled) this.duelMod!(src, tgt, res, kind, skillId);
    enterCombat(src);
    enterCombat(tgt);
    if (src.kind === 'player') this.stopMeditating();
    if (tgt.kind === 'player') {
      this.stopMeditating();
      // Knocked off the mount by any blow, even a dodged one.
      this.dismount();
    }
    if (tgt.kind === 'monster') {
      this.aggro(tgt);
      tgt.brain!.foeId = src.id;
    }
    if (tgt.rival) {
      tgt.rival.aggroT = 12;
      enterCombat(tgt);
    }

    tgt.hp -= res.damage;
    tgt.stamina = Math.max(0, tgt.stamina - res.staminaDamage * gearGuardMul(this, src, tgt));
    if (res.damage > 0) tgt.hitT = 0.12;
    if (res.outcome === 'interrupt') tgt.cast = null;
    if (armoured) res.stun = 0;
    if (res.stun > 0) {
      tgt.stunT = Math.max(tgt.stunT, res.stun);
      tgt.blocking = false;
      tgt.cast = null;
      if (tgt.brain) tgt.brain.blockT = 0;
    }
    if (res.outcome === 'blocked' && !dueled) {
      src.stunT = Math.max(src.stunT, COMBAT.deflectStagger);
      src.cast = null;
    }
    // Rage builds from trading blows.
    if (res.damage > 0 && res.outcome !== 'dodge') {
      if (tgt.maxRage) tgt.rage = Math.min(tgt.maxRage, tgt.rage + RAGE.perHitTaken * gearRageMul(this, tgt));
    }
    const landed = (res.outcome === 'hit' || res.outcome === 'interrupt' || res.outcome === 'guardBreak') && !res.spent;
    const sk = SKILLS[skillId];
    if (sk && landed && !tgt.dead) {
      if (sk.hitStun && !followUp && !armoured) {
        tgt.stunT = Math.max(tgt.stunT, sk.hitStun * (100 / (100 + tgt.toughness)));
        tgt.blocking = false;
        tgt.cast = null;
      }
      if (sk.applies && !(armoured && isControlStatus(sk.applies.status))) this.addStatus(tgt, sk.applies.status, sk.applies.stacks ?? 1, src.id); // red: no control
      if (sk.onGuardBreak && res.outcome === 'guardBreak')
        this.addStatus(tgt, sk.onGuardBreak.status, 1, src.id);
      if (sk.lifesteal) src.hp = Math.min(src.maxHp, src.hp + res.damage * sk.lifesteal * statusHealMul(src.statuses));
    }
    // Lifesteal from statuses (คลั่งโลหิต, กระหายโลหิต) on every blow that lands.
    const drain =
      landed && res.damage > 0
        ? statusLifesteal(src.statuses) + (this.slotOf(src)?.gear.lifesteal ?? 0)
        : 0;
    if (drain && !src.dead) src.hp = Math.min(src.maxHp, src.hp + res.damage * drain * statusHealMul(src.statuses));
    this.emit({
      type: 'hit',
      srcId: src.id,
      tgtId: tgt.id,
      skillId,
      outcome: res.outcome,
      damage: res.damage,
      crit: res.crit,
    });

    if (src.kind === 'player' && res.outcome === 'guardBreak') {
      this.questEvent('guardBreak');
      this.log('fight', `ทำลายการตั้งรับของ${tgt.name}!`);
    }
    if (src.kind === 'player' && res.outcome === 'interrupt') {
      this.questEvent('interrupt');
      this.log('fight', `ขัดจังหวะท่าทำลายของ${tgt.name}!`);
    }
    if (tgt.kind === 'player' && res.outcome === 'blocked') this.questEvent('block');
    if (tgt.kind === 'player' && res.outcome === 'guardBreak') this.notice('การ์ดแตก! ศัตรูใช้ท่าทำลาย');

    if (tgt.hp <= 0) this.kill(tgt, src);
    return res.outcome;
  }

  /** A death, run as the player who died, else as the player who struck it down. */
  private kill(tgt: Entity, src: Entity, how?: string): void {
    const s = (tgt.kind === 'player' ? this.slotOf(tgt) : undefined) ?? this.slotOf(src) ?? this.cur;
    this.as(s, () => this.killNow(tgt, src, how));
  }

  private killNow(tgt: Entity, src: Entity, how?: string): void {
    // A duel between players is not to the death: the loser keeps a sliver, the bout ends (systems/pvpDuel.ts).
    const pb = this.pvpBout;
    // (In any phase of the bout: a bleed or a late blow between rounds must not kill, nor touch karma.)
    if (pb && (tgt.id === pb.a || tgt.id === pb.b)) {
      tgt.hp = pb.fight ? 1 : Math.max(1, tgt.hp);
      return;
    }
    const d = this.duel;
    if (d?.phase === 'fight' && (tgt === this.player || tgt.id === d.foeId)) {
      // A bout is not to the death: the loser drops, the player keeps a sliver of blood.
      if (tgt === this.player) tgt.hp = 1;
      else {
        Object.assign(tgt, { hp: 0, dead: true, deadT: 0, cast: null, blocking: false });
        this.emit({ type: 'death', id: tgt.id });
      }
      return this.sys.arena.endDuel(tgt !== this.player);
    }
    tgt.hp = 0;
    tgt.dead = true;
    tgt.deadT = 0;
    tgt.cast = null;
    tgt.blocking = false;
    this.emit({ type: 'death', id: tgt.id });
    if (
      src !== tgt &&
      (src.kind === 'player' || src.kind === 'rival') &&
      (tgt.kind === 'player' || tgt.kind === 'rival')
    )
      this.pkKill(src, tgt);

    if (tgt.kind === 'monster') {
      const def = getMonster(tgt.brain!.defId);
      // The player's own kills, and the party's made near the player, pay out (shared in a party).
      const p = this.player;
      const credited =
        src.kind === 'player' || (this.inParty(src) && !p.dead && dist(p, tgt) <= PARTY.shareRange);
      if (credited) {
        const sharing =
          1 + this.partyMembers().filter((e) => !e.dead && dist(e, tgt) <= PARTY.shareRange).length;
        const cult = Math.round(partyShare(def.reward.cultivation, sharing) * autoMul(this));
        // Gold is split like training points (ECONOMY E5), not copied to every member.
        const gold = Math.round(partyShare(randInt(this.rng, def.reward.gold[0], def.reward.gold[1]), sharing) * autoMul(this));
        this.progress.gold += gold;
        this.progress.cultivation += cult;
        // A foe that pays nothing per kill (a guild war's: the war pays at its end) shows no reward line.
        if (gold || cult) {
          this.emit({ type: 'reward', id: tgt.id, gold, cultivation: cult });
          this.log(
            'fight',
            `ปราบ${def.name} ได้รับแต้มฝึกปรือ ${cult}${sharing > 1 ? ` (แบ่งกันในปาร์ตี้ ${sharing} คน)` : ''} และ ${gold} ตำลึง`,
          );
        }
        this.questEvent('kill', def.id);
        this.passXp(passXpForKill(cult));
        this.rollLoot(tgt, def);
        if (def.id === 'worldboss') {
          this.dropLegend(tgt, def);
          this.progress.jade += JADE.worldBoss;
          this.log('fight', `ได้รับหยกเซียน ${JADE.worldBoss}`);
        }
        this.progress.kills[def.id] = (this.progress.kills[def.id] ?? 0) + 1;
        this.checkTitles();
      }
      if (this.player.targetId === tgt.id) {
        this.player.targetId = null;
        this.input.engage = false;
      }
      if (def.id === 'boss') {
        this.emit({ type: 'bossDefeated' });
        this.log('sys', `ประกาศ: ${this.progress.name}ปราบปีศาจกระบี่ทมิฬสำเร็จ`);
      }
      return;
    }
    if (tgt.kind === 'player') {
      tgt.deadT = RESPAWN_SECONDS;
      tgt.path = null;
      tgt.targetId = null;
      tgt.meditating = false;
      this.dismount();
      this.input.engage = false;
      this.input.block = false;
      this.log('sys', how ?? `ท่านถูก${src.name}สังหาร`);
      for (const m of this.entities.values())
        if (m.brain && m.brain.state === 'chase') m.brain.state = 'return';
    }
  }

  private respawnPlayer(): void {
    const p = this.player;
    // A red name wakes up in jail.
    const jailed = p.pkPoints > PK.red;
    if (jailed) {
      p.jailT = jailSeconds(p.pkPoints);
      this.progress.jailT = p.jailT;
      p.pk = false;
      this.dismount();
      this.emit({ type: 'jail', seconds: p.jailT });
      this.log('sys', `ถูกจับเข้าคุก ${Math.round(p.jailT / 60)} นาที`);
    }
    const at = jailed ? this.map.jail : this.map.spawn;
    Object.assign(p, {
      dead: false,
      x: at.x,
      y: at.y,
      z: 0,
      vz: 0,
      jumps: 0,
      hp: p.maxHp,
      qi: p.maxQi / 2,
      stamina: p.maxStamina,
      stunT: 0,
      healT: 0,
    });
    this.emit({ type: 'respawn', id: p.id });
    this.log('sys', jailed ? 'ท่านฟื้นคืนสติในคุก' : 'ท่านฟื้นคืนสติที่ลานศิลาเก้ากระบี่');
  }

  /**
   * Feet on the ground after `h` metres of fall (0: none, or into water): past the safe height the
   * landing hurts, steeply, and can kill (data/qinggong.ts `FALL`, the user's decision 2026-10-07).
   */
  private land(p: Entity, h: number): void {
    this.qinggong.fallTop = Number.NaN;
    const share = fallDamage(h, this.qgStats().fallSafe) * gearFallMul(this);
    if (share <= 0) return this.emit({ type: 'land', id: p.id });
    const damage = Math.max(1, Math.round(p.maxHp * share));
    const fall = Math.round(h * 10) / 10;
    this.emit({ type: 'land', id: p.id, fall, damage });
    p.hp -= damage;
    if (p.hp <= 0) return this.kill(p, p, `ตกจากที่สูง ${Math.round(h)} เมตร สิ้นใจ`);
    this.notice(`ตกจากที่สูง ${Math.round(h)} เมตร เสียพลังชีวิต ${damage}`);
  }

  /**
   * The wall-kick (ไต่กำแพง): springs back off the wall and up, the run's push carrying the body
   * away from it a moment (as a dash does), and gives one air jump back.
   */
  private wallKick(p: Entity): void {
    if (!this.qinggongAllowed(p)) return;
    if (p.stamina < WALL_KICK.stamina) return this.notice('พลังกายไม่พอ');
    p.stamina -= WALL_KICK.stamina;
    p.climbing = false;
    p.vz = WALL_KICK.up;
    p.jumps = Math.max(0, p.jumps - 1);
    const l = Math.hypot(p.dirX, p.dirY) || 1;
    p.dashVX = (-p.dirX / l) * WALL_KICK.back;
    p.dashVY = (-p.dirY / l) * WALL_KICK.back;
    p.dashT = WALL_KICK.time;
    p.blocking = false;
    this.emit({ type: 'jump', id: p.id, air: true });
  }

  /**
   * ไต่กำแพง: a wall ahead with a top to stand on (a house or pavilion roof, a tall boulder) is run up
   * at climbing speed while stamina lasts; once high enough, the next step lands on top.
   */
  private climbWall(p: Entity, dx: number, dy: number, dt: number, climb: QinggongStats['climb']): void {
    const l = Math.hypot(dx, dy) || 1;
    const top = standAt(this.map, p.x + (dx / l) * (p.radius + 0.35), p.y + (dy / l) * (p.radius + 0.35));
    const floor = floorAt(this.map, p.x, p.y);
    const alt = floor + p.z;
    // The wall's height is counted from the ground at its foot (`maxHeight` is in metres).
    if (!climb || top === null || top <= alt + STEP_UP || (top - floor) * PX_METRES > climb.maxHeight) return;
    if (p.stamina <= 0) return;
    p.stamina = Math.max(0, p.stamina - climb.stamina * dt);
    p.vz = climb.speed;
    p.z = Math.max(p.z, 0.01);
    p.climbing = true;
  }

  /** Hops over a low obstacle straight ahead when there is somewhere to land within two tiles. */
  private autoVault(p: Entity, dx: number, dy: number): void {
    const l = Math.hypot(dx, dy) || 1;
    const [ux, uy] = [dx / l, dy / l];
    const ahead = (d: number) => [p.x + ux * d, p.y + uy * d] as const;
    const [ax, ay] = ahead(0.45);
    if (freeAt(this.map, ax, ay, p.radius) || !clearAbove(this.map, ax, ay, p.radius)) return;
    // Only over something on the same level: never a hop off (or up) a terrace's wall.
    const here = levelAt(this.map, p.x, p.y);
    for (let d = 0.75; d <= 2.25; d += 0.25) {
      const [x, y] = ahead(d);
      if (!clearAbove(this.map, x, y, p.radius)) return;
      if (Math.abs(levelAt(this.map, x, y) - here) > MAX_STEP) return;
      if (freeAt(this.map, x, y, p.radius)) return this.jump();
    }
  }

  private jump(): void {
    const p = this.player;
    if (p.mount && MOUNTS[p.mount]?.fly) {
      p.flyAlt = Math.min(MOVEMENT.flyMax, p.flyAlt + MOVEMENT.flyStep);
      return;
    }
    if (p.dead || p.stunT > 0 || p.cast || p.spinT > 0) return;
    const qg = this.qgStats();
    // Running up a wall (ไต่กำแพง): the press kicks off it instead.
    if (p.climbing && p.z > 0 && qg.climb) return this.wallKick(p);
    // Out of jumps (เท้าผ่านฟ้า sets how many): with ร่อนลม, a press opens or folds the glide.
    if (p.jumps >= qg.maxJumps) {
      if (p.z > 0 && qg.glide) this.setGlide(!this.qinggong.gliding);
      return;
    }
    if (!this.qinggongAllowed(p)) return;
    const air = p.z > 0;
    if (air) {
      if (p.stamina < qg.airJumpStamina) return this.notice('พลังกายไม่พอ');
      p.stamina -= qg.airJumpStamina;
      p.vz = MOVEMENT.airJumpVelocity;
      if (this.qinggong.gliding) this.setGlide(false);
    } else {
      p.vz = MOVEMENT.jumpVelocity;
      // กระโจนข้ามเหว: a jump off a qinggong run carries the run's speed on through the air.
      if (p.sprinting && qg.leap > 1) this.qinggong.leap = qg.leap;
    }
    p.z = Math.max(p.z, 0.01);
    p.jumps++;
    p.blocking = false;
    this.stopMeditating();
    this.emit(p.jumps >= 3 ? { type: 'jump', id: p.id, air, cloud: true } : { type: 'jump', id: p.id, air });
  }

  private playerSprite(): string {
    return lookOf(this.progress).sprite;
  }

  private joinSchool(id: string): void {
    const sc = SCHOOLS[id];
    const pr = this.progress;
    if (!sc) return;
    if (pr.school)
      return this.notice(pr.school === id ? 'เจ้าเป็นศิษย์สำนักนี้อยู่แล้ว' : 'เจ้ามีสำนักอยู่แล้ว');
    pr.school = id;
    const first = sc.arts[0]!;
    if (!pr.arts.includes(first)) pr.arts.push(first);
    pr.neigong[sc.neigong] ??= 1;
    pr.activeArt = first;
    this.checkTitles();
    this.wearTitle(`disciple-${id}`);
    this.refreshPlayerStats();
    this.emit({ type: 'joinedSchool', school: id });
    this.emit({ type: 'learnedArt', art: first });
    this.emit({ type: 'progressChanged' });
    this.log(
      'sys',
      `เข้าเป็นศิษย์สำนัก${sc.name} ได้รับ${getArt(first).name}และ${getNeigong(sc.neigong).name}`,
    );
  }

  /** Each loot entry rolls on its own; the drops fall to the ground round the body (systems/items.ts). */
  private rollLoot(m: Entity, def: MonsterDef): void {
    this.sys.items.dropLoot(m, def);
  }

  /** Which martial art a blow belongs to (a basic blow: the art in use). */
  private artOfHit(skillId: string): string {
    return skillId === 'basic' ? this.progress.activeArt : (artOfSkill(skillId)?.id ?? '');
  }

  /** The world boss's prize: a กระบี่มารพันกระบี่ rolled with its own stats. */
  private dropLegend(m: Entity, def: MonsterDef): void {
    const pr = this.progress;
    if (pr.inventory.length >= BAG_SIZE) return this.notice('กระเป๋าเต็ม! ของจากจอมมารสูญหาย');
    const swordArts = Object.values(MARTIAL_ARTS)
      // กระบี่ (straight-sword) arts only, not the ดาบ (sabre) ones.
      .filter((a) => isSwordFamily(a) && a.name.includes('กระบี่'))
      .map((a) => a.id);
    const id = `roll-${pr.nextRoll++}`;
    pr.rolls[id] = rollWeapon('founder-sabre', swordArts, this.rng);
    pr.inventory.push(id);
    this.emit({ type: 'loot', id: m.id, item: id });
    this.emit({ type: 'progressChanged' });
    this.log('fight', `ได้รับ「กระบี่มารพันกระบี่」จาก${def.name}`);
  }

  private equip(id: string): void {
    const pr = this.progress;
    const it = itemOf(pr.rolls, id);
    if (!it || !pr.inventory.includes(id)) return;
    if (pr.equipment[it.slot] === id) return;
    pr.equipment[it.slot] = id;
    this.refreshPlayerStats();
    this.emit({ type: 'equipped', slot: it.slot, item: id });
    this.emit({ type: 'progressChanged' });
    this.log('sys', `สวมใส่「${it.name}」`);
  }

  /** Whether someone is free to strike a pose: standing still on the ground, not fighting. */
  private canEmote(e: Entity): boolean {
    return (
      !e.dead &&
      !e.moving &&
      !e.mount &&
      !e.meditating &&
      !e.blocking &&
      !e.cast &&
      e.swingT <= 0 &&
      e.hitT <= 0 &&
      e.stunT <= 0 &&
      e.dashT <= 0 &&
      e.z <= 0.5
    );
  }

  /** Takes up an emote (or lets one go); moving, fighting or being struck ends it. */
  private startEmote(e: Entity, id: string | null): void {
    if (id !== null && !EMOTES[id]) return;
    if (id !== null && !this.canEmote(e)) {
      if (e === this.player)
        this.notice(e.mount ? 'ลงจากพาหนะก่อนจึงโพสท่าได้' : 'ต้องยืนนิ่งก่อนจึงโพสท่าได้');
      return;
    }
    if (e.emote === id) return;
    e.emote = id;
    e.emoteT = 0;
    this.emit({ type: 'emote', id: e.id, emote: id });
  }

  /** Pops an emoji up over the head (not faster than one every EMOJI_COOLDOWN seconds). */
  private showEmoji(e: Entity, id: string): void {
    if (!EMOJIS[id] || e.dead) return;
    if (e.emoji && e.emojiT > EMOJI_SECONDS - EMOJI_COOLDOWN) return;
    e.emoji = id;
    e.emojiT = EMOJI_SECONDS;
    this.emit({ type: 'emoji', id: e.id, emoji: id });
  }

  /** How the worn weapon was forged, for the renderer (null when unforged or bare-handed). */
  private forgeOf(): WeaponForge | null {
    const id = this.progress.equipment.weapon;
    const f = id ? itemOf(this.progress.rolls, id)?.forge : undefined;
    return f && (f.plus || f.lv) ? f : null;
  }

  /**
   * The id to forge a weapon under: a rolled item already has one; a shop weapon (shared by every
   * copy) becomes an item of its own, in the bag and in the hand if it was worn.
   */
  private forgeable(id: string): string {
    const pr = this.progress;
    if (pr.rolls[id]) return id;
    const rid = `roll-${pr.nextRoll++}`;
    pr.rolls[rid] = { base: id, stats: { ...getItem(id).stats } };
    const i = pr.inventory.indexOf(id);
    if (i >= 0) pr.inventory[i] = rid;
    // The one in hand (or worn) is the one forged.
    const slot = getItem(id).slot;
    if (pr.equipment[slot] === id) pr.equipment[slot] = rid;
    return rid;
  }

  /** A weapon in the bag that can go to the forge, or a notice why not. */
  private forgeTarget(id: string): ItemDef | null {
    const pr = this.progress;
    const it = itemOf(pr.rolls, id);
    if (!it || !pr.inventory.includes(id)) return null;
    if (!FORGE_SLOTS.includes(it.slot)) {
      this.notice('ตีบวกได้เฉพาะอาวุธและเครื่องสวมใส่');
      return null;
    }
    return it;
  }

  /** ตีบวก: one + higher, or (past +7) one lower on a failed strike. */
  private forge(id: string, protect = false): void {
    const pr = this.progress;
    const it = this.forgeTarget(id);
    if (!it) return;
    const f = it.forge ?? UNFORGED;
    const step = forgeStep(f.plus);
    if (!step) return this.notice(`${it.name} ถึง +${FORGE_MAX} แล้ว`);
    if (pr.gold < step.cost) return this.notice('ตำลึงไม่พอ');
    pr.gold -= step.cost;
    const rid = this.forgeable(id);
    const ok = this.rng() < step.chance;
    // ยันต์กันถอย (opt-in): spent only when a failed strike would have slipped.
    const slips = !ok && f.plus >= FORGE_SLIP_FROM;
    const held = protect && slips && (pr.trades.goods[CRAFT_MATS.protect] ?? 0) > 0;
    if (held) {
      const left = pr.trades.goods[CRAFT_MATS.protect]! - 1;
      if (left > 0) pr.trades.goods[CRAFT_MATS.protect] = left;
      else delete pr.trades.goods[CRAFT_MATS.protect];
    }
    const plus = ok ? f.plus + 1 : slips && !held ? f.plus - 1 : f.plus;
    pr.rolls[rid] = { ...pr.rolls[rid]!, forge: { ...f, plus } };
    this.emit({ type: 'forge', what: 'plus', item: rid, ok, level: plus });
    this.afterForge(
      ok
        ? `ตีบวกสำเร็จ!「${it.name}」+${plus}`
        : plus < f.plus
          ? `ตีบวกล้มเหลว「${it.name}」ลดลงเป็น +${plus}`
          : held
            ? `ตีบวกล้มเหลว「${it.name}」ยันต์กันถอยรักษา +${plus} ไว้`
            : `ตีบวกล้มเหลว「${it.name}」ยังคง +${plus}`,
    );
  }

  /** ตีธาตุ: the element one level higher; a different element than the one held starts over. */
  private infuse(id: string, el: ForgeElement): void {
    const pr = this.progress;
    if (!isForgeElement(el)) return;
    const it = this.forgeTarget(id);
    if (!it) return;
    const f = it.forge ?? UNFORGED;
    const lv = f.el === el ? f.lv : 0;
    const step = infuseStep(lv);
    const name = FORGE_ELEMENT_INFO[el].name;
    if (!step) return this.notice(`ธาตุ${name}ถึงขั้น ${INFUSE_MAX} แล้ว`);
    if (pr.gold < step.cost) return this.notice('ตำลึงไม่พอ');
    pr.gold -= step.cost;
    const rid = this.forgeable(id);
    const ok = this.rng() < step.chance;
    const level = ok ? lv + 1 : lv;
    // A failed first strike of a new element leaves the old one as it was.
    if (ok || f.el === el) pr.rolls[rid] = { ...pr.rolls[rid]!, forge: { ...f, el, lv: level } };
    this.emit({ type: 'forge', what: 'element', item: rid, ok, level, el });
    this.afterForge(
      ok ? `ตีธาตุ${name}สำเร็จ!「${it.name}」ขั้น ${level}` : `ตีธาตุ${name}ล้มเหลว「${it.name}」`,
    );
  }

  private afterForge(text: string): void {
    this.refreshPlayerStats();
    this.emit({ type: 'progressChanged' });
    this.notice(text);
    this.log('sys', text);
  }

  private unequip(slot: EquipSlot): void {
    const pr = this.progress;
    if (!pr.equipment[slot]) return;
    delete pr.equipment[slot];
    this.refreshPlayerStats();
    this.emit({ type: 'equipped', slot, item: null });
    this.emit({ type: 'progressChanged' });
  }

  private learnArt(id: string): void {
    const art = MARTIAL_ARTS[id];
    const pr = this.progress;
    // School arts need membership; wandering arts (no school, a learn cost) are open to anyone.
    if (!art || art.hidden || pr.arts.includes(id) || (!art.schoolId && art.learnCost === undefined)) return;
    if (art.schoolId && pr.school !== art.schoolId)
      return this.notice(`ต้องเป็นศิษย์สำนัก${getSchool(art.schoolId).name}ก่อน`);
    if (art.requires && !pr.arts.includes(art.requires))
      return this.notice(`ต้องเรียน${getArt(art.requires).name}ก่อน`);
    if (!this.spend(art.learnCost ?? 0)) return;
    pr.arts.push(id);
    this.emit({ type: 'learnedArt', art: id });
    this.emit({ type: 'progressChanged' });
    this.log('sys', `เรียนสำเร็จ: ${art.name}`);
  }

  private dash(dx: number, dy: number): void {
    const p = this.player;
    // On a flying mount, Q dives instead.
    if (p.mount && MOUNTS[p.mount]?.fly) {
      p.flyAlt = Math.max(0, p.flyAlt - MOVEMENT.flyStep);
      return;
    }
    if (p.dead || p.stunT > 0 || p.cast || p.dashT > 0 || p.spinT > 0) return;
    const air = p.z > 0;
    // Without นกนางแอ่นพุ่งตัว this is the plain dodge step (ก้าวหลบ): shorter, and never in the air.
    const d = this.qgStats().dash;
    // A second air dash with รองเท้าเมฆาเหิน (systems/gearCombat.ts): 0 = none left, else the stamina multiplier.
    const airMul = air ? gearAirDashMul(this, p) : 1;
    if (air && (!airMul || !d.air)) return;
    if (!this.qinggongAllowed(p)) return;
    if (p.stamina < d.stamina * airMul) {
      this.notice('พลังกายไม่พอ');
      return;
    }
    const l = Math.hypot(dx, dy);
    const [nx, ny] = l > 0 ? [dx / l, dy / l] : [p.dirX, p.dirY];
    p.stamina -= d.stamina * airMul;
    p.dashT = COMBAT.dashDuration;
    p.dashVX = (nx * d.dist) / COMBAT.dashDuration;
    p.dashVY = (ny * d.dist) / COMBAT.dashDuration;
    p.invulnT = d.invuln;
    p.blocking = false;
    p.path = null;
    this.input.engage = false;
    this.stopMeditating();
    this.dismount();
    if (air) {
      // Air dash: a burst forward that holds the height for a moment instead of falling.
      p.airDashed = true;
      p.vz = Math.max(p.vz, 40);
    }
    this.emit({ type: 'dash', id: p.id });
  }

  // ───────────────────────── วิชาตัวเบา (qinggong.ts) ─────────────────────────

  /** What the player's learned qinggong allows (recomputed only when the levels change). */
  qgStats(): QinggongStats {
    const levels = this.progress.qinggong;
    const key = JSON.stringify(levels) + gearQinggongKey(this);
    if (this.qgCache?.key !== key) this.qgCache = { key, stats: gearQinggong(this, qinggongStats(levels)) };
    return this.qgCache.stats;
  }

  /**
   * Skimming water, gliding, the cliff (systems/climb.ts) and the water (systems/water.ts): out of
   * stamina skimming drops into the water; the water pushes a swimmer; out of breath, health drains
   * and drowning kills.
   */
  private tickQinggongArts(dt: number): void {
    const p = this.player;
    const st = this.qinggong;
    const qg = this.qgStats();
    const inp = this.input;
    if (holdClimb(this.map, p, st, inp.dx, inp.dy, MOVEMENT.gravity, dt) === 'fell')
      this.notice('พลังกายหมด ร่วงจากหน้าผา!');
    const was = st.gliding;
    const r = tickQinggong(this.map, p, qg, st, sealedQinggong(p.statuses), dt);
    if (was && !st.gliding) this.emit({ type: 'glide', id: p.id, on: false });
    if (r === 'sink') {
      this.emit({ type: 'sink', id: p.id, fromX: p.x, fromY: p.y });
      if (qg.water) this.notice('พลังกายหมด ตกลงน้ำ');
    }
    // Nobody rides into deep water.
    if (swimming(st) && p.mount && MOUNTS[p.mount]?.move !== 'water') this.dismount();
    const w = tickWater(p, st, this.map, this.time, inp.fly < 0, !!(inp.dx || inp.dy || p.path), dt);
    if (w.pushX || w.pushY) this.moveEntity(p, w.pushX * dt, w.pushY * dt);
    if (w.note) this.notice(w.note);
    if (w.hp > 0) {
      p.hp -= w.hp;
      if (p.hp <= 0) this.kill(p, p, 'จมน้ำ สิ้นใจ');
    }
  }

  /** Opens (needs a little stamina) or folds ร่อนลม. */
  private setGlide(on: boolean): void {
    const p = this.player;
    const glide = this.qgStats().glide;
    if (on && (!glide || (glide.stamina > 0 && p.stamina <= 0) || p.z <= 0)) return;
    if (this.qinggong.gliding === on) return;
    this.qinggong.gliding = on;
    this.emit({ type: 'glide', id: p.id, on });
  }

  private learnQinggong(id: string): void {
    const pr = this.progress;
    const block = qinggongLearnBlock(pr, id, this.map.zone);
    if (block) return this.notice(block);
    const q = getQinggong(id);
    pr.cultivation -= q.learn.cultivation;
    pr.cultivationSpent += q.learn.cultivation;
    pr.gold -= q.learn.gold ?? 0;
    pr.qinggong[id] = 1;
    this.emit({ type: 'qinggong', id, level: 1 });
    this.emit({ type: 'progressChanged' });
    this.log('sys', `เรียนวิชาตัวเบาสำเร็จ: ${q.name}`);
  }

  private trainQinggong(id: string): void {
    const pr = this.progress;
    const block = qinggongTrainBlock(pr, id);
    if (block) return this.notice(block);
    const q = getQinggong(id);
    const level = pr.qinggong[id]!;
    if (!this.spend(qinggongTrainCost(q, level)!)) return;
    pr.qinggong[id] = level + 1;
    this.emit({ type: 'qinggong', id, level: level + 1 });
    this.emit({ type: 'progressChanged' });
    this.log('sys', `${q.name} ขั้น ${level + 1}`);
  }

  // ───────────────────────── cultivation ─────────────────────────

  // ───────────────────────── auto-battle ─────────────────────────

  /** How far from where auto was switched on it will roam to find and chase enemies. */
  static readonly AUTO_LEASH = 12;

  private setAuto(on: boolean): void {
    const p = this.player;
    if (this.auto.on === on || (on && p.dead)) return;
    if (on && !this.sys.autoRules.allow()) return;
    Object.assign(this.auto, { on, ax: p.x, ay: p.y, thinkT: 0 });
    if (!on) {
      this.input.block = false;
      this.input.engage = false;
    }
    this.emit({ type: 'auto', on });
    this.notice(on ? 'เปิดออโต้: ล่าศัตรูรอบจุดนี้' : 'ปิดออโต้');
  }

  /** Can this move be used right now (without the notices a player would see)? */
  private ready(p: Entity, s: SkillDef): boolean {
    if (p.cooldowns[s.id]) return false;
    if (p.qi < s.qi && !this.gmFlags.infQi) return false;
    if (s.rage && p.rage < s.rage && !this.gmFlags.infQi) return false;
    return true;
  }

  /** Is the target within this move's reach? */
  private inReach(p: Entity, t: Entity, s: SkillDef): boolean {
    const d = dist(p, t);
    if (s.aoe) return d <= s.aoe;
    if (s.volley) return d <= s.range;
    return d <= s.range + 0.4 + (t.scale - 1) * 0.4;
  }

  /**
   * One auto-battle decision every quarter second, playing the triangle like a player would:
   * dodge ground circles, guard against charged attacks, break guards, interrupt breaks, spend rage,
   * keep buffs up, heal when low, and rest or walk home when nothing is left to fight.
   */
  private autoThink(dt: number): void {
    const p = this.player;
    const inp = this.input;
    const a = this.auto;
    if (p.dead) return;
    a.thinkT -= dt;
    if (a.thinkT > 0) return;
    a.thinkT = 0.25;
    if (p.stunT > 0 || p.cast || p.dashT > 0 || p.z > 0) return;

    // Ground circles about to burst: get out.
    for (const tel of this.telegraphs)
      if (dist(p, tel) <= tel.radius + 0.4 && tel.life - tel.t < 0.7) {
        const dx = p.x - tel.x || 1;
        const dy = p.y - tel.y;
        if (p.stamina >= COMBAT.dashStaminaCost) this.dash(dx, dy);
        else this.jump();
        return;
      }

    const home = (e: { x: number; y: number }) => Math.hypot(e.x - a.ax, e.y - a.ay);
    let t = this.get(p.targetId);
    if (!t || t.dead || !this.isHostile(p, t) || home(t) > World.AUTO_LEASH) {
      t =
        [...this.entities.values()]
          .filter((o) => !o.dead && this.isHostile(p, o) && home(o) <= World.AUTO_LEASH && dist(p, o) <= 10)
          .sort((x, y) => dist(p, x) - dist(p, y))[0] ?? null;
    }
    const skills = this.activeSkills();
    const use = (s: SkillDef | undefined, target: Entity | null) =>
      !!s && this.useSkill(p, s.id, target ?? undefined);

    if (!t) {
      inp.engage = false;
      inp.block = false;
      // Nothing to fight: patch up, then walk back to the hunting spot and rest.
      const heal = skills.find((s) => s.kind === 'heal' && this.ready(p, s));
      if (p.hp < p.maxHp * 0.6 && heal && use(heal, null)) return;
      if (home(p) > 2) {
        if (!p.path) p.path = findPath(this.map, p.x, p.y, a.ax, a.ay);
      } else if (p.hp < p.maxHp * 0.7 && !p.meditating && p.combatT <= 0) this.toggleMeditate();
      return;
    }
    if (p.meditating) this.toggleMeditate();
    p.targetId = t.id;
    inp.engage = true;

    const d = dist(p, t);
    // Guard against an attack charging up close; let go once it has passed.
    inp.block = !!t.cast && t.cast.kind === 'attack' && d <= 3.2 && p.stamina > 20;
    if (inp.block) return;

    const ready = skills.filter((s) => this.ready(p, s));
    if (
      p.hp < p.maxHp * 0.5 &&
      use(
        ready.find((s) => s.kind === 'heal'),
        null,
      )
    )
      return;
    // Cleansing moves when slowed, sealed or trapped; other buffs whenever they have lapsed.
    for (const s of ready) {
      if (s.kind !== 'buff' || d > 5) continue;
      if (
        s.cleanse
          ? p.statuses.some((st) => cleansable(st))
          : s.self && !stacksOf(p.statuses, s.self.status)
      )
        if (use(s, null)) return;
    }
    const reach = ready.filter((s) => s.kind !== 'heal' && s.kind !== 'buff' && this.inReach(p, t!, s));
    const pick = (kind: SkillKind) =>
      reach.filter((s) => s.kind === kind).sort((x, y) => y.cooldown - x.cooldown || y.power - x.power)[0];
    if (t.blocking && use(pick('break'), t)) return;
    if (
      t.cast?.kind === 'break' &&
      use(
        reach.find((s) => s.kind === 'attack' && !s.windup),
        t,
      )
    )
      return;
    if (use(pick('power'), t)) return;
    if (!t.blocking) use(pick('attack'), t);
  }

  private toggleMeditate(): void {
    this.sys.meditation.toggle();
  }

  /** A basic attack with no one to fight: a practice cut where the fighter faces. */
  private swingAir(): void {
    const p = this.player;
    if (
      p.dead ||
      p.stunT > 0 ||
      p.cast ||
      p.dashT > 0 ||
      p.blocking ||
      p.attackT > 0 ||
      p.spinT > 0 ||
      this.blinking(p)
    )
      return;
    this.stopMeditating();
    this.dismount();
    this.setSheathed(false);
    p.path = null;
    this.emit({ type: 'basic', srcId: p.id, tgtId: -1, step: this.nextCut(p) });
  }

  /**
   * Advances the basic-attack chain and sets the timing of the cut: two quick cuts, then a heavier
   * finisher. Pausing too long between cuts starts the chain over.
   */
  private nextCut(p: Entity): number {
    const rate = this.activeArt().basic.rate;
    const step = this.time - p.comboAt > rate * 1.6 + 0.35 ? 0 : p.combo;
    const cut = BASIC_CHAIN[step]!;
    p.combo = (step + 1) % BASIC_CHAIN.length;
    p.comboAt = this.time;
    p.attackT = rate * cut.rate;
    p.swingT = cut.swing;
    return step;
  }

  /** Keeps exactly the active neigong's passive buff on the player (once trained far enough). */
  private syncNeigongPassive(p: Entity): void {
    const pr = this.progress;
    const want = neigongPassive(pr.neigong, pr.activeNeigong);
    for (const st of [...p.statuses])
      if (STATUSES[st.id]?.passive && st.id !== want) this.addStatus(p, st.id, -st.stacks);
    if (want && !p.statuses.some((st) => st.id === want)) this.addStatus(p, want, 1);
  }

  /**
   * NPC guards (the elder and the school representatives) cut down red names who come near:
   * a heavy blow every 1.5 s within 7 tiles.
   */
  private updateGuards(dt: number): void {
    const p = this.player;
    if (p.dead || p.pkPoints <= PK.red || p.jailT > 0) return;
    // The two nearest guards within reach take turns at the red name.
    const guards = [this.elder, ...this.schoolMasters.values()]
      .filter((n) => dist(n, p) <= 7)
      .sort((a, b) => dist(a, p) - dist(b, p))
      .slice(0, 2);
    for (const n of guards) {
      n.guardT = Math.max(0, n.guardT - dt);
      if (n.guardT > 0) continue;
      n.guardT = 1.5;
      // Masters of the jianghu: they hit hard and rarely miss.
      n.atk = Math.max(n.atk, 40);
      n.hit = Math.max(n.hit, 300);
      this.face(n, p);
      n.swingT = 0.22;
      this.emit({ type: 'basic', srcId: n.id, tgtId: p.id, step: 2 });
      this.strike(n, p, 4, 'attack', 'basic', 7);
    }
  }

  /** A player killed another: the killer's karma turns by the rules of the jianghu. */
  private pkKill(killer: Entity, victim: Entity): void {
    // A duel is friendly: never a PK.
    if (this.pvpBout && (this.pvpBout.a === killer.id || this.pvpBout.b === killer.id)) return;
    // A system may take the kill over with no penalty (a kill banner: systems/guildBanner.ts).
    if (this.systems.some((s) => s.pkKill?.(killer, victim))) return;
    const before = killer.karma;
    const wasColor = pkColor(killer.pkPoints);
    killer.pkPoints += PK.perKill;
    if (killer.kind === 'player') this.progress.pkPoints = killer.pkPoints;
    const color = pkColor(killer.pkPoints);
    if (color !== wasColor) {
      this.emit({ type: 'pkColor', id: killer.id, color });
      if (killer.kind === 'player' && color === 'red')
        this.notice('หัวแดง! ทหาร NPC จะโจมตีเจ้า ถ้าตายจะติดคุก');
    }

    killer.karma = karmaAfterKill(killer.karma, victim.karma);
    if (killer.kind === 'player') this.progress.karma = killer.karma;
    this.emit({ type: 'karma', id: killer.id, karma: killer.karma });
    this.log(
      'fight',
      `${killer.name}สังหาร${victim.name} (${KARMA_LABELS[victim.karma]})` +
        (before !== killer.karma ? ` · ${killer.name}กลายเป็นฝ่าย「${KARMA_LABELS[killer.karma]}」` : ''),
    );
  }

  // ───────────────────────── party ─────────────────────────

  /** The middle of the nearest walkable tile within `r` tiles, or null. */
  private floorNear(x: number, y: number, r: number): Point | null {
    const w = nearestFloor(this.map, x, y, r);
    return w ? { x: w[0], y: w[1] } : null;
  }

  /** A party member: a fellow wanderer whose id is on the player's party list. */
  inParty(e: Entity): boolean {
    const f = e.rival?.fellow;
    return e.kind === 'rival' && (!!e.rival?.helper || (!!f && this.progress.party.includes(f)));
  }

  /** The fellow wanderer an entity is, if any. */
  fellowOf(e: Entity): FellowDef | null {
    const f = e.rival?.fellow;
    return f ? (FELLOWS[f] ?? null) : null;
  }

  /** The party's members besides the player, in joining order. */
  partyMembers(): Entity[] {
    const out: Entity[] = [];
    for (const id of this.progress.party) {
      const e = this.fellows.get(id);
      if (e) out.push(e);
    }
    return out;
  }

  /** Fellows who wait in this zone, and party members beside the player wherever they are. */
  private spawnFellows(hub: boolean): void {
    const m = this.map;
    const p = this.player;
    for (const f of FELLOW_LIST) {
      const joined = this.progress.party.includes(f.id);
      if (!joined && !(hub && f.zone === m.zone)) continue;
      const want = joined
        ? {
            x: p.x + Math.cos(this.fellows.size * 1.3) * 1.6,
            y: p.y + Math.sin(this.fellows.size * 1.3) * 1.6,
          }
        : { x: m.plaza.x + f.home.dx, y: m.plaza.y + f.home.dy };
      const at = this.floorNear(want.x, want.y, 4) ?? { x: p.x, y: p.y };
      const e = this.add({
        kind: 'rival',
        x: at.x,
        y: at.y,
        name: f.name,
        title: f.title,
        sprite: f.sprite,
        speed: p.speed,
        level: p.level,
        rival: {
          homeX: at.x,
          homeY: at.y,
          aggroT: 0,
          skillT: 2,
          skills: getArt(f.art)
            .skills.filter((id) => ['attack', 'break', 'power'].includes(getSkill(id).kind))
            .slice(0, 4),
          fellow: f.id,
          followT: 0,
        },
        wander: { cx: at.x, cy: at.y, r: 3, t: 1, tx: null, ty: null },
      });
      // As strong as the player, a little less.
      const k = PARTY.strength;
      Object.assign(e, {
        maxHp: Math.round(p.maxHp * k),
        hp: Math.round(p.maxHp * k),
        maxQi: p.maxQi,
        qi: p.maxQi,
        atk: p.atk * k,
        def: p.def * k,
        magic: p.magic * k,
        magicDef: p.magicDef * k,
        hit: p.hit,
        magicHit: p.magicHit,
        dodge: p.dodge,
        crit: p.crit,
        magicCrit: p.magicCrit,
        toughness: p.toughness,
        maxStamina: p.maxStamina,
        stamina: p.maxStamina,
      });
      this.fellows.set(f.id, e);
      if (f.id === 'shadowless') this.rivalId = e.id;
    }
  }

  private partyInvite(id: number): void {
    const p = this.player;
    const e = this.get(id);
    const f = e && this.fellowOf(e);
    if (!e || !f || e.dead) return;
    const party = this.progress.party;
    if (party.includes(f.id)) return this.notice(`${f.name}อยู่ในปาร์ตี้แล้ว`);
    if (party.length + 1 >= PARTY.maxSize) return this.notice(`ปาร์ตี้เต็มแล้ว (${PARTY.maxSize} คน)`);
    if (this.duel) return this.notice('ชวนเข้าปาร์ตี้ระหว่างประลองไม่ได้');
    if (this.isHostile(p, e) || e.rival!.aggroT > 0)
      return this.notice(`${f.name}ไม่ยอมร่วมทางกับคนที่กำลังต่อสู้ด้วย`);
    if (dist(p, e) > PARTY.inviteRange) return this.notice(`เข้าไปใกล้${f.name}อีกหน่อยเพื่อชวน`);
    party.push(f.id);
    Object.assign(e, { pk: false, targetId: null, path: null });
    this.log('sys', `${f.name}: 「${f.greeting}」`);
    this.notice(`${f.name}เข้าร่วมปาร์ตี้ (${party.length + 1}/${PARTY.maxSize})`);
    this.emit({ type: 'party', members: [...party] });
    this.emit({ type: 'progressChanged' });
  }

  /** Sends a member away: they stay where they are, back to wandering on their own. */
  private partyKick(fellow: string, quiet = false): void {
    const party = this.progress.party;
    const i = party.indexOf(fellow);
    if (i < 0) return;
    party.splice(i, 1);
    const e = this.fellows.get(fellow);
    if (e) {
      const b = e.rival!;
      Object.assign(b, { homeX: e.x, homeY: e.y, aggroT: 0 });
      Object.assign(e, { targetId: null, path: null });
      e.wander = { cx: e.x, cy: e.y, r: 3, t: 1, tx: null, ty: null };
    }
    if (!quiet) this.log('sys', `${FELLOWS[fellow]?.name ?? fellow}ออกจากปาร์ตี้`);
    this.emit({ type: 'party', members: [...party] });
    this.emit({ type: 'progressChanged' });
  }

  /** Who a fighting monster is after: the party member that struck it last, else the player. */
  private foeOf(m: Entity): Entity {
    const f = this.get(m.brain!.foeId);
    // Another player it fights (docs/ONLINE.md §2, 1c), or a party member; else the primary player.
    if (f && !f.dead && f !== this.primary.entity && this.slots.has(f.id)) return f;
    return f && !f.dead && f.kind !== 'player' && this.inParty(f) ? f : this.primary.entity;
  }

  /**
   * A party member: follows the player, joins the player's fight (their target, or whatever is
   * fighting the party near them) with basic cuts and moves from their art, and gets back up
   * beside the player a while after falling.
   */
  private updateCompanion(r: Entity, dt: number): void {
    const b = r.rival!;
    const p = this.player;
    const beside = () => {
      const a = (r.id * 1.7) % (Math.PI * 2);
      return this.floorNear(p.x + Math.cos(a) * 1.5, p.y + Math.sin(a) * 1.5, 3) ?? { x: p.x, y: p.y };
    };
    if (r.dead) {
      r.deadT += dt;
      if (r.deadT >= PARTY.reviveSeconds) {
        const at = beside();
        Object.assign(r, {
          dead: false,
          deadT: 0,
          hp: r.maxHp,
          x: at.x,
          y: at.y,
          z: 0,
          statuses: [],
          cast: null,
        });
        this.emit({ type: 'respawn', id: r.id });
      }
      return;
    }
    r.qi = r.maxQi;
    if (r.combatT <= 0) r.hp = Math.min(r.maxHp, r.hp + r.maxHp * 0.03 * dt);
    r.moving = false;
    r.sprinting = false;
    if (r.stunT > 0 || r.cast || r.spinT > 0 || this.blinking(r)) return;
    const near = dist(r, p);
    // Left far behind (the player warped, died or rode off): catch up at once.
    if (near > PARTY.teleport) {
      const at = beside();
      Object.assign(r, { x: at.x, y: at.y, z: 0, path: null, targetId: null });
      return;
    }
    // The fight: the player's target, else the nearest monster fighting one of the party.
    let foe: Entity | null = null;
    if (near < PARTY.catchUp && !p.dead) {
      const cur = this.get(r.targetId);
      const pt = this.get(p.targetId);
      const ok = (e: Entity | undefined): e is Entity =>
        !!e && !e.dead && e.kind === 'monster' && dist(e, p) <= PARTY.assistRange;
      if (ok(pt) && this.isHostile(r, pt)) foe = pt;
      else if (ok(cur) && cur.brain?.state === 'chase') foe = cur;
      else {
        let best: number = PARTY.assistRange;
        for (const e of this.entities.values()) {
          if (!ok(e) || e.brain?.state !== 'chase') continue;
          const d = dist(e, r);
          if (d < best) [best, foe] = [d, e];
        }
      }
    }
    if (foe) {
      enterCombat(r);
      r.targetId = foe.id;
      r.path = null;
      this.face(r, foe);
      b.skillT -= dt;
      const d = dist(r, foe);
      if (b.skillT <= 0) {
        b.skillT = 2.5 + this.rng() * 2.5;
        const id = b.skills[Math.floor(this.rng() * b.skills.length)]!;
        if (this.useSkill(r, id, foe)) return;
      }
      if (d > 1.9) {
        const l = d || 1;
        this.moveEntity(r, ((foe.x - r.x) / l) * this.spd(r) * dt, ((foe.y - r.y) / l) * this.spd(r) * dt);
        r.moving = true;
      } else if (r.attackT <= 0) {
        r.attackT = 0.9;
        r.swingT = 0.22;
        r.combo = (r.combo + 1) % 3;
        this.emit({ type: 'basic', srcId: r.id, tgtId: foe.id, step: r.combo });
        this.strike(r, foe, 1, 'attack', 'basic', 1.9);
      }
      return;
    }
    r.targetId = null;
    if (near <= PARTY.followDistance) {
      r.path = null;
      return;
    }
    // Follow along the ground, keeping pace with the player (mounted or running).
    const speed = Math.max(this.spd(r), this.spd(p)) * (near > PARTY.catchUp ? 1.3 : 1);
    b.followT = (b.followT ?? 0) - dt;
    if (!r.path || b.followT <= 0) {
      b.followT = 0.8;
      r.path = findPath(this.map, r.x, r.y, p.x, p.y);
    }
    if (r.path) this.followPath(r, speed, dt);
    else {
      const l = near || 1;
      this.moveEntity(r, ((p.x - r.x) / l) * speed * dt, ((p.y - r.y) / l) * speed * dt);
    }
    r.moving = true;
    r.sprinting = near > PARTY.catchUp;
  }

  /**
   * The fake player: wanders near home; with PK on it hunts the player when near (outside the
   * plaza), otherwise it only fights back for a while after being struck, with basic cuts and
   * moves from its art. It comes back to life after a while.
   */
  private updateRival(r: Entity, dt: number): void {
    if (this.inParty(r)) return this.updateCompanion(r, dt);
    const b = r.rival!;
    const duelling = this.duel?.foeId === r.id;
    if (duelling && (r.dead || this.duel!.phase !== 'fight')) {
      r.moving = false;
      return;
    }
    if (r.dead) {
      r.deadT += dt;
      if (r.deadT >= 8)
        Object.assign(r, { dead: false, deadT: 0, hp: r.maxHp, x: b.homeX, y: b.homeY, statuses: [] });
      return;
    }
    r.qi = r.maxQi;
    if (r.combatT <= 0) r.hp = Math.min(r.maxHp, r.hp + r.maxHp * 0.03 * dt);
    b.aggroT = Math.max(0, b.aggroT - dt);
    r.moving = false;
    if (r.stunT > 0 || r.cast || r.spinT > 0 || this.blinking(r)) return;
    const p = this.player;
    const near = dist(r, p);
    const foe =
      !p.dead && this.isHostile(r, p) && (duelling || (near < 14 && (b.aggroT > 0 || (r.pk && near < 9))))
        ? p
        : null;
    if (!foe) {
      if (dist(r, { x: b.homeX, y: b.homeY }) > 6) {
        r.path ??= findPath(this.map, r.x, r.y, b.homeX, b.homeY);
        if (r.path) this.followPath(r, r.speed, dt);
      } else this.updateWander(r, dt, 1);
      return;
    }
    enterCombat(r);
    r.targetId = foe.id;
    this.face(r, foe);
    b.skillT -= dt;
    if (b.skillT <= 0) {
      b.skillT = 2.5 + this.rng() * 2.5;
      const id = b.skills[Math.floor(this.rng() * b.skills.length)]!;
      if (this.useSkill(r, id, foe)) return;
    }
    if (near > 1.9) {
      const l = near || 1;
      this.moveEntity(r, ((foe.x - r.x) / l) * this.spd(r) * dt, ((foe.y - r.y) / l) * this.spd(r) * dt);
      r.moving = true;
    } else if (r.attackT <= 0) {
      r.attackT = 0.9;
      r.swingT = 0.22;
      r.combo = (r.combo + 1) % 3;
      this.emit({ type: 'basic', srcId: r.id, tgtId: foe.id, step: r.combo });
      this.strike(r, foe, 1, 'attack', 'basic', 1.9);
    }
  }

  /** The quest is in this chain and the chain is past it (a quest the chain lacks is never passed). */
  private questPassed(id: string): boolean {
    const k = QUESTS.findIndex((q) => q.id === id);
    return k >= 0 && k < this.progress.quest.index;
  }

  /** Grants every title whose condition is now met (announcing each one). */
  checkTitles(): void {
    const pr = this.progress;
    const mastered = Object.keys(pr.neigong).some((id) => neigongMastered(pr.neigong, id));
    const questsDone = pr.quest.index >= QUESTS.length;
    for (const t of TITLE_LIST) {
      if (pr.titles.includes(t.id)) continue;
      const u = t.unlock;
      const earned =
        u.kind === 'start' ||
        (u.kind === 'kills' && (pr.kills[u.monster] ?? 0) >= u.count) ||
        (u.kind === 'quest' && this.questPassed(u.quest)) ||
        (u.kind === 'school' && pr.school === u.school) ||
        (u.kind === 'mastery' && mastered) ||
        (u.kind === 'legend' && mastered && questsDone);
      if (!earned) continue;
      this.grantTitle(t.id);
    }
  }

  /** Gives the player a title and whatever comes with it. */
  grantTitle(id: string): void {
    const pr = this.progress;
    const t = TITLES[id];
    if (!t || pr.titles.includes(id)) return;
    pr.titles.push(t.id);
    if (t.grants?.mount && !pr.mounts.includes(t.grants.mount)) pr.mounts.push(t.grants.mount);
    if (t.grants?.skin && !pr.skins.includes(t.grants.skin)) pr.skins.push(t.grants.skin);
    this.emit({ type: 'title', id: t.id });
    this.log('sys', `ได้รับฉายา「${t.name}」 เลือกใช้ได้ที่แผงตัวละคร (C)`);
  }

  /** Shows an earned title over the player's head. */
  wearTitle(id: string): void {
    const pr = this.progress;
    const t = TITLES[id];
    if (!t || !pr.titles.includes(id)) return;
    pr.titleWorn = id;
    pr.title = t.name;
    this.player.title = t.name;
    this.emit({ type: 'progressChanged' });
  }

  /** What the player wears, as appearance keys: equipment, and the weapon put away if it is. */
  private outfitOf(p: Entity): string[] {
    const looks = lookOf(this.progress).outfit;
    return p.sheathed ? [...looks, 'sheathed'] : looks;
  }

  private setSheathed(on: boolean): void {
    const p = this.player;
    if (p.sheathed === on) return;
    p.sheathed = on;
    p.outfit = this.outfitOf(p);
    this.emit({ type: 'sheathe', id: p.id, on });
  }

  private toggleMount(): void {
    const p = this.player;
    if (p.mount) return this.dismount();
    const pr = this.progress;
    const id = pr.mounts.includes(pr.mountPick) ? pr.mountPick : pr.mounts[0];
    if (!id || p.dead) return;
    if (p.combatT > 0) return this.notice(combatBlockText(p, 'ขี่พาหนะไม่ได้'));
    if (p.z > 0 || p.cast || p.stunT > 0 || p.dashT > 0) return;
    this.stopMeditating();
    p.blocking = false;
    p.mount = id;
    // A flying mount takes off at once.
    p.flyAlt = MOUNTS[id]?.fly ? MOVEMENT.flyStep : 0;
    this.emit({ type: 'mount', id: p.id, mount: id });
  }

  private dismount(): void {
    const p = this.player;
    if (!p.mount) return;
    p.mount = null;
    this.emit({ type: 'mount', id: p.id, mount: null });
  }

  private stopMeditating(): void {
    this.sys.meditation.stop();
  }

  private spend(cost: number): boolean {
    const pr = this.progress;
    if (pr.cultivation < cost) {
      this.notice(`แต้มฝึกปรือไม่พอ (ต้องการ ${cost})`);
      return false;
    }
    pr.cultivation -= cost;
    pr.cultivationSpent += cost;
    return true;
  }

  /** "คัมภีร์สัญญาเลือด" or a move's name (systems/training.ts). */
  trainingName(t: Pick<TrainingState, 'kind' | 'id'>): string {
    return this.sys.training.trainingName(t);
  }

  private learnNeigong(id: string): void {
    const pr = this.progress;
    const n = NEIGONG[id];
    if (!n || pr.neigong[id] != null) return;
    const why = neigongLearnBlock(pr.neigong, pr.school, id);
    if (why) return this.notice(why);
    if (!this.spend(n.learnCost ?? 0)) return;
    pr.neigong[id] = 1;
    this.emit({ type: 'progressChanged' });
    this.log('sys', `เรียนสำเร็จ: ${n.name} (คัมภีร์ขั้น ${n.tier})`);
  }

  private setNeigong(id: string): void {
    const pr = this.progress;
    if (pr.neigong[id] == null || pr.activeNeigong === id) return;
    if (this.player.combatT > 0) return this.notice(combatBlockText(this.player, 'เปลี่ยนกำลังภายในไม่ได้'));
    pr.activeNeigong = id;
    this.refreshPlayerStats();
    this.emit({ type: 'progressChanged' });
  }

  private setArt(id: string): void {
    const pr = this.progress;
    if (!pr.arts.includes(id) || pr.activeArt === id || MARTIAL_ARTS[id]?.hidden) return;
    if (this.player.cast) return;
    pr.activeArt = id;
    this.refreshPlayerStats();
    this.emit({ type: 'progressChanged' });
    this.notice(`เปลี่ยนเป็น${getArt(id).name}`);
  }

  // ───────────────────────── quests ─────────────────────────

  private openDialog(npc: Entity): void {
    if (this.carriage.owns(npc)) return this.carriage.talk(npc);
    // A sect's seniors (systems/sectJoin.ts) answer before the interior's own greeting.
    if (this.systems.find((s) => s.name === 'sectJoin')?.talk?.(npc)) return;
    if (this.interiors.talk(npc)) return;
    for (const s of this.systems) if (s.talk?.(npc)) return;
    if (npc === this.guide) {
      const hub = this.map.kind === 'hub';
      // Directions: where each warp circle of this zone leads, and how many warps the snow peak is.
      const sides = { n: 'เหนือ', e: 'ตะวันออก', s: 'ใต้', w: 'ตะวันตก' } as const;
      const ways = this.map.portals.map((p) => `ทิศ${sides[p.side]}ไป${ZONES[p.to]?.name ?? 'ถนนสายใหม่'}`).join(' · ');
      return this.emit({
        type: 'dialog',
        npcId: npc.id,
        text: hub
          ? `วงวาร์ปอยู่กลางริมแมปแต่ละด้าน ${ways} · แมปไกลต้องวาร์ปต่อกันไป ยอดเขาหิมะต้องผ่าน ${warpDistance(HUB, 'snow')} วงวาร์ป`
          : `วงวาร์ปของที่นี่: ${ways} · ถ้าอยากกลับลานศิลาเก้ากระบี่ทันที ข้าพาไปได้`,
        action: hub ? null : 'travel',
      });
    }
    if (npc === this.merchant)
      return this.emit({
        type: 'dialog',
        npcId: npc.id,
        text: 'ของดีจากทั่วยุทธภพ ราคาเป็นมิตร ของในกระเป๋าเจ้าข้าก็รับซื้อนะ',
        action: 'shop',
      });
    for (const [sid, master] of this.schoolMasters) {
      if (master.id !== npc.id) continue;
      const sc = getSchool(sid);
      const mine = this.progress.school;
      const text =
        mine === sid
          ? sc.memberText
          : mine
            ? 'เจ้ามีสำนักของตนอยู่แล้ว จงฝึกวิชาของสำนักตนให้ถึงที่สุดเถิด'
            : sc.joinText;
      return this.emit({
        type: 'dialog',
        npcId: npc.id,
        text,
        action: mine ? null : 'joinSchool',
        schoolId: sid,
      });
    }
    // His first meeting with a newcomer plays instead (scene 'elder-meets'), the first time.
    if (npc === this.elder && this.story.meetElder()) return;
    const q = this.currentQuest();
    const st = this.progress.quest.status;
    if (!q) return this.emit({ type: 'dialog', npcId: npc.id, text: FINAL_DIALOG, action: null });
    if (st === 'available')
      return this.emit({ type: 'dialog', npcId: npc.id, text: q.offer, action: 'accept' });
    if (st === 'active') return this.emit({ type: 'dialog', npcId: npc.id, text: q.remind, action: null });
    this.emit({ type: 'dialog', npcId: npc.id, text: q.done, action: 'turnIn' });
  }

  /** Sets off for the other zone (the client saves and loads it). */
  private travel(to: ZoneId, byPortal: boolean): void {
    if (this.travelling || to === this.map.zone) return;
    if (!zoneEnabled(to)) return this.notice(CLOSED_NOTICE);
    this.travelling = true;
    this.progress.arriveFrom = byPortal ? this.map.zone : null;
    this.progress.location = to;
    this.emit({ type: 'travel', to });
  }

  /** Pays a reward (top-up milestone or pass tier) and describes it (systems/store.ts). */
  private grantReward(m: PassReward): string {
    return this.sys.store.grantReward(m);
  }

  /** Season XP; announces each tier reached (systems/store.ts). */
  private passXp(n: number): void {
    this.sys.store.passXp(n);
  }

  /** The breakthrough gate at stage 9 (systems/breakthrough.ts). */
  get breakthrough(): BreakthroughSystem {
    return this.sys.breakthrough;
  }

  /** Seam gates: `enter(gate, end)` starts a ride, `ride` is the one under way (systems/seamGate.ts). */
  get seam(): SeamGateSystem {
    return this.sys.seamGate;
  }

  /** The ladder bout or arena bout under way (or the ladder queue), or null (systems/arena.ts). */
  get duel(): Duel | null {
    return this.sys.arena.duel;
  }
  set duel(d: Duel | null) {
    this.sys.arena.duel = d;
  }

  /** Arena ranks offered to challenge, and the last bout fought (systems/arena.ts). */
  get arenaPicks(): number[] {
    return this.sys.arena.picks;
  }
  set arenaPicks(picks: number[]) {
    this.sys.arena.picks = picks;
  }
  get arenaLast(): { won: boolean; foe: string; from: number; to: number } | null {
    return this.sys.arena.last;
  }
  set arenaLast(last: { won: boolean; foe: string; from: number; to: number } | null) {
    this.sys.arena.last = last;
  }

  /** In the arena (counting down, fighting or seeing the result), not just queued. */
  inArena(): boolean {
    return this.sys.arena.inArena();
  }

  /** The player's place in today's arena ranking (1-based). */
  arenaRank(): number {
    return this.sys.arena.arenaRank();
  }

  /** The build of whoever holds an arena rank: the player's own, or a contender's. */
  arenaBuild(rank: number): ArenaBuild {
    return this.sys.arena.arenaBuild(rank);
  }

  /**
   * A new day (the client's calendar date, by the server in phase 2): yesterday's rank pays its
   * reward if the player took part, and the ranking starts over. Returns what was paid, if anything.
   */
  arenaNewDay(day: string): string | null {
    return this.sys.arena.arenaNewDay(day);
  }

  /** The training spot the player stands in (data/trainingSpots.ts), or null. */
  get trainingSpot(): TrainingSpotDef | null {
    return this.sys.cultivation.trainingSpot;
  }

  /** Where the player trains now, and why its bonus is held back (systems/cultivation.ts). */
  spotState(): TrainingSpotState | null {
    return this.sys.cultivation.spotState();
  }

  private nearMerchant(): boolean {
    if (this.interiors.inside ? this.interiors.nearShop() : dist(this.player, this.merchant) <= 5)
      return true;
    this.notice('ต้องอยู่ใกล้เถ้าแก่ฉางจึงซื้อขายได้');
    return false;
  }

  private buy(item: string): void {
    const pr = this.progress;
    if (!this.nearMerchant()) return;
    const e = SHOP.find((x) => x.item === item);
    if (!e || !ITEMS[item]) return;
    // What the merchant asks: the list price by how he regards the player (systems/npcMemory.ts).
    const price = shopPrice(this, e.price);
    if (pr.gold < price) return this.notice('ตำลึงไม่พอ');
    if (pr.inventory.length >= BAG_SIZE) return this.notice('กระเป๋าเต็ม');
    pr.gold -= price;
    pr.inventory.push(item);
    this.notice(`ซื้อ「${ITEMS[item]!.name}」แล้ว`);
    this.emit({ type: 'progressChanged' });
  }

  private sell(id: string): void {
    const pr = this.progress;
    if (!this.nearMerchant()) return;
    const i = pr.inventory.indexOf(id);
    const it = itemOf(pr.rolls, id);
    if (i < 0 || !it) return;
    // Selling the worn one is refused unless another of it is carried.
    if (Object.values(pr.equipment).includes(id) && pr.inventory.filter((x) => x === id).length < 2)
      return this.notice('ถอดออกก่อนจึงขายได้');
    // A forged shop weapon sells as the weapon it was; only rolled legends fetch the fixed sum.
    const price = sellPrice(pr.rolls[id]?.base ?? id, !!pr.rolls[id]?.art);
    pr.inventory.splice(i, 1);
    if (pr.rolls[id] && !pr.inventory.includes(id)) delete pr.rolls[id];
    pr.gold += price;
    this.notice(`ขาย「${it.name}」ได้ ${price} ตำลึง`);
    this.emit({ type: 'progressChanged' });
  }

  private acceptQuest(): void {
    const q = this.currentQuest();
    const qs = this.progress.quest;
    if (!q || qs.status !== 'available') return;
    qs.status = 'active';
    qs.progress = q.objectives.map(() => 0);
    this.emit({ type: 'quest', status: 'accepted', questId: q.id });
    this.log('sys', `รับภารกิจ: ${q.title}`);
    if (q.spawnsBoss) this.spawnBoss();
  }

  private turnInQuest(): void {
    const q = this.currentQuest();
    const pr = this.progress;
    if (!q || pr.quest.status !== 'ready') return;
    const r = q.rewards;
    pr.cultivation += r.cultivation;
    pr.gold += r.gold;
    pr.jade += JADE.questTurnIn;
    this.passXp(SEASON.xp.questTurnIn);
    for (const a of r.arts ?? []) if (!pr.arts.includes(a)) pr.arts.push(a);
    for (const n of r.neigong ?? []) pr.neigong[n] ??= 1;
    pr.quest = { index: pr.quest.index + 1, status: 'available', progress: [] };
    this.checkTitles();
    this.refreshPlayerStats();
    this.log('sys', `ส่งภารกิจ ${q.title} ได้รับแต้มฝึกปรือ ${r.cultivation} และ ${r.gold} ตำลึง`);
    const chapter = LEGEND.find((c) => c.after === q.id);
    if (chapter) this.log('sys', `ตำนานกระบี่ทั้งเก้า บทใหม่: ${chapter.title} (กด H อ่านตำนาน)`);
    this.emit({ type: 'quest', status: 'completed', questId: q.id });
    // The scene that closes a chapter (chapter one: in Yanyu, after the three forms).
    this.story.questDone(q.id);
    this.emit({ type: 'progressChanged' });
  }

  private questEvent(type: ObjectiveType, target?: string): void {
    const q = this.currentQuest();
    const qs = this.progress.quest;
    if (!q || qs.status !== 'active') return;
    const r = advanceObjectives(q, qs.progress, type, target);
    if (r === 'ready') {
      qs.status = 'ready';
      this.emit({ type: 'quest', status: 'ready', questId: q.id });
      this.notice('ภารกิจสำเร็จ กลับไปหาผู้เฒ่าหลิว');
    } else if (r) this.emit({ type: 'quest', status: 'progress', questId: q.id });
  }

  // ───────────────────────── movement ─────────────────────────

  /** Moves to the nearest open spot (searching outward), for a fighter that ended up inside an obstacle. */
  private settle(e: Entity): void {
    for (let r = 0.25; r <= 4; r += 0.25)
      for (let k = 0; k < 16; k++) {
        const a = (k / 16) * Math.PI * 2;
        const x = e.x + Math.cos(a) * r;
        const y = e.y + Math.sin(a) * r;
        if (freeAt(this.map, x, y, e.radius)) {
          e.x = x;
          e.y = y;
          return;
        }
      }
  }

  private moveEntity(e: Entity, dx: number, dy: number): boolean {
    // Heights: a fighter at altitude `alt` can move onto anything no more than a step above it
    // (boulders, a roof once high enough), and over trees while well up in a jump.
    const m = this.map;
    const alt = floorAt(m, e.x, e.y) + e.z;
    const r = e.radius;
    // On the ground, nobody walks up or down a wall between levels (only stairs and ramps join them).
    const grounded = e.z <= 0;
    // High on a flying mount, only the edge of the world stops the flight.
    const soaring = !!e.mount && !!MOUNTS[e.mount]?.fly && e.z >= MOVEMENT.flyClear;
    // The player goes into the water (wading, swimming), or onto it (วิ่งบนน้ำ): footing at its surface.
    // A monster fighting or going home wades the shallows after a swimmer; deep water stays safe (systems/water.ts).
    const wet = (x: number, y: number) => (e === this.player ? isWaterAt(m, x, y) : wadesAt(m, e, x, y));
    const J = this.map.jail;
    // A cliff's rock (a map with levels, away from its edge) is flown over well above its level:
    // the region's mountainsides under a glide (docs/archive/OPEN_WORLD.md §3.1).
    const overRock = (x: number, y: number) => {
      const i = Math.floor(x);
      const j = Math.floor(y);
      return (
        hasLevels(m) &&
        tileAt(m, i, j) === Tile.Rock &&
        !isBorder(m, i, j) &&
        alt > levelAt(m, x, y) / PX_METRES + MOVEMENT.flyClear
      );
    };
    const open = (x: number, y: number) => {
      // Jail walls.
      if (e.jailT > 0 && (Math.abs(x - J.x) > 2.4 || Math.abs(y - J.y) > 2.4)) return false;
      // The duelling ground's edge.
      if (this.inArena() && (e === this.player || e.id === this.duel!.foeId))
        if (Math.hypot(x - m.duel.x, y - m.duel.y) > LADDER.arenaRadius) return false;
      if (soaring)
        return [
          [x - r, y - r],
          [x + r, y + r],
        ].every(([cx, cy]) => !isBorder(m, Math.floor(cx!), Math.floor(cy!)));
      if (grounded && crossesWall(m, e.x, e.y, x, y, r, e === this.player)) return false;
      for (const [cx, cy] of [
        [x - r, y - r],
        [x + r, y - r],
        [x - r, y + r],
        [x + r, y + r],
      ] as const) {
        const top =
          standAt(m, cx, cy) ?? (wet(cx, cy) ? waterSurfaceAt(m, cx, cy) / PX_METRES : null);
        if (top === null) {
          if (
            e.z < MOVEMENT.clearHeight ||
            (blocksJump(m, Math.floor(cx), Math.floor(cy)) && !overRock(cx, cy))
          )
            return false;
        } else if (top > alt + STEP_UP) return false;
      }
      return true;
    };
    let moved = false;
    if (dx && open(e.x + dx, e.y)) {
      e.x += dx;
      moved = true;
    }
    if (dy && open(e.x, e.y + dy)) {
      e.y += dy;
      moved = true;
    }
    // Keep the altitude: stepping off a roof or boulder leaves the player in the air to fall.
    // Small drops (off a boulder) are simply stepped down.
    if (moved && e.kind === 'player') {
      const z = Math.max(0, alt - floorAt(m, e.x, e.y));
      e.z = e.z <= 0 && z <= STEP_UP ? 0 : z;
    }
    const sf = screenFacing(dx, dy);
    if (Math.abs(sf) > 1e-4) e.facing = sf > 0 ? 1 : -1;
    const l = Math.hypot(dx, dy);
    if (l > 0) {
      e.heading = screenHeading(dx, dy);
      e.dirX = dx / l;
      e.dirY = dy / l;
    }
    return moved;
  }

  private followPath(e: Entity, speed: number, dt: number): void {
    const path = e.path!;
    const [tx, ty] = path[0]!;
    const dx = tx - e.x;
    const dy = ty - e.y;
    const d = Math.hypot(dx, dy);
    const step = speed * dt;
    if (d <= step) {
      if (freeAt(this.map, tx, ty, e.radius)) {
        e.x = tx;
        e.y = ty;
      }
      path.shift();
      e.stuckT = 0;
    } else if (!this.moveEntity(e, (dx / d) * step, (dy / d) * step)) {
      e.stuckT += dt;
      if (e.stuckT > 0.25) {
        path.shift();
        e.stuckT = 0;
      }
    }
    if (!path.length) e.path = null;
    e.moving = true;
  }

  private face(e: Entity, t: { x: number; y: number }): void {
    const sf = screenFacing(t.x - e.x, t.y - e.y);
    if (Math.abs(sf) > 0.01) e.facing = sf > 0 ? 1 : -1;
    if (Math.hypot(t.x - e.x, t.y - e.y) > 0.01) e.heading = screenHeading(t.x - e.x, t.y - e.y);
  }

  // ───────────────────────── events ─────────────────────────

  private emit(e: WorldEvent, to?: Audience): void {
    if (e.type === 'hit') {
      const t = this.get(e.tgtId);
      if (t) this.lastFight = { x: t.x, y: t.y, t: this.time };
    }
    this.events.push(e);
    this.audiences.push(to ?? this.audienceOf(e));
    // What the systems make of it (systems/npcMemory.ts, npcTalk.ts).
    for (const s of this.systems) s.observe?.(e);
  }

  private notice(text: string): void {
    this.emit({ type: 'notice', text });
  }

  private log(channel: LogChannel, text: string): void {
    this.emit({ type: 'log', channel, text });
  }
}
